export interface MoveMetadata {
  id: string;
  accuracy: number | null;
  damageClass: string;
  effect: string;
  effectChance: number | null;
  learnedBy: number[];
  meta: {
    ailment: string;
    ailmentChance: number;
    category: string;
    critRate: number;
    drain: number;
    flinchChance: number;
    healing: number;
    maxHits: number | null;
    maxTurns: number | null;
    minHits: number | null;
    minTurns: number | null;
    statChance: number;
  } | null;
  name: string;
  power: number | null;
  pp: number | null;
  priority: number;
  type: string;
}

const moveMetadata: Record<string, MoveMetadata> = {
  pound: {
    id: "pound",
    accuracy: 100,
    damageClass: "physical",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [
      35, 36, 39, 40, 60, 61, 62, 88, 89, 96, 97, 113, 115, 122, 124, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Pound",
    power: 40,
    pp: 35,
    priority: 0,
    type: "normal",
  },
  "karate-chop": {
    id: "karate-chop",
    accuracy: 100,
    damageClass: "physical",
    effect: "Has an increased chance for a critical hit.",
    effectChance: null,
    learnedBy: [56, 57, 66, 67, 68],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 1,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Karate Chop",
    power: 50,
    pp: 25,
    priority: 0,
    type: "fighting",
  },
  "double-slap": {
    id: "double-slap",
    accuracy: 85,
    damageClass: "physical",
    effect: "Hits 2-5 times in one turn.",
    effectChance: null,
    learnedBy: [35, 36, 39, 40, 60, 61, 62, 113, 122, 124],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: 5,
      maxTurns: null,
      minHits: 2,
      minTurns: null,
      statChance: 0,
    },
    name: "Double Slap",
    power: 15,
    pp: 10,
    priority: 0,
    type: "normal",
  },
  "comet-punch": {
    id: "comet-punch",
    accuracy: 85,
    damageClass: "physical",
    effect: "Hits 2-5 times in one turn.",
    effectChance: null,
    learnedBy: [107, 115],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: 5,
      maxTurns: null,
      minHits: 2,
      minTurns: null,
      statChance: 0,
    },
    name: "Comet Punch",
    power: 18,
    pp: 15,
    priority: 0,
    type: "normal",
  },
  "mega-punch": {
    id: "mega-punch",
    accuracy: 85,
    damageClass: "physical",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [
      4, 5, 6, 7, 8, 9, 25, 26, 31, 34, 35, 36, 39, 40, 54, 55, 56, 57, 61, 62,
      63, 64, 65, 66, 67, 68, 74, 75, 76, 80, 94, 96, 97, 104, 105, 106, 107,
      108, 112, 113, 115, 122, 124, 125, 126, 143, 149, 150, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Mega Punch",
    power: 80,
    pp: 20,
    priority: 0,
    type: "normal",
  },
  "pay-day": {
    id: "pay-day",
    accuracy: 100,
    damageClass: "physical",
    effect: "Scatters money on the ground worth five times the user's level.",
    effectChance: null,
    learnedBy: [
      25, 26, 31, 34, 52, 53, 54, 55, 56, 57, 78, 79, 80, 86, 87, 112, 133, 134,
      135, 136, 143, 150, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Pay Day",
    power: 40,
    pp: 20,
    priority: 0,
    type: "normal",
  },
  "fire-punch": {
    id: "fire-punch",
    accuracy: 100,
    damageClass: "physical",
    effect: "Has a $effect_chance% chance to burn the target.",
    effectChance: 10,
    learnedBy: [
      4, 5, 6, 31, 34, 35, 36, 39, 40, 56, 57, 63, 64, 65, 66, 67, 68, 74, 75,
      76, 88, 89, 92, 93, 94, 96, 97, 104, 105, 107, 108, 112, 113, 115, 122,
      125, 126, 143, 149, 150, 151,
    ],
    meta: {
      ailment: "burn",
      ailmentChance: 10,
      category: "damage+ailment",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Fire Punch",
    power: 75,
    pp: 15,
    priority: 0,
    type: "fire",
  },
  "ice-punch": {
    id: "ice-punch",
    accuracy: 100,
    damageClass: "physical",
    effect: "Has a $effect_chance% chance to freeze the target.",
    effectChance: 10,
    learnedBy: [
      7, 8, 9, 31, 34, 35, 36, 39, 40, 54, 55, 56, 57, 61, 62, 63, 64, 65, 66,
      67, 68, 80, 88, 89, 92, 93, 94, 96, 97, 107, 108, 112, 113, 115, 122, 124,
      125, 143, 149, 150, 151,
    ],
    meta: {
      ailment: "freeze",
      ailmentChance: 10,
      category: "damage+ailment",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Ice Punch",
    power: 75,
    pp: 15,
    priority: 0,
    type: "ice",
  },
  "thunder-punch": {
    id: "thunder-punch",
    accuracy: 100,
    damageClass: "physical",
    effect: "Has a $effect_chance% chance to paralyze the target.",
    effectChance: 10,
    learnedBy: [
      4, 5, 6, 25, 26, 31, 34, 35, 36, 39, 40, 56, 57, 63, 64, 65, 66, 67, 68,
      74, 75, 76, 88, 89, 92, 93, 94, 96, 97, 104, 105, 107, 108, 112, 113, 115,
      122, 125, 126, 143, 149, 150, 151,
    ],
    meta: {
      ailment: "paralysis",
      ailmentChance: 10,
      category: "damage+ailment",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Thunder Punch",
    power: 75,
    pp: 15,
    priority: 0,
    type: "electric",
  },
  scratch: {
    id: "scratch",
    accuracy: 100,
    damageClass: "physical",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [
      4, 5, 6, 27, 28, 29, 30, 31, 46, 47, 50, 51, 52, 53, 54, 55, 56, 57, 140,
      141,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Scratch",
    power: 40,
    pp: 35,
    priority: 0,
    type: "normal",
  },
  "vice-grip": {
    id: "vice-grip",
    accuracy: 100,
    damageClass: "physical",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [98, 99, 127],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Vise Grip",
    power: 55,
    pp: 30,
    priority: 0,
    type: "normal",
  },
  guillotine: {
    id: "guillotine",
    accuracy: 30,
    damageClass: "physical",
    effect: "Causes a one-hit KO.",
    effectChance: null,
    learnedBy: [98, 99, 127],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "ohko",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Guillotine",
    power: null,
    pp: 5,
    priority: 0,
    type: "normal",
  },
  "razor-wind": {
    id: "razor-wind",
    accuracy: 100,
    damageClass: "special",
    effect: "Requires a turn to charge before attacking.",
    effectChance: null,
    learnedBy: [
      1, 12, 16, 17, 18, 21, 22, 41, 42, 49, 83, 116, 123, 141, 142, 144, 145,
      146, 149, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 1,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Razor Wind",
    power: 80,
    pp: 10,
    priority: 0,
    type: "normal",
  },
  "swords-dance": {
    id: "swords-dance",
    accuracy: null,
    damageClass: "status",
    effect: "Raises the user's Attack by two stages.",
    effectChance: null,
    learnedBy: [
      1, 2, 3, 4, 5, 6, 15, 20, 27, 28, 43, 44, 45, 46, 47, 50, 51, 69, 70, 71,
      72, 73, 78, 83, 84, 85, 98, 99, 102, 103, 104, 105, 108, 111, 112, 114,
      118, 119, 123, 127, 141, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "net-good-stats",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Swords Dance",
    power: null,
    pp: 20,
    priority: 0,
    type: "normal",
  },
  cut: {
    id: "cut",
    accuracy: 95,
    damageClass: "physical",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [
      1, 2, 3, 4, 5, 6, 15, 19, 20, 27, 28, 29, 30, 31, 32, 33, 34, 43, 44, 45,
      46, 47, 50, 51, 52, 53, 69, 70, 71, 72, 73, 83, 98, 99, 108, 112, 114,
      115, 123, 127, 141, 149, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Cut",
    power: 50,
    pp: 30,
    priority: 0,
    type: "normal",
  },
  gust: {
    id: "gust",
    accuracy: 100,
    damageClass: "special",
    effect: "Inflicts regular damage and can hit Pokémon in the air.",
    effectChance: null,
    learnedBy: [12, 16, 17, 18, 41, 49, 83, 144, 146],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Gust",
    power: 40,
    pp: 35,
    priority: 0,
    type: "flying",
  },
  "wing-attack": {
    id: "wing-attack",
    accuracy: 100,
    damageClass: "physical",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [4, 6, 16, 17, 18, 21, 22, 41, 42, 84, 85, 123, 142, 146, 149],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Wing Attack",
    power: 60,
    pp: 35,
    priority: 0,
    type: "flying",
  },
  whirlwind: {
    id: "whirlwind",
    accuracy: null,
    damageClass: "status",
    effect:
      "Immediately ends wild battles.  Forces trainers to switch Pokémon.",
    effectChance: null,
    learnedBy: [
      12, 16, 17, 18, 21, 22, 41, 42, 49, 83, 84, 85, 142, 143, 144, 145, 146,
      151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "force-switch",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Whirlwind",
    power: null,
    pp: 20,
    priority: -6,
    type: "normal",
  },
  fly: {
    id: "fly",
    accuracy: 95,
    damageClass: "physical",
    effect:
      "User flies high into the air, dodging all attacks, and hits next turn.",
    effectChance: null,
    learnedBy: [
      6, 16, 17, 18, 21, 22, 41, 42, 83, 84, 85, 142, 144, 145, 146, 149, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Fly",
    power: 90,
    pp: 15,
    priority: 0,
    type: "flying",
  },
  bind: {
    id: "bind",
    accuracy: 85,
    damageClass: "physical",
    effect:
      "Prevents the target from fleeing and inflicts damage for 2-5 turns.",
    effectChance: 100,
    learnedBy: [
      1, 2, 3, 23, 24, 69, 70, 71, 72, 73, 95, 108, 114, 127, 130, 138, 139,
      147, 148, 149, 151,
    ],
    meta: {
      ailment: "trap",
      ailmentChance: 100,
      category: "damage+ailment",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: 6,
      minHits: null,
      minTurns: 5,
      statChance: 0,
    },
    name: "Bind",
    power: 15,
    pp: 20,
    priority: 0,
    type: "normal",
  },
  slam: {
    id: "slam",
    accuracy: 75,
    damageClass: "physical",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [
      23, 24, 25, 26, 69, 70, 71, 86, 95, 98, 99, 108, 114, 138, 147, 148, 149,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Slam",
    power: 80,
    pp: 20,
    priority: 0,
    type: "normal",
  },
  "vine-whip": {
    id: "vine-whip",
    accuracy: 100,
    damageClass: "physical",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [1, 2, 3, 69, 70, 71, 114],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Vine Whip",
    power: 45,
    pp: 25,
    priority: 0,
    type: "grass",
  },
  stomp: {
    id: "stomp",
    accuracy: 100,
    damageClass: "physical",
    effect: "Has a $effect_chance% chance to make the target flinch.",
    effectChance: 30,
    learnedBy: [77, 78, 79, 80, 98, 99, 103, 108, 111, 112, 115, 128],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 30,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Stomp",
    power: 65,
    pp: 20,
    priority: 0,
    type: "normal",
  },
  "double-kick": {
    id: "double-kick",
    accuracy: 100,
    damageClass: "physical",
    effect: "Hits twice in one turn.",
    effectChance: null,
    learnedBy: [
      25, 29, 30, 31, 32, 33, 34, 58, 77, 78, 104, 106, 133, 134, 135, 136,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: 2,
      maxTurns: null,
      minHits: 2,
      minTurns: null,
      statChance: 0,
    },
    name: "Double Kick",
    power: 30,
    pp: 30,
    priority: 0,
    type: "fighting",
  },
  "mega-kick": {
    id: "mega-kick",
    accuracy: 75,
    damageClass: "physical",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [
      4, 5, 6, 7, 8, 9, 25, 26, 31, 34, 35, 36, 39, 40, 54, 55, 56, 57, 61, 62,
      63, 64, 65, 66, 67, 68, 76, 80, 94, 96, 97, 104, 105, 106, 107, 108, 112,
      113, 115, 122, 124, 125, 126, 141, 143, 149, 150, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Mega Kick",
    power: 120,
    pp: 5,
    priority: 0,
    type: "normal",
  },
  "jump-kick": {
    id: "jump-kick",
    accuracy: 95,
    damageClass: "physical",
    effect:
      "If the user misses, it takes half the damage it would have inflicted in recoil.",
    effectChance: null,
    learnedBy: [84, 85, 106],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Jump Kick",
    power: 100,
    pp: 10,
    priority: 0,
    type: "fighting",
  },
  "rolling-kick": {
    id: "rolling-kick",
    accuracy: 85,
    damageClass: "physical",
    effect: "Has a $effect_chance% chance to make the target flinch.",
    effectChance: 30,
    learnedBy: [66, 106],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 30,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Rolling Kick",
    power: 60,
    pp: 15,
    priority: 0,
    type: "fighting",
  },
  "sand-attack": {
    id: "sand-attack",
    accuracy: 100,
    damageClass: "status",
    effect: "Lowers the target's accuracy by one stage.",
    effectChance: null,
    learnedBy: [
      16, 17, 18, 27, 28, 50, 51, 74, 75, 76, 83, 111, 112, 133, 134, 135, 136,
      138, 139, 140, 141,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "net-good-stats",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Sand Attack",
    power: null,
    pp: 15,
    priority: 0,
    type: "ground",
  },
  headbutt: {
    id: "headbutt",
    accuracy: 100,
    damageClass: "physical",
    effect: "Has a $effect_chance% chance to make the target flinch.",
    effectChance: 30,
    learnedBy: [
      1, 2, 3, 4, 5, 6, 7, 8, 9, 12, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25,
      26, 27, 28, 29, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 40, 41, 42, 43,
      44, 45, 46, 47, 48, 49, 50, 51, 52, 53, 54, 55, 56, 57, 58, 59, 60, 61,
      62, 63, 64, 65, 66, 67, 68, 69, 70, 71, 72, 73, 74, 75, 76, 77, 78, 79,
      80, 81, 82, 83, 84, 85, 86, 87, 88, 89, 90, 91, 92, 93, 94, 95, 96, 97,
      98, 99, 100, 101, 102, 103, 104, 105, 106, 107, 108, 109, 110, 111, 112,
      113, 114, 115, 116, 117, 118, 119, 120, 121, 122, 123, 124, 125, 126, 127,
      128, 130, 131, 133, 134, 135, 136, 137, 138, 139, 140, 141, 142, 143, 144,
      145, 146, 147, 148, 149, 150, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 30,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Headbutt",
    power: 70,
    pp: 15,
    priority: 0,
    type: "normal",
  },
  "horn-attack": {
    id: "horn-attack",
    accuracy: 100,
    damageClass: "physical",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [32, 33, 34, 111, 112, 118, 119, 128, 138, 139],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Horn Attack",
    power: 65,
    pp: 25,
    priority: 0,
    type: "normal",
  },
  "fury-attack": {
    id: "fury-attack",
    accuracy: 85,
    damageClass: "physical",
    effect: "Hits 2-5 times in one turn.",
    effectChance: null,
    learnedBy: [
      15, 21, 22, 32, 33, 34, 78, 83, 84, 85, 111, 112, 118, 119, 127,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: 5,
      maxTurns: null,
      minHits: 2,
      minTurns: null,
      statChance: 0,
    },
    name: "Fury Attack",
    power: 15,
    pp: 20,
    priority: 0,
    type: "normal",
  },
  "horn-drill": {
    id: "horn-drill",
    accuracy: 30,
    damageClass: "physical",
    effect: "Causes a one-hit KO.",
    effectChance: null,
    learnedBy: [
      30, 31, 32, 33, 34, 77, 78, 86, 87, 111, 112, 118, 119, 128, 131, 139,
      148, 149, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "ohko",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Horn Drill",
    power: null,
    pp: 5,
    priority: 0,
    type: "normal",
  },
  tackle: {
    id: "tackle",
    accuracy: 100,
    damageClass: "physical",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [
      1, 2, 3, 7, 8, 9, 10, 12, 16, 17, 18, 19, 20, 29, 30, 31, 32, 33, 34, 37,
      38, 48, 49, 74, 75, 76, 77, 78, 79, 80, 81, 82, 90, 91, 95, 100, 101, 106,
      107, 109, 110, 111, 112, 120, 121, 128, 129, 130, 133, 134, 135, 136, 137,
      143,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Tackle",
    power: 40,
    pp: 35,
    priority: 0,
    type: "normal",
  },
  "body-slam": {
    id: "body-slam",
    accuracy: 100,
    damageClass: "physical",
    effect: "Has a $effect_chance% chance to paralyze the target.",
    effectChance: 30,
    learnedBy: [
      1, 2, 3, 4, 5, 6, 7, 8, 9, 19, 20, 23, 24, 25, 26, 27, 28, 29, 30, 31, 32,
      33, 34, 35, 36, 37, 38, 39, 40, 45, 46, 47, 50, 51, 52, 53, 54, 55, 56,
      57, 58, 59, 60, 61, 62, 63, 64, 65, 66, 67, 68, 71, 74, 75, 76, 77, 78,
      79, 80, 83, 84, 85, 86, 87, 88, 89, 91, 94, 95, 96, 97, 98, 99, 104, 105,
      106, 107, 108, 111, 112, 113, 114, 115, 118, 119, 122, 124, 125, 126, 127,
      128, 130, 131, 133, 134, 135, 136, 138, 139, 140, 141, 143, 147, 148, 149,
      150, 151,
    ],
    meta: {
      ailment: "paralysis",
      ailmentChance: 30,
      category: "damage+ailment",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Body Slam",
    power: 85,
    pp: 15,
    priority: 0,
    type: "normal",
  },
  wrap: {
    id: "wrap",
    accuracy: 90,
    damageClass: "physical",
    effect:
      "Prevents the target from fleeing and inflicts damage for 2-5 turns.",
    effectChance: 100,
    learnedBy: [23, 24, 69, 70, 71, 72, 73, 108, 147, 148, 149],
    meta: {
      ailment: "trap",
      ailmentChance: 100,
      category: "damage+ailment",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: 6,
      minHits: null,
      minTurns: 5,
      statChance: 0,
    },
    name: "Wrap",
    power: 15,
    pp: 20,
    priority: 0,
    type: "normal",
  },
  "take-down": {
    id: "take-down",
    accuracy: 85,
    damageClass: "physical",
    effect: "User receives 1/4 the damage it inflicts in recoil.",
    effectChance: null,
    learnedBy: [
      1, 2, 3, 4, 5, 6, 7, 8, 9, 12, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25,
      26, 27, 28, 29, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 40, 41, 42, 43,
      44, 45, 46, 47, 48, 49, 50, 51, 52, 53, 54, 55, 56, 57, 58, 59, 60, 61,
      62, 63, 64, 65, 66, 67, 68, 69, 70, 71, 72, 73, 74, 75, 76, 77, 78, 79,
      80, 81, 82, 83, 84, 85, 86, 87, 88, 89, 90, 91, 94, 95, 96, 97, 98, 99,
      100, 101, 102, 103, 104, 105, 106, 107, 108, 111, 112, 113, 114, 115, 116,
      117, 118, 119, 120, 121, 122, 123, 124, 125, 126, 127, 128, 130, 131, 133,
      134, 135, 136, 137, 138, 139, 140, 141, 142, 143, 144, 145, 146, 147, 148,
      149, 150, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: -25,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Take Down",
    power: 90,
    pp: 20,
    priority: 0,
    type: "normal",
  },
  thrash: {
    id: "thrash",
    accuracy: 100,
    damageClass: "physical",
    effect: "Hits every turn for 2-3 turns, then confuses the user.",
    effectChance: null,
    learnedBy: [
      32, 34, 56, 57, 58, 77, 84, 85, 104, 105, 108, 111, 124, 127, 128, 130,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Thrash",
    power: 120,
    pp: 10,
    priority: 0,
    type: "normal",
  },
  "double-edge": {
    id: "double-edge",
    accuracy: 100,
    damageClass: "physical",
    effect: "User receives 1/3 the damage inflicted in recoil.",
    effectChance: null,
    learnedBy: [
      1, 2, 3, 4, 5, 6, 7, 8, 9, 12, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25,
      26, 27, 28, 29, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 40, 41, 42, 43,
      44, 45, 46, 47, 48, 49, 50, 51, 52, 53, 54, 55, 56, 57, 58, 59, 60, 61,
      62, 63, 64, 65, 66, 67, 68, 69, 70, 71, 72, 73, 74, 75, 76, 77, 78, 79,
      80, 81, 82, 83, 84, 85, 86, 87, 90, 91, 94, 95, 96, 97, 98, 99, 102, 103,
      104, 105, 106, 107, 108, 111, 112, 113, 114, 115, 116, 117, 118, 119, 120,
      121, 122, 123, 124, 125, 126, 127, 128, 130, 131, 133, 134, 135, 136, 137,
      138, 139, 140, 141, 142, 143, 144, 145, 146, 147, 148, 149, 150, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: -33,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Double-Edge",
    power: 120,
    pp: 15,
    priority: 0,
    type: "normal",
  },
  "tail-whip": {
    id: "tail-whip",
    accuracy: 100,
    damageClass: "status",
    effect: "Lowers the target's Defense by one stage.",
    effectChance: null,
    learnedBy: [
      7, 8, 9, 19, 20, 25, 26, 29, 30, 31, 37, 38, 52, 54, 55, 77, 78, 104, 105,
      111, 112, 113, 115, 118, 119, 128, 133, 134, 135, 136,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "net-good-stats",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Tail Whip",
    power: null,
    pp: 30,
    priority: 0,
    type: "normal",
  },
  "poison-sting": {
    id: "poison-sting",
    accuracy: 100,
    damageClass: "physical",
    effect: "Has a $effect_chance% chance to poison the target.",
    effectChance: 30,
    learnedBy: [13, 15, 23, 24, 27, 28, 29, 30, 31, 32, 33, 34, 72, 73],
    meta: {
      ailment: "poison",
      ailmentChance: 30,
      category: "damage+ailment",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Poison Sting",
    power: 15,
    pp: 35,
    priority: 0,
    type: "poison",
  },
  twineedle: {
    id: "twineedle",
    accuracy: 100,
    damageClass: "physical",
    effect:
      "Hits twice in the same turn.  Has a $effect_chance% chance to poison the target.",
    effectChance: 20,
    learnedBy: [15, 90, 91],
    meta: {
      ailment: "poison",
      ailmentChance: 20,
      category: "damage+ailment",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: 2,
      maxTurns: null,
      minHits: 2,
      minTurns: null,
      statChance: 0,
    },
    name: "Twineedle",
    power: 25,
    pp: 20,
    priority: 0,
    type: "bug",
  },
  "pin-missile": {
    id: "pin-missile",
    accuracy: 95,
    damageClass: "physical",
    effect: "Hits 2-5 times in one turn.",
    effectChance: null,
    learnedBy: [15, 28, 91, 135, 139, 151],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: 5,
      maxTurns: null,
      minHits: 2,
      minTurns: null,
      statChance: 0,
    },
    name: "Pin Missile",
    power: 25,
    pp: 20,
    priority: 0,
    type: "bug",
  },
  leer: {
    id: "leer",
    accuracy: 100,
    damageClass: "status",
    effect: "Lowers the target's Defense by one stage.",
    effectChance: 100,
    learnedBy: [
      4, 5, 6, 21, 22, 23, 24, 32, 33, 34, 56, 57, 58, 59, 66, 67, 68, 83, 84,
      85, 90, 91, 98, 99, 104, 105, 107, 111, 112, 115, 116, 117, 123, 125, 126,
      128, 130, 136, 138, 139, 140, 141, 144, 145, 146, 147, 148, 149,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "net-good-stats",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 100,
    },
    name: "Leer",
    power: null,
    pp: 30,
    priority: 0,
    type: "normal",
  },
  bite: {
    id: "bite",
    accuracy: 100,
    damageClass: "physical",
    effect: "Has a $effect_chance% chance to make the target flinch.",
    effectChance: 30,
    learnedBy: [
      4, 7, 8, 9, 19, 20, 23, 24, 29, 30, 31, 41, 42, 52, 53, 58, 59, 115, 130,
      133, 134, 135, 136, 138, 139, 142, 143,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 30,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Bite",
    power: 60,
    pp: 25,
    priority: 0,
    type: "dark",
  },
  growl: {
    id: "growl",
    accuracy: 100,
    damageClass: "status",
    effect: "Lowers the target's Attack by one stage.",
    effectChance: null,
    learnedBy: [
      1, 2, 3, 4, 5, 6, 21, 22, 25, 26, 29, 30, 31, 35, 36, 50, 51, 52, 53, 77,
      78, 79, 80, 84, 85, 86, 87, 104, 105, 113, 115, 131, 133, 134, 135, 136,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "net-good-stats",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Growl",
    power: null,
    pp: 40,
    priority: 0,
    type: "normal",
  },
  roar: {
    id: "roar",
    accuracy: null,
    damageClass: "status",
    effect:
      "Immediately ends wild battles.  Forces trainers to switch Pokémon.",
    effectChance: null,
    learnedBy: [
      3, 6, 9, 20, 31, 34, 37, 38, 53, 58, 59, 76, 95, 111, 112, 115, 130, 131,
      134, 135, 136, 142, 144, 145, 146, 149, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "force-switch",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Roar",
    power: null,
    pp: 20,
    priority: -6,
    type: "normal",
  },
  sing: {
    id: "sing",
    accuracy: 55,
    damageClass: "status",
    effect: "Puts the target to sleep.",
    effectChance: null,
    learnedBy: [35, 36, 39, 40, 113, 124, 131],
    meta: {
      ailment: "sleep",
      ailmentChance: 0,
      category: "ailment",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: 4,
      minHits: null,
      minTurns: 2,
      statChance: 0,
    },
    name: "Sing",
    power: null,
    pp: 15,
    priority: 0,
    type: "normal",
  },
  supersonic: {
    id: "supersonic",
    accuracy: 55,
    damageClass: "status",
    effect: "Confuses the target.",
    effectChance: null,
    learnedBy: [
      12, 29, 31, 32, 34, 41, 42, 48, 49, 72, 73, 81, 82, 84, 85, 90, 91, 108,
      118, 119, 120, 138, 139, 142, 147,
    ],
    meta: {
      ailment: "confusion",
      ailmentChance: 0,
      category: "ailment",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: 5,
      minHits: null,
      minTurns: 2,
      statChance: 0,
    },
    name: "Supersonic",
    power: null,
    pp: 20,
    priority: 0,
    type: "normal",
  },
  "sonic-boom": {
    id: "sonic-boom",
    accuracy: 90,
    damageClass: "special",
    effect: "Inflicts 20 points of damage.",
    effectChance: null,
    learnedBy: [81, 82, 100, 101],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Sonic Boom",
    power: null,
    pp: 20,
    priority: 0,
    type: "normal",
  },
  disable: {
    id: "disable",
    accuracy: 100,
    damageClass: "status",
    effect: "Disables the target's last used move for 1-8 turns.",
    effectChance: null,
    learnedBy: [
      23, 29, 32, 37, 38, 39, 40, 48, 49, 54, 55, 64, 65, 79, 80, 86, 88, 89,
      92, 94, 96, 97, 108, 115, 116, 117, 150,
    ],
    meta: {
      ailment: "disable",
      ailmentChance: 0,
      category: "unique",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: 4,
      minHits: null,
      minTurns: 4,
      statChance: 0,
    },
    name: "Disable",
    power: null,
    pp: 20,
    priority: 0,
    type: "normal",
  },
  acid: {
    id: "acid",
    accuracy: 100,
    damageClass: "special",
    effect:
      "Has a $effect_chance% chance to lower the target's Special Defense by one stage.",
    effectChance: 10,
    learnedBy: [23, 24, 43, 44, 45, 69, 70, 71, 72, 73, 108],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage+lower",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 10,
    },
    name: "Acid",
    power: 40,
    pp: 30,
    priority: 0,
    type: "poison",
  },
  ember: {
    id: "ember",
    accuracy: 100,
    damageClass: "special",
    effect: "Has a $effect_chance% chance to burn the target.",
    effectChance: 10,
    learnedBy: [4, 5, 6, 37, 38, 58, 59, 77, 78, 126, 136, 146],
    meta: {
      ailment: "burn",
      ailmentChance: 10,
      category: "damage+ailment",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Ember",
    power: 40,
    pp: 25,
    priority: 0,
    type: "fire",
  },
  flamethrower: {
    id: "flamethrower",
    accuracy: 100,
    damageClass: "special",
    effect: "Has a $effect_chance% chance to burn the target.",
    effectChance: 10,
    learnedBy: [
      4, 5, 6, 31, 34, 35, 36, 37, 38, 39, 40, 58, 59, 66, 67, 68, 74, 75, 76,
      77, 78, 79, 80, 88, 89, 104, 105, 108, 109, 110, 111, 112, 113, 115, 126,
      128, 130, 136, 142, 143, 146, 147, 148, 149, 150, 151,
    ],
    meta: {
      ailment: "burn",
      ailmentChance: 10,
      category: "damage+ailment",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Flamethrower",
    power: 90,
    pp: 15,
    priority: 0,
    type: "fire",
  },
  mist: {
    id: "mist",
    accuracy: null,
    damageClass: "status",
    effect: "Protects the user's stats from being changed by enemy moves.",
    effectChance: null,
    learnedBy: [7, 60, 62, 131, 134, 144, 147, 149, 150],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "field-effect",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Mist",
    power: null,
    pp: 30,
    priority: 0,
    type: "ice",
  },
  "water-gun": {
    id: "water-gun",
    accuracy: 100,
    damageClass: "special",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [
      7, 8, 9, 19, 20, 30, 31, 33, 34, 35, 36, 39, 40, 52, 53, 54, 55, 60, 61,
      62, 72, 73, 79, 80, 86, 87, 90, 91, 98, 99, 104, 105, 108, 112, 113, 115,
      116, 117, 118, 119, 120, 121, 124, 130, 131, 134, 138, 139, 140, 141, 143,
      144, 147, 148, 149, 150, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Water Gun",
    power: 40,
    pp: 25,
    priority: 0,
    type: "water",
  },
  "hydro-pump": {
    id: "hydro-pump",
    accuracy: 80,
    damageClass: "special",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [
      7, 8, 9, 54, 55, 60, 61, 62, 72, 73, 79, 80, 90, 91, 99, 108, 112, 115,
      116, 117, 118, 119, 120, 121, 129, 130, 131, 134, 138, 139, 140, 141, 143,
      147, 148, 149, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Hydro Pump",
    power: 110,
    pp: 5,
    priority: 0,
    type: "water",
  },
  surf: {
    id: "surf",
    accuracy: 100,
    damageClass: "special",
    effect: "Inflicts regular damage and can hit Dive users.",
    effectChance: null,
    learnedBy: [
      7, 8, 9, 25, 26, 31, 34, 54, 55, 60, 61, 62, 72, 73, 79, 80, 86, 87, 90,
      91, 98, 99, 108, 112, 115, 116, 117, 118, 119, 120, 121, 128, 130, 131,
      134, 138, 139, 140, 141, 143, 147, 148, 149, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Surf",
    power: 90,
    pp: 15,
    priority: 0,
    type: "water",
  },
  "ice-beam": {
    id: "ice-beam",
    accuracy: 100,
    damageClass: "special",
    effect: "Has a $effect_chance% chance to freeze the target.",
    effectChance: 10,
    learnedBy: [
      7, 8, 9, 19, 20, 29, 30, 31, 32, 33, 34, 35, 36, 39, 40, 54, 55, 60, 61,
      62, 72, 73, 79, 80, 86, 87, 90, 91, 98, 99, 104, 105, 108, 111, 112, 113,
      115, 116, 117, 118, 119, 120, 121, 124, 128, 130, 131, 134, 137, 138, 139,
      140, 141, 143, 144, 147, 148, 149, 150, 151,
    ],
    meta: {
      ailment: "freeze",
      ailmentChance: 10,
      category: "damage+ailment",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Ice Beam",
    power: 90,
    pp: 10,
    priority: 0,
    type: "ice",
  },
  blizzard: {
    id: "blizzard",
    accuracy: 70,
    damageClass: "special",
    effect: "Has a $effect_chance% chance to freeze the target.",
    effectChance: 10,
    learnedBy: [
      7, 8, 9, 19, 20, 29, 30, 31, 32, 33, 34, 35, 36, 39, 40, 54, 55, 60, 61,
      62, 72, 73, 79, 80, 86, 87, 90, 91, 98, 99, 104, 105, 108, 111, 112, 113,
      115, 116, 117, 118, 119, 120, 121, 124, 128, 130, 131, 134, 137, 138, 139,
      140, 141, 143, 144, 147, 148, 149, 150, 151,
    ],
    meta: {
      ailment: "freeze",
      ailmentChance: 10,
      category: "damage+ailment",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Blizzard",
    power: 110,
    pp: 5,
    priority: 0,
    type: "ice",
  },
  psybeam: {
    id: "psybeam",
    accuracy: 100,
    damageClass: "special",
    effect: "Has a $effect_chance% chance to confuse the target.",
    effectChance: 10,
    learnedBy: [
      12, 46, 48, 49, 54, 55, 64, 65, 79, 80, 96, 97, 102, 109, 110, 118, 119,
      120, 121, 122, 137, 150, 151,
    ],
    meta: {
      ailment: "confusion",
      ailmentChance: 10,
      category: "damage+ailment",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: 5,
      minHits: null,
      minTurns: 2,
      statChance: 0,
    },
    name: "Psybeam",
    power: 65,
    pp: 20,
    priority: 0,
    type: "psychic",
  },
  "bubble-beam": {
    id: "bubble-beam",
    accuracy: 100,
    damageClass: "special",
    effect:
      "Has a $effect_chance% chance to lower the target's Speed by one stage.",
    effectChance: 10,
    learnedBy: [
      7, 8, 9, 19, 20, 30, 31, 33, 34, 35, 36, 39, 40, 52, 53, 54, 55, 60, 61,
      62, 72, 73, 79, 80, 86, 87, 90, 91, 98, 99, 104, 105, 108, 112, 113, 115,
      116, 117, 118, 119, 120, 121, 124, 130, 131, 134, 138, 139, 140, 141, 143,
      144, 147, 148, 149, 150, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage+lower",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 10,
    },
    name: "Bubble Beam",
    power: 65,
    pp: 20,
    priority: 0,
    type: "water",
  },
  "aurora-beam": {
    id: "aurora-beam",
    accuracy: 100,
    damageClass: "special",
    effect:
      "Has a $effect_chance% chance to lower the target's Attack by one stage.",
    effectChance: 10,
    learnedBy: [72, 86, 87, 90, 91, 116, 120, 131, 134, 138, 140],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage+lower",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 10,
    },
    name: "Aurora Beam",
    power: 65,
    pp: 20,
    priority: 0,
    type: "ice",
  },
  "hyper-beam": {
    id: "hyper-beam",
    accuracy: 90,
    damageClass: "special",
    effect: "User foregoes its next turn to recharge.",
    effectChance: null,
    learnedBy: [
      3, 6, 9, 12, 15, 18, 20, 22, 24, 26, 28, 31, 34, 36, 38, 40, 42, 45, 47,
      49, 51, 53, 55, 57, 59, 62, 65, 68, 71, 73, 76, 78, 80, 82, 85, 87, 89,
      91, 94, 97, 99, 101, 103, 105, 108, 110, 112, 113, 114, 115, 117, 119,
      121, 122, 123, 124, 125, 126, 127, 128, 130, 131, 134, 135, 136, 137, 139,
      141, 142, 143, 144, 145, 146, 147, 148, 149, 150, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Hyper Beam",
    power: 150,
    pp: 5,
    priority: 0,
    type: "normal",
  },
  peck: {
    id: "peck",
    accuracy: 100,
    damageClass: "physical",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [
      15, 21, 22, 32, 33, 34, 83, 84, 85, 86, 118, 119, 144, 145, 146,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Peck",
    power: 35,
    pp: 35,
    priority: 0,
    type: "flying",
  },
  "drill-peck": {
    id: "drill-peck",
    accuracy: 100,
    damageClass: "physical",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [21, 22, 84, 85, 145],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Drill Peck",
    power: 80,
    pp: 20,
    priority: 0,
    type: "flying",
  },
  submission: {
    id: "submission",
    accuracy: 80,
    damageClass: "physical",
    effect: "User receives 1/4 the damage it inflicts in recoil.",
    effectChance: null,
    learnedBy: [
      4, 5, 6, 7, 8, 9, 25, 26, 27, 28, 31, 34, 35, 36, 39, 40, 54, 55, 56, 57,
      61, 62, 63, 64, 65, 66, 67, 68, 74, 75, 76, 80, 94, 96, 97, 104, 105, 106,
      107, 108, 112, 113, 115, 122, 124, 125, 126, 127, 139, 141, 143, 150, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: -25,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Submission",
    power: 80,
    pp: 20,
    priority: 0,
    type: "fighting",
  },
  "low-kick": {
    id: "low-kick",
    accuracy: 100,
    damageClass: "physical",
    effect:
      "Inflicts more damage to heavier targets, with a maximum of 120 power.",
    effectChance: null,
    learnedBy: [
      54, 55, 56, 57, 60, 61, 66, 67, 68, 77, 78, 96, 97, 103, 104, 105, 106,
      107, 115, 125, 126, 141, 149, 150, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Low Kick",
    power: null,
    pp: 20,
    priority: 0,
    type: "fighting",
  },
  counter: {
    id: "counter",
    accuracy: 100,
    damageClass: "physical",
    effect:
      "Inflicts twice the damage the user received from the last physical hit it took.",
    effectChance: null,
    learnedBy: [
      4, 5, 6, 7, 8, 9, 19, 20, 25, 26, 27, 28, 29, 30, 31, 32, 33, 34, 35, 36,
      39, 40, 46, 47, 54, 55, 56, 57, 61, 62, 63, 64, 65, 66, 67, 68, 74, 75,
      76, 80, 94, 96, 97, 104, 105, 106, 107, 108, 111, 112, 113, 115, 122, 123,
      124, 125, 126, 143, 150, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Counter",
    power: null,
    pp: 20,
    priority: -5,
    type: "fighting",
  },
  "seismic-toss": {
    id: "seismic-toss",
    accuracy: 100,
    damageClass: "physical",
    effect: "Inflicts damage equal to the user's level.",
    effectChance: null,
    learnedBy: [
      4, 5, 6, 7, 8, 9, 25, 26, 27, 28, 31, 34, 35, 36, 39, 40, 54, 55, 56, 57,
      61, 62, 63, 64, 65, 66, 67, 68, 74, 75, 76, 80, 94, 96, 97, 104, 105, 106,
      107, 108, 112, 113, 115, 122, 124, 125, 126, 127, 139, 141, 143, 150, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Seismic Toss",
    power: null,
    pp: 20,
    priority: 0,
    type: "fighting",
  },
  strength: {
    id: "strength",
    accuracy: 100,
    damageClass: "physical",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [
      1, 2, 3, 4, 5, 6, 7, 8, 9, 20, 23, 24, 25, 26, 27, 28, 29, 30, 31, 32, 33,
      34, 35, 36, 39, 40, 54, 55, 56, 57, 58, 59, 61, 62, 66, 67, 68, 74, 75,
      76, 77, 78, 79, 80, 86, 87, 88, 89, 94, 95, 98, 99, 102, 103, 104, 105,
      106, 107, 108, 111, 112, 113, 115, 125, 126, 127, 128, 130, 131, 134, 135,
      136, 142, 143, 149, 150, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Strength",
    power: 80,
    pp: 15,
    priority: 0,
    type: "normal",
  },
  absorb: {
    id: "absorb",
    accuracy: 100,
    damageClass: "special",
    effect: "Drains half the damage inflicted to heal the user.",
    effectChance: null,
    learnedBy: [41, 42, 43, 44, 45, 46, 47, 102, 103, 114, 140, 141],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage+heal",
      critRate: 0,
      drain: 50,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Absorb",
    power: 20,
    pp: 25,
    priority: 0,
    type: "grass",
  },
  "mega-drain": {
    id: "mega-drain",
    accuracy: 100,
    damageClass: "special",
    effect: "Drains half the damage inflicted to heal the user.",
    effectChance: null,
    learnedBy: [
      1, 2, 3, 12, 15, 23, 24, 41, 42, 43, 44, 45, 46, 47, 48, 49, 69, 70, 71,
      72, 73, 88, 89, 92, 93, 94, 102, 103, 114, 140, 141, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage+heal",
      critRate: 0,
      drain: 50,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Mega Drain",
    power: 40,
    pp: 15,
    priority: 0,
    type: "grass",
  },
  "leech-seed": {
    id: "leech-seed",
    accuracy: 90,
    damageClass: "status",
    effect: "Seeds the target, stealing HP from it every turn.",
    effectChance: null,
    learnedBy: [1, 2, 3, 43, 46, 47, 102, 103, 114],
    meta: {
      ailment: "leech-seed",
      ailmentChance: 0,
      category: "ailment",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Leech Seed",
    power: null,
    pp: 10,
    priority: 0,
    type: "grass",
  },
  growth: {
    id: "growth",
    accuracy: null,
    damageClass: "status",
    effect: "Raises the user's Attack and Special Attack by one stage.",
    effectChance: null,
    learnedBy: [1, 2, 3, 43, 44, 45, 46, 47, 69, 70, 71, 114],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "net-good-stats",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Growth",
    power: null,
    pp: 20,
    priority: 0,
    type: "normal",
  },
  "razor-leaf": {
    id: "razor-leaf",
    accuracy: 95,
    damageClass: "physical",
    effect: "Has an increased chance for a critical hit.",
    effectChance: null,
    learnedBy: [1, 2, 3, 43, 44, 69, 70, 71, 83],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 1,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Razor Leaf",
    power: 55,
    pp: 25,
    priority: 0,
    type: "grass",
  },
  "solar-beam": {
    id: "solar-beam",
    accuracy: 100,
    damageClass: "special",
    effect: "Requires a turn to charge before attacking.",
    effectChance: null,
    learnedBy: [
      1, 2, 3, 6, 12, 15, 35, 36, 38, 39, 40, 43, 44, 45, 46, 47, 48, 49, 59,
      69, 70, 71, 77, 78, 102, 103, 108, 113, 114, 115, 122, 128, 131, 137, 143,
      146, 150, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Solar Beam",
    power: 120,
    pp: 10,
    priority: 0,
    type: "grass",
  },
  "poison-powder": {
    id: "poison-powder",
    accuracy: 75,
    damageClass: "status",
    effect: "Poisons the target.",
    effectChance: null,
    learnedBy: [1, 2, 3, 12, 43, 44, 45, 46, 47, 48, 49, 69, 70, 71, 102, 114],
    meta: {
      ailment: "poison",
      ailmentChance: 0,
      category: "ailment",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Poison Powder",
    power: null,
    pp: 35,
    priority: 0,
    type: "poison",
  },
  "stun-spore": {
    id: "stun-spore",
    accuracy: 75,
    damageClass: "status",
    effect: "Paralyzes the target.",
    effectChance: null,
    learnedBy: [12, 43, 44, 45, 46, 47, 48, 49, 69, 70, 71, 102, 103, 114],
    meta: {
      ailment: "paralysis",
      ailmentChance: 0,
      category: "ailment",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Stun Spore",
    power: null,
    pp: 30,
    priority: 0,
    type: "grass",
  },
  "sleep-powder": {
    id: "sleep-powder",
    accuracy: 75,
    damageClass: "status",
    effect: "Puts the target to sleep.",
    effectChance: null,
    learnedBy: [1, 2, 3, 12, 43, 44, 45, 46, 47, 48, 49, 69, 70, 71, 102, 114],
    meta: {
      ailment: "sleep",
      ailmentChance: 0,
      category: "ailment",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: 4,
      minHits: null,
      minTurns: 2,
      statChance: 0,
    },
    name: "Sleep Powder",
    power: null,
    pp: 15,
    priority: 0,
    type: "grass",
  },
  "petal-dance": {
    id: "petal-dance",
    accuracy: 100,
    damageClass: "special",
    effect: "Hits every turn for 2-3 turns, then confuses the user.",
    effectChance: null,
    learnedBy: [1, 3, 43, 44, 45],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Petal Dance",
    power: 120,
    pp: 10,
    priority: 0,
    type: "grass",
  },
  "string-shot": {
    id: "string-shot",
    accuracy: 95,
    damageClass: "status",
    effect: "Lowers the target's Speed by two stages.",
    effectChance: null,
    learnedBy: [1, 2, 3, 10, 11, 12, 13, 14, 15, 46, 47, 48, 49, 127, 151],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "net-good-stats",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "String Shot",
    power: null,
    pp: 40,
    priority: 0,
    type: "bug",
  },
  "dragon-rage": {
    id: "dragon-rage",
    accuracy: 100,
    damageClass: "special",
    effect: "Inflicts 40 points of damage.",
    effectChance: null,
    learnedBy: [4, 5, 6, 58, 59, 116, 130, 131, 142, 147, 148, 149, 151],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Dragon Rage",
    power: null,
    pp: 10,
    priority: 0,
    type: "dragon",
  },
  "fire-spin": {
    id: "fire-spin",
    accuracy: 85,
    damageClass: "special",
    effect:
      "Prevents the target from fleeing and inflicts damage for 2-5 turns.",
    effectChance: 100,
    learnedBy: [
      4, 5, 6, 37, 38, 58, 59, 77, 78, 126, 136, 146, 147, 148, 149, 151,
    ],
    meta: {
      ailment: "trap",
      ailmentChance: 100,
      category: "damage+ailment",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: 6,
      minHits: null,
      minTurns: 5,
      statChance: 0,
    },
    name: "Fire Spin",
    power: 35,
    pp: 15,
    priority: 0,
    type: "fire",
  },
  "thunder-shock": {
    id: "thunder-shock",
    accuracy: 100,
    damageClass: "special",
    effect: "Has a $effect_chance% chance to paralyze the target.",
    effectChance: 10,
    learnedBy: [25, 26, 81, 82, 100, 101, 125, 135, 137, 145],
    meta: {
      ailment: "paralysis",
      ailmentChance: 10,
      category: "damage+ailment",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Thunder Shock",
    power: 40,
    pp: 30,
    priority: 0,
    type: "electric",
  },
  thunderbolt: {
    id: "thunderbolt",
    accuracy: 100,
    damageClass: "special",
    effect: "Has a $effect_chance% chance to paralyze the target.",
    effectChance: 10,
    learnedBy: [
      19, 20, 25, 26, 29, 30, 31, 32, 33, 34, 35, 36, 39, 40, 52, 53, 56, 57,
      81, 82, 88, 89, 92, 93, 94, 100, 101, 108, 109, 110, 111, 112, 113, 115,
      120, 121, 122, 125, 128, 130, 131, 135, 137, 143, 145, 147, 148, 149, 150,
      151,
    ],
    meta: {
      ailment: "paralysis",
      ailmentChance: 10,
      category: "damage+ailment",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Thunderbolt",
    power: 90,
    pp: 15,
    priority: 0,
    type: "electric",
  },
  "thunder-wave": {
    id: "thunder-wave",
    accuracy: 90,
    damageClass: "status",
    effect: "Paralyzes the target.",
    effectChance: null,
    learnedBy: [
      19, 20, 25, 26, 35, 36, 39, 40, 52, 53, 63, 64, 65, 79, 80, 81, 82, 94,
      96, 97, 100, 101, 113, 120, 121, 122, 125, 130, 135, 137, 145, 147, 148,
      149, 150, 151,
    ],
    meta: {
      ailment: "paralysis",
      ailmentChance: 0,
      category: "ailment",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Thunder Wave",
    power: null,
    pp: 20,
    priority: 0,
    type: "electric",
  },
  thunder: {
    id: "thunder",
    accuracy: 70,
    damageClass: "special",
    effect: "Has a $effect_chance% chance to paralyze the target.",
    effectChance: 30,
    learnedBy: [
      19, 20, 25, 26, 29, 30, 31, 32, 33, 34, 35, 36, 39, 40, 52, 53, 56, 57,
      81, 82, 88, 89, 92, 93, 94, 100, 101, 108, 109, 110, 111, 112, 113, 115,
      120, 121, 122, 125, 128, 130, 131, 135, 137, 143, 145, 147, 148, 149, 150,
      151,
    ],
    meta: {
      ailment: "paralysis",
      ailmentChance: 30,
      category: "damage+ailment",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Thunder",
    power: 110,
    pp: 10,
    priority: 0,
    type: "electric",
  },
  "rock-throw": {
    id: "rock-throw",
    accuracy: 90,
    damageClass: "physical",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [74, 75, 76, 95, 111, 112, 138, 139, 140, 141, 142],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Rock Throw",
    power: 50,
    pp: 15,
    priority: 0,
    type: "rock",
  },
  earthquake: {
    id: "earthquake",
    accuracy: 100,
    damageClass: "physical",
    effect: "Inflicts regular damage and can hit Dig users.",
    effectChance: null,
    learnedBy: [
      3, 6, 9, 23, 24, 27, 28, 31, 34, 50, 51, 56, 57, 61, 62, 66, 67, 68, 74,
      75, 76, 79, 80, 95, 104, 105, 106, 107, 108, 111, 112, 113, 115, 127, 128,
      130, 142, 143, 149, 150, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Earthquake",
    power: 100,
    pp: 10,
    priority: 0,
    type: "ground",
  },
  fissure: {
    id: "fissure",
    accuracy: 30,
    damageClass: "physical",
    effect: "Causes a one-hit KO.",
    effectChance: null,
    learnedBy: [
      6, 9, 23, 24, 27, 28, 31, 34, 50, 51, 61, 62, 66, 67, 68, 74, 75, 76, 79,
      80, 95, 104, 105, 108, 111, 112, 115, 128, 131, 143, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "ohko",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Fissure",
    power: null,
    pp: 5,
    priority: 0,
    type: "ground",
  },
  dig: {
    id: "dig",
    accuracy: 100,
    damageClass: "physical",
    effect: "User digs underground, dodging all attacks, and hits next turn.",
    effectChance: null,
    learnedBy: [
      4, 5, 6, 7, 8, 9, 19, 20, 23, 24, 25, 26, 27, 28, 29, 30, 31, 32, 33, 34,
      35, 36, 37, 38, 39, 40, 46, 47, 50, 51, 52, 53, 54, 55, 56, 57, 58, 59,
      60, 61, 62, 64, 65, 66, 67, 68, 74, 75, 76, 79, 80, 88, 89, 95, 98, 99,
      104, 105, 108, 111, 112, 115, 127, 128, 133, 134, 135, 136, 140, 141, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Dig",
    power: 80,
    pp: 10,
    priority: 0,
    type: "ground",
  },
  toxic: {
    id: "toxic",
    accuracy: 90,
    damageClass: "status",
    effect: "Badly poisons the target, inflicting more damage every turn.",
    effectChance: null,
    learnedBy: [
      1, 2, 3, 4, 5, 6, 7, 8, 9, 12, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25,
      26, 27, 28, 29, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 40, 41, 42, 43,
      44, 45, 46, 47, 48, 49, 50, 51, 52, 53, 54, 55, 56, 57, 58, 59, 60, 61,
      62, 63, 64, 65, 66, 67, 68, 69, 70, 71, 72, 73, 74, 75, 76, 77, 78, 79,
      80, 81, 82, 83, 84, 85, 86, 87, 88, 89, 90, 91, 92, 93, 94, 95, 96, 97,
      98, 99, 100, 101, 102, 103, 104, 105, 106, 107, 108, 109, 110, 111, 112,
      113, 114, 115, 116, 117, 118, 119, 120, 121, 122, 123, 124, 125, 126, 127,
      128, 130, 131, 133, 134, 135, 136, 137, 138, 139, 140, 141, 142, 143, 144,
      145, 146, 147, 148, 149, 150, 151,
    ],
    meta: {
      ailment: "poison",
      ailmentChance: 0,
      category: "ailment",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: 15,
      minHits: null,
      minTurns: 15,
      statChance: 0,
    },
    name: "Toxic",
    power: null,
    pp: 10,
    priority: 0,
    type: "poison",
  },
  confusion: {
    id: "confusion",
    accuracy: 100,
    damageClass: "special",
    effect: "Has a $effect_chance% chance to confuse the target.",
    effectChance: 10,
    learnedBy: [
      7, 12, 32, 48, 49, 54, 55, 63, 64, 65, 79, 80, 96, 97, 102, 103, 114, 122,
      124, 150, 151,
    ],
    meta: {
      ailment: "confusion",
      ailmentChance: 10,
      category: "damage+ailment",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: 5,
      minHits: null,
      minTurns: 2,
      statChance: 0,
    },
    name: "Confusion",
    power: 50,
    pp: 25,
    priority: 0,
    type: "psychic",
  },
  psychic: {
    id: "psychic",
    accuracy: 100,
    damageClass: "special",
    effect:
      "Has a $effect_chance% chance to lower the target's Special Defense by one stage.",
    effectChance: 10,
    learnedBy: [
      12, 35, 36, 39, 40, 48, 49, 54, 55, 60, 61, 62, 63, 64, 65, 79, 80, 92,
      93, 94, 96, 97, 102, 103, 113, 120, 121, 122, 124, 125, 126, 131, 137,
      143, 150, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage+lower",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 10,
    },
    name: "Psychic",
    power: 90,
    pp: 10,
    priority: 0,
    type: "psychic",
  },
  hypnosis: {
    id: "hypnosis",
    accuracy: 60,
    damageClass: "status",
    effect: "Puts the target to sleep.",
    effectChance: null,
    learnedBy: [
      37, 38, 41, 52, 53, 54, 60, 61, 62, 77, 78, 92, 93, 94, 96, 97, 102, 103,
      122, 151,
    ],
    meta: {
      ailment: "sleep",
      ailmentChance: 0,
      category: "ailment",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: 4,
      minHits: null,
      minTurns: 2,
      statChance: 0,
    },
    name: "Hypnosis",
    power: null,
    pp: 20,
    priority: 0,
    type: "psychic",
  },
  meditate: {
    id: "meditate",
    accuracy: null,
    damageClass: "status",
    effect: "Raises the user's Attack by one stage.",
    effectChance: null,
    learnedBy: [56, 66, 96, 97, 106, 122],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "net-good-stats",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Meditate",
    power: null,
    pp: 40,
    priority: 0,
    type: "psychic",
  },
  agility: {
    id: "agility",
    accuracy: null,
    damageClass: "status",
    effect: "Raises the user's Speed by two stages.",
    effectChance: null,
    learnedBy: [
      15, 16, 17, 18, 21, 22, 25, 26, 27, 28, 37, 38, 41, 42, 46, 48, 49, 50,
      51, 52, 53, 58, 59, 77, 78, 83, 84, 85, 98, 99, 100, 101, 107, 116, 117,
      118, 119, 121, 123, 135, 137, 142, 144, 145, 146, 147, 148, 149, 150, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "net-good-stats",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Agility",
    power: null,
    pp: 30,
    priority: 0,
    type: "psychic",
  },
  "quick-attack": {
    id: "quick-attack",
    accuracy: 100,
    damageClass: "physical",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [
      16, 17, 18, 19, 20, 21, 22, 25, 26, 37, 38, 41, 42, 77, 78, 83, 84, 85,
      118, 119, 123, 125, 127, 133, 134, 135, 136,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Quick Attack",
    power: 40,
    pp: 30,
    priority: 1,
    type: "normal",
  },
  rage: {
    id: "rage",
    accuracy: 100,
    damageClass: "physical",
    effect:
      "If the user is hit after using this move, its Attack rises by one stage.",
    effectChance: null,
    learnedBy: [
      1, 2, 3, 4, 5, 6, 7, 8, 9, 12, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25,
      26, 27, 28, 29, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 40, 41, 42, 43,
      44, 45, 46, 47, 48, 49, 50, 51, 52, 53, 54, 55, 56, 57, 58, 59, 60, 61,
      62, 63, 64, 65, 66, 67, 68, 69, 70, 71, 72, 73, 74, 75, 76, 77, 78, 79,
      80, 81, 82, 83, 84, 85, 86, 87, 88, 89, 90, 91, 92, 93, 94, 95, 96, 97,
      98, 99, 100, 101, 102, 103, 104, 105, 106, 107, 108, 109, 110, 111, 112,
      113, 114, 115, 116, 117, 118, 119, 120, 121, 122, 123, 124, 125, 126, 127,
      128, 130, 131, 133, 134, 135, 136, 137, 138, 139, 140, 141, 142, 143, 144,
      145, 146, 147, 148, 149, 150, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Rage",
    power: 20,
    pp: 20,
    priority: 0,
    type: "normal",
  },
  teleport: {
    id: "teleport",
    accuracy: null,
    damageClass: "status",
    effect: "Immediately ends wild battles.  No effect otherwise.",
    effectChance: null,
    learnedBy: [
      12, 35, 36, 39, 40, 49, 59, 63, 64, 65, 79, 80, 81, 82, 90, 91, 96, 97,
      100, 101, 102, 103, 113, 120, 121, 122, 124, 125, 126, 137, 150, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "unique",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Teleport",
    power: null,
    pp: 20,
    priority: -6,
    type: "psychic",
  },
  "night-shade": {
    id: "night-shade",
    accuracy: 100,
    damageClass: "special",
    effect: "Inflicts damage equal to the user's level.",
    effectChance: null,
    learnedBy: [48, 49, 64, 65, 92, 93, 94, 96, 97, 150, 151],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Night Shade",
    power: null,
    pp: 15,
    priority: 0,
    type: "ghost",
  },
  mimic: {
    id: "mimic",
    accuracy: null,
    damageClass: "status",
    effect: "Copies the target's last used move.",
    effectChance: null,
    learnedBy: [
      1, 2, 3, 4, 5, 6, 7, 8, 9, 12, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25,
      26, 27, 28, 29, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 40, 41, 42, 43,
      44, 45, 46, 47, 48, 49, 50, 51, 52, 53, 54, 55, 56, 57, 58, 59, 60, 61,
      62, 63, 64, 65, 66, 67, 68, 69, 70, 71, 72, 73, 74, 75, 76, 77, 78, 79,
      80, 81, 82, 83, 84, 85, 86, 87, 88, 89, 90, 91, 92, 93, 94, 95, 96, 97,
      98, 99, 100, 101, 102, 103, 104, 105, 106, 107, 108, 109, 110, 111, 112,
      113, 114, 115, 116, 117, 118, 119, 120, 121, 122, 123, 124, 125, 126, 127,
      128, 130, 131, 133, 134, 135, 136, 137, 138, 139, 140, 141, 142, 143, 144,
      145, 146, 147, 148, 149, 150, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "unique",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Mimic",
    power: null,
    pp: 10,
    priority: 0,
    type: "normal",
  },
  screech: {
    id: "screech",
    accuracy: 85,
    damageClass: "status",
    effect: "Lowers the target's Defense by two stages.",
    effectChance: null,
    learnedBy: [
      19, 23, 24, 39, 40, 42, 46, 47, 48, 49, 50, 51, 52, 53, 54, 55, 56, 57,
      72, 73, 81, 82, 88, 89, 90, 91, 95, 100, 101, 104, 105, 108, 109, 110,
      124, 125, 126, 140, 141, 143, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "net-good-stats",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Screech",
    power: null,
    pp: 40,
    priority: 0,
    type: "normal",
  },
  "double-team": {
    id: "double-team",
    accuracy: null,
    damageClass: "status",
    effect: "Raises the user's evasion by one stage.",
    effectChance: null,
    learnedBy: [
      1, 2, 3, 4, 5, 6, 7, 8, 9, 12, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25,
      26, 27, 28, 29, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 40, 41, 42, 43,
      44, 45, 46, 47, 48, 49, 50, 51, 52, 53, 54, 55, 56, 57, 58, 59, 60, 61,
      62, 63, 64, 65, 66, 67, 68, 69, 70, 71, 72, 73, 74, 75, 76, 77, 78, 79,
      80, 81, 82, 83, 84, 85, 86, 87, 88, 89, 90, 91, 92, 93, 94, 95, 96, 97,
      98, 99, 100, 101, 102, 103, 104, 105, 106, 107, 108, 109, 110, 111, 112,
      113, 114, 115, 116, 117, 118, 119, 120, 121, 122, 123, 124, 125, 126, 127,
      128, 130, 131, 133, 134, 135, 136, 137, 138, 139, 140, 141, 142, 143, 144,
      145, 146, 147, 148, 149, 150, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "net-good-stats",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Double Team",
    power: null,
    pp: 15,
    priority: 0,
    type: "normal",
  },
  recover: {
    id: "recover",
    accuracy: null,
    damageClass: "status",
    effect: "Heals the user by half its max HP.",
    effectChance: null,
    learnedBy: [64, 65, 120, 121, 137, 150],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "heal",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 50,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Recover",
    power: null,
    pp: 10,
    priority: 0,
    type: "normal",
  },
  harden: {
    id: "harden",
    accuracy: null,
    damageClass: "status",
    effect: "Raises the user's Defense by one stage.",
    effectChance: null,
    learnedBy: [
      11, 12, 14, 15, 74, 75, 76, 88, 89, 95, 98, 99, 120, 121, 127, 140, 141,
      143,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "net-good-stats",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Harden",
    power: null,
    pp: 30,
    priority: 0,
    type: "normal",
  },
  minimize: {
    id: "minimize",
    accuracy: null,
    damageClass: "status",
    effect: "Raises the user's evasion by two stages.",
    effectChance: null,
    learnedBy: [35, 36, 40, 88, 89, 113, 120, 121],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "net-good-stats",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Minimize",
    power: null,
    pp: 10,
    priority: 0,
    type: "normal",
  },
  smokescreen: {
    id: "smokescreen",
    accuracy: 100,
    damageClass: "status",
    effect: "Lowers the target's accuracy by one stage.",
    effectChance: null,
    learnedBy: [4, 5, 6, 109, 110, 116, 117, 126],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "net-good-stats",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Smokescreen",
    power: null,
    pp: 20,
    priority: 0,
    type: "normal",
  },
  "confuse-ray": {
    id: "confuse-ray",
    accuracy: 100,
    damageClass: "status",
    effect: "Confuses the target.",
    effectChance: null,
    learnedBy: [
      37, 38, 41, 42, 48, 49, 54, 55, 72, 81, 82, 88, 89, 92, 93, 94, 97, 120,
      121, 122, 126, 131, 140, 141, 150, 151,
    ],
    meta: {
      ailment: "confusion",
      ailmentChance: 0,
      category: "ailment",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: 5,
      minHits: null,
      minTurns: 2,
      statChance: 0,
    },
    name: "Confuse Ray",
    power: null,
    pp: 10,
    priority: 0,
    type: "ghost",
  },
  withdraw: {
    id: "withdraw",
    accuracy: null,
    damageClass: "status",
    effect: "Raises the user's Defense by one stage.",
    effectChance: null,
    learnedBy: [7, 8, 9, 80, 90, 91, 138, 139],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "net-good-stats",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Withdraw",
    power: null,
    pp: 40,
    priority: 0,
    type: "water",
  },
  "defense-curl": {
    id: "defense-curl",
    accuracy: null,
    damageClass: "status",
    effect: "Raises user's Defense by one stage.",
    effectChance: null,
    learnedBy: [
      1, 2, 3, 4, 5, 6, 7, 8, 9, 19, 20, 25, 26, 27, 28, 29, 30, 31, 32, 33, 34,
      35, 36, 39, 40, 52, 53, 56, 57, 60, 61, 62, 74, 75, 76, 95, 108, 113, 143,
      151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "net-good-stats",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Defense Curl",
    power: null,
    pp: 40,
    priority: 0,
    type: "normal",
  },
  barrier: {
    id: "barrier",
    accuracy: null,
    damageClass: "status",
    effect: "Raises the user's Defense by two stages.",
    effectChance: null,
    learnedBy: [63, 65, 72, 73, 90, 91, 96, 97, 120, 122, 137, 150, 151],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "net-good-stats",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Barrier",
    power: null,
    pp: 20,
    priority: 0,
    type: "psychic",
  },
  "light-screen": {
    id: "light-screen",
    accuracy: null,
    damageClass: "status",
    effect: "Reduces damage from special attacks by 50% for five turns.",
    effectChance: null,
    learnedBy: [
      1, 2, 3, 25, 26, 35, 36, 39, 40, 46, 47, 54, 55, 63, 64, 65, 66, 67, 68,
      79, 80, 81, 82, 91, 96, 97, 100, 101, 102, 103, 113, 120, 121, 122, 123,
      124, 125, 135, 144, 145, 147, 148, 149, 150, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "field-effect",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Light Screen",
    power: null,
    pp: 30,
    priority: 0,
    type: "psychic",
  },
  haze: {
    id: "haze",
    accuracy: null,
    damageClass: "status",
    effect: "Resets all Pokémon's stats, accuracy, and evasion.",
    effectChance: null,
    learnedBy: [
      7, 23, 24, 41, 42, 60, 62, 72, 73, 84, 88, 89, 92, 94, 98, 109, 110, 118,
      134, 138, 144, 147,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "whole-field-effect",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Haze",
    power: null,
    pp: 30,
    priority: 0,
    type: "ice",
  },
  reflect: {
    id: "reflect",
    accuracy: null,
    damageClass: "status",
    effect: "Reduces damage from physical attacks by half.",
    effectChance: null,
    learnedBy: [
      1, 2, 3, 4, 5, 6, 7, 8, 9, 12, 15, 16, 17, 18, 25, 26, 29, 30, 31, 32, 33,
      34, 35, 36, 37, 38, 39, 40, 43, 44, 45, 46, 47, 48, 49, 58, 59, 63, 64,
      65, 69, 70, 71, 72, 73, 77, 78, 79, 80, 81, 82, 83, 84, 85, 90, 91, 96,
      97, 100, 101, 102, 103, 113, 114, 120, 121, 122, 124, 125, 130, 131, 133,
      134, 135, 136, 137, 138, 139, 140, 141, 142, 143, 144, 145, 146, 147, 148,
      149, 150, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "field-effect",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Reflect",
    power: null,
    pp: 20,
    priority: 0,
    type: "psychic",
  },
  "focus-energy": {
    id: "focus-energy",
    accuracy: null,
    damageClass: "status",
    effect: "Increases the user's chance to score a critical hit.",
    effectChance: null,
    learnedBy: [
      15, 19, 20, 21, 22, 29, 30, 31, 32, 33, 34, 56, 57, 66, 67, 68, 83, 104,
      105, 106, 107, 115, 116, 117, 123, 126, 127, 128, 133, 134, 135, 136, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "unique",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Focus Energy",
    power: null,
    pp: 30,
    priority: 0,
    type: "normal",
  },
  bide: {
    id: "bide",
    accuracy: null,
    damageClass: "physical",
    effect:
      "User waits for two turns, then hits back for twice the damage it took.",
    effectChance: null,
    learnedBy: [
      1, 2, 3, 4, 5, 6, 7, 8, 9, 12, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25,
      26, 27, 28, 29, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 40, 41, 42, 43,
      44, 45, 46, 47, 48, 49, 50, 51, 52, 53, 54, 55, 56, 57, 58, 59, 60, 61,
      62, 63, 64, 65, 66, 67, 68, 69, 70, 71, 72, 73, 74, 75, 76, 77, 78, 79,
      80, 81, 82, 83, 84, 85, 86, 87, 88, 89, 90, 91, 92, 93, 94, 95, 96, 97,
      98, 99, 100, 101, 102, 103, 104, 105, 106, 107, 108, 109, 110, 111, 112,
      113, 114, 115, 116, 117, 118, 119, 120, 121, 122, 123, 124, 125, 126, 127,
      128, 130, 131, 133, 134, 135, 136, 137, 138, 139, 140, 141, 142, 143, 144,
      145, 146, 147, 148, 149, 150, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Bide",
    power: null,
    pp: 10,
    priority: 1,
    type: "normal",
  },
  metronome: {
    id: "metronome",
    accuracy: null,
    damageClass: "status",
    effect: "Randomly selects and uses any move in the game.",
    effectChance: null,
    learnedBy: [
      35, 36, 39, 40, 54, 55, 56, 57, 61, 62, 63, 64, 65, 66, 67, 68, 74, 75,
      76, 80, 88, 89, 93, 94, 96, 97, 106, 107, 113, 122, 124, 125, 126, 143,
      149, 150, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "unique",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Metronome",
    power: null,
    pp: 10,
    priority: 0,
    type: "normal",
  },
  "mirror-move": {
    id: "mirror-move",
    accuracy: null,
    damageClass: "status",
    effect: "Uses the target's last used move.",
    effectChance: null,
    learnedBy: [16, 17, 18, 21, 22, 83, 84, 85],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "unique",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Mirror Move",
    power: null,
    pp: 20,
    priority: 0,
    type: "flying",
  },
  "self-destruct": {
    id: "self-destruct",
    accuracy: 100,
    damageClass: "physical",
    effect: "User faints.",
    effectChance: null,
    learnedBy: [
      39, 40, 74, 75, 76, 88, 89, 90, 91, 92, 93, 94, 95, 100, 101, 102, 103,
      109, 110, 143, 150, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Self-Destruct",
    power: 200,
    pp: 5,
    priority: 0,
    type: "normal",
  },
  "egg-bomb": {
    id: "egg-bomb",
    accuracy: 75,
    damageClass: "physical",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [102, 103, 113, 151],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Egg Bomb",
    power: 100,
    pp: 10,
    priority: 0,
    type: "normal",
  },
  lick: {
    id: "lick",
    accuracy: 100,
    damageClass: "physical",
    effect: "Has a $effect_chance% chance to paralyze the target.",
    effectChance: 30,
    learnedBy: [86, 88, 92, 93, 94, 108, 124, 143],
    meta: {
      ailment: "paralysis",
      ailmentChance: 30,
      category: "damage+ailment",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Lick",
    power: 30,
    pp: 30,
    priority: 0,
    type: "ghost",
  },
  smog: {
    id: "smog",
    accuracy: 70,
    damageClass: "special",
    effect: "Has a $effect_chance% chance to poison the target.",
    effectChance: 40,
    learnedBy: [88, 89, 92, 93, 94, 109, 110, 126, 136],
    meta: {
      ailment: "poison",
      ailmentChance: 40,
      category: "damage+ailment",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Smog",
    power: 30,
    pp: 20,
    priority: 0,
    type: "poison",
  },
  sludge: {
    id: "sludge",
    accuracy: 100,
    damageClass: "special",
    effect: "Has a $effect_chance% chance to poison the target.",
    effectChance: 30,
    learnedBy: [1, 88, 89, 109, 110],
    meta: {
      ailment: "poison",
      ailmentChance: 30,
      category: "damage+ailment",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Sludge",
    power: 65,
    pp: 20,
    priority: 0,
    type: "poison",
  },
  "bone-club": {
    id: "bone-club",
    accuracy: 85,
    damageClass: "physical",
    effect: "Has a $effect_chance% chance to make the target flinch.",
    effectChance: 10,
    learnedBy: [104, 105],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 10,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Bone Club",
    power: 65,
    pp: 20,
    priority: 0,
    type: "ground",
  },
  "fire-blast": {
    id: "fire-blast",
    accuracy: 85,
    damageClass: "special",
    effect: "Has a $effect_chance% chance to burn the target.",
    effectChance: 10,
    learnedBy: [
      4, 5, 6, 31, 34, 35, 36, 37, 38, 39, 40, 58, 59, 66, 67, 68, 74, 75, 76,
      77, 78, 79, 80, 88, 89, 104, 105, 108, 109, 110, 111, 112, 113, 115, 126,
      128, 130, 136, 142, 143, 146, 147, 148, 149, 150, 151,
    ],
    meta: {
      ailment: "burn",
      ailmentChance: 10,
      category: "damage+ailment",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Fire Blast",
    power: 110,
    pp: 5,
    priority: 0,
    type: "fire",
  },
  waterfall: {
    id: "waterfall",
    accuracy: 100,
    damageClass: "physical",
    effect: "Has a $effect_chance% chance to make the target flinch.",
    effectChance: 20,
    learnedBy: [
      7, 8, 9, 54, 55, 60, 61, 62, 72, 73, 79, 80, 86, 87, 90, 91, 116, 117,
      118, 119, 120, 121, 130, 131, 134, 138, 139, 140, 141, 147, 148, 149, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 20,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Waterfall",
    power: 80,
    pp: 15,
    priority: 0,
    type: "water",
  },
  clamp: {
    id: "clamp",
    accuracy: 85,
    damageClass: "physical",
    effect:
      "Prevents the target from fleeing and inflicts damage for 2-5 turns.",
    effectChance: 100,
    learnedBy: [90, 91],
    meta: {
      ailment: "trap",
      ailmentChance: 100,
      category: "damage+ailment",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: 6,
      minHits: null,
      minTurns: 5,
      statChance: 0,
    },
    name: "Clamp",
    power: 35,
    pp: 15,
    priority: 0,
    type: "water",
  },
  swift: {
    id: "swift",
    accuracy: null,
    damageClass: "special",
    effect: "Never misses.",
    effectChance: null,
    learnedBy: [
      4, 5, 6, 12, 15, 16, 17, 18, 19, 20, 21, 22, 25, 26, 27, 28, 37, 38, 39,
      40, 41, 42, 48, 49, 52, 53, 54, 55, 56, 57, 58, 59, 63, 64, 65, 77, 78,
      79, 80, 81, 82, 83, 84, 85, 89, 90, 91, 96, 97, 100, 101, 104, 105, 106,
      107, 113, 116, 117, 118, 119, 120, 121, 123, 125, 133, 134, 135, 136, 137,
      142, 144, 145, 146, 147, 148, 149, 150, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Swift",
    power: 60,
    pp: 20,
    priority: 0,
    type: "normal",
  },
  "skull-bash": {
    id: "skull-bash",
    accuracy: 100,
    damageClass: "physical",
    effect:
      "Raises the user's Defense by one stage.  User charges for one turn before attacking.",
    effectChance: 100,
    learnedBy: [
      1, 4, 5, 6, 7, 8, 9, 15, 19, 20, 23, 24, 25, 26, 27, 28, 29, 30, 31, 32,
      33, 34, 35, 36, 37, 38, 39, 40, 46, 47, 52, 53, 54, 55, 56, 57, 58, 59,
      60, 61, 62, 63, 64, 65, 66, 67, 68, 72, 73, 77, 78, 79, 80, 83, 84, 85,
      86, 87, 94, 95, 96, 97, 101, 104, 105, 106, 107, 108, 111, 112, 113, 114,
      115, 116, 117, 118, 119, 120, 121, 122, 123, 124, 125, 126, 128, 130, 131,
      133, 134, 135, 136, 137, 139, 141, 143, 147, 148, 149, 150, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 100,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Skull Bash",
    power: 130,
    pp: 10,
    priority: 0,
    type: "normal",
  },
  "spike-cannon": {
    id: "spike-cannon",
    accuracy: 100,
    damageClass: "physical",
    effect: "Hits 2-5 times in one turn.",
    effectChance: null,
    learnedBy: [91, 138, 139],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: 5,
      maxTurns: null,
      minHits: 2,
      minTurns: null,
      statChance: 0,
    },
    name: "Spike Cannon",
    power: 20,
    pp: 15,
    priority: 0,
    type: "normal",
  },
  constrict: {
    id: "constrict",
    accuracy: 100,
    damageClass: "physical",
    effect:
      "Has a $effect_chance% chance to lower the target's Speed by one stage.",
    effectChance: 10,
    learnedBy: [72, 73, 114, 138, 139],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage+lower",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 10,
    },
    name: "Constrict",
    power: 10,
    pp: 35,
    priority: 0,
    type: "normal",
  },
  amnesia: {
    id: "amnesia",
    accuracy: null,
    damageClass: "status",
    effect: "Raises the user's Special Defense by two stages.",
    effectChance: null,
    learnedBy: [
      1, 2, 3, 27, 28, 32, 33, 34, 35, 36, 39, 40, 52, 53, 54, 55, 60, 61, 79,
      80, 98, 99, 108, 114, 143, 150, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "net-good-stats",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Amnesia",
    power: null,
    pp: 20,
    priority: 0,
    type: "psychic",
  },
  kinesis: {
    id: "kinesis",
    accuracy: 80,
    damageClass: "status",
    effect: "Lowers the target's accuracy by one stage.",
    effectChance: null,
    learnedBy: [64, 65],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "net-good-stats",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Kinesis",
    power: null,
    pp: 15,
    priority: 0,
    type: "psychic",
  },
  "soft-boiled": {
    id: "soft-boiled",
    accuracy: null,
    damageClass: "status",
    effect: "Heals the user by half its max HP.",
    effectChance: null,
    learnedBy: [35, 36, 113, 151],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "heal",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 50,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Soft-Boiled",
    power: null,
    pp: 10,
    priority: 0,
    type: "normal",
  },
  "high-jump-kick": {
    id: "high-jump-kick",
    accuracy: 90,
    damageClass: "physical",
    effect:
      "If the user misses, it takes half the damage it would have inflicted in recoil.",
    effectChance: null,
    learnedBy: [106],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "High Jump Kick",
    power: 130,
    pp: 10,
    priority: 0,
    type: "fighting",
  },
  glare: {
    id: "glare",
    accuracy: 100,
    damageClass: "status",
    effect: "Paralyzes the target.",
    effectChance: null,
    learnedBy: [23, 24],
    meta: {
      ailment: "paralysis",
      ailmentChance: 0,
      category: "ailment",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Glare",
    power: null,
    pp: 30,
    priority: 0,
    type: "normal",
  },
  "dream-eater": {
    id: "dream-eater",
    accuracy: 100,
    damageClass: "special",
    effect:
      "Only works on sleeping Pokémon.  Drains half the damage inflicted to heal the user.",
    effectChance: null,
    learnedBy: [
      12, 35, 36, 38, 39, 40, 49, 52, 53, 63, 64, 65, 79, 80, 92, 93, 94, 96,
      97, 102, 103, 108, 113, 121, 122, 124, 131, 137, 150, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage+heal",
      critRate: 0,
      drain: 50,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Dream Eater",
    power: 100,
    pp: 15,
    priority: 0,
    type: "psychic",
  },
  "poison-gas": {
    id: "poison-gas",
    accuracy: 90,
    damageClass: "status",
    effect: "Poisons the target.",
    effectChance: null,
    learnedBy: [88, 89, 92, 93, 94, 96, 97, 109, 110],
    meta: {
      ailment: "poison",
      ailmentChance: 0,
      category: "ailment",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Poison Gas",
    power: null,
    pp: 40,
    priority: 0,
    type: "poison",
  },
  barrage: {
    id: "barrage",
    accuracy: 85,
    damageClass: "physical",
    effect: "Hits 2-5 times in one turn.",
    effectChance: null,
    learnedBy: [102, 103],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: 5,
      maxTurns: null,
      minHits: 2,
      minTurns: null,
      statChance: 0,
    },
    name: "Barrage",
    power: 15,
    pp: 20,
    priority: 0,
    type: "normal",
  },
  "leech-life": {
    id: "leech-life",
    accuracy: 100,
    damageClass: "physical",
    effect: "Drains half the damage inflicted to heal the user.",
    effectChance: null,
    learnedBy: [27, 28, 41, 42, 46, 47, 48, 49, 69, 71, 140, 141, 151],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage+heal",
      critRate: 0,
      drain: 50,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Leech Life",
    power: 80,
    pp: 10,
    priority: 0,
    type: "bug",
  },
  "lovely-kiss": {
    id: "lovely-kiss",
    accuracy: 75,
    damageClass: "status",
    effect: "Puts the target to sleep.",
    effectChance: null,
    learnedBy: [124],
    meta: {
      ailment: "sleep",
      ailmentChance: 0,
      category: "ailment",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: 4,
      minHits: null,
      minTurns: 2,
      statChance: 0,
    },
    name: "Lovely Kiss",
    power: null,
    pp: 10,
    priority: 0,
    type: "normal",
  },
  "sky-attack": {
    id: "sky-attack",
    accuracy: 90,
    damageClass: "physical",
    effect:
      "User charges for one turn before attacking.  Has a $effect_chance% chance to make the target flinch.",
    effectChance: 30,
    learnedBy: [16, 17, 18, 21, 22, 83, 84, 85, 142, 144, 145, 146, 151],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 1,
      drain: 0,
      flinchChance: 30,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Sky Attack",
    power: 140,
    pp: 5,
    priority: 0,
    type: "flying",
  },
  transform: {
    id: "transform",
    accuracy: null,
    damageClass: "status",
    effect: "User becomes a copy of the target until it leaves battle.",
    effectChance: null,
    learnedBy: [132, 151],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "unique",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Transform",
    power: null,
    pp: 10,
    priority: 0,
    type: "normal",
  },
  bubble: {
    id: "bubble",
    accuracy: 100,
    damageClass: "special",
    effect:
      "Has a $effect_chance% chance to lower the target's Speed by one stage.",
    effectChance: 10,
    learnedBy: [7, 8, 9, 60, 61, 62, 72, 98, 99, 116, 117],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage+lower",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 10,
    },
    name: "Bubble",
    power: 40,
    pp: 30,
    priority: 0,
    type: "water",
  },
  "dizzy-punch": {
    id: "dizzy-punch",
    accuracy: 100,
    damageClass: "physical",
    effect: "Has a $effect_chance% chance to confuse the target.",
    effectChance: 20,
    learnedBy: [107, 115],
    meta: {
      ailment: "confusion",
      ailmentChance: 20,
      category: "damage+ailment",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: 5,
      minHits: null,
      minTurns: 2,
      statChance: 0,
    },
    name: "Dizzy Punch",
    power: 70,
    pp: 10,
    priority: 0,
    type: "normal",
  },
  spore: {
    id: "spore",
    accuracy: 100,
    damageClass: "status",
    effect: "Puts the target to sleep.",
    effectChance: null,
    learnedBy: [46, 47],
    meta: {
      ailment: "sleep",
      ailmentChance: 0,
      category: "ailment",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: 4,
      minHits: null,
      minTurns: 2,
      statChance: 0,
    },
    name: "Spore",
    power: null,
    pp: 15,
    priority: 0,
    type: "grass",
  },
  flash: {
    id: "flash",
    accuracy: 100,
    damageClass: "status",
    effect: "Lowers the target's accuracy by one stage.",
    effectChance: null,
    learnedBy: [
      1, 2, 3, 12, 15, 25, 26, 35, 36, 39, 40, 43, 44, 45, 46, 47, 48, 49, 52,
      53, 54, 55, 63, 64, 65, 69, 70, 71, 79, 80, 81, 82, 96, 97, 100, 101, 102,
      103, 109, 110, 113, 114, 120, 121, 122, 124, 125, 135, 137, 145, 150, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "net-good-stats",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Flash",
    power: null,
    pp: 20,
    priority: 0,
    type: "normal",
  },
  psywave: {
    id: "psywave",
    accuracy: 100,
    damageClass: "special",
    effect: "Inflicts damage between 50% and 150% of the user's level.",
    effectChance: null,
    learnedBy: [
      12, 35, 36, 39, 40, 48, 49, 60, 61, 62, 63, 64, 65, 79, 80, 92, 93, 94,
      96, 97, 102, 103, 109, 113, 120, 121, 122, 124, 125, 126, 131, 137, 143,
      150, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Psywave",
    power: null,
    pp: 15,
    priority: 0,
    type: "psychic",
  },
  splash: {
    id: "splash",
    accuracy: null,
    damageClass: "status",
    effect: "Does nothing.",
    effectChance: null,
    learnedBy: [35, 36, 60, 116, 129, 130],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "unique",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Splash",
    power: null,
    pp: 40,
    priority: 0,
    type: "normal",
  },
  "acid-armor": {
    id: "acid-armor",
    accuracy: null,
    damageClass: "status",
    effect: "Raises the user's Defense by two stages.",
    effectChance: null,
    learnedBy: [72, 73, 88, 89, 134],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "net-good-stats",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Acid Armor",
    power: null,
    pp: 20,
    priority: 0,
    type: "poison",
  },
  crabhammer: {
    id: "crabhammer",
    accuracy: 90,
    damageClass: "physical",
    effect: "Has an increased chance for a critical hit.",
    effectChance: null,
    learnedBy: [98, 99],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 1,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Crabhammer",
    power: 100,
    pp: 10,
    priority: 0,
    type: "water",
  },
  explosion: {
    id: "explosion",
    accuracy: 100,
    damageClass: "physical",
    effect: "User faints.",
    effectChance: null,
    learnedBy: [
      74, 75, 76, 81, 82, 88, 89, 90, 91, 92, 93, 94, 95, 100, 101, 102, 103,
      109, 110, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Explosion",
    power: 250,
    pp: 5,
    priority: 0,
    type: "normal",
  },
  "fury-swipes": {
    id: "fury-swipes",
    accuracy: 80,
    damageClass: "physical",
    effect: "Hits 2-5 times in one turn.",
    effectChance: null,
    learnedBy: [
      4, 5, 6, 19, 20, 27, 28, 29, 30, 31, 46, 47, 50, 51, 52, 53, 54, 55, 56,
      57,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: 5,
      maxTurns: null,
      minHits: 2,
      minTurns: null,
      statChance: 0,
    },
    name: "Fury Swipes",
    power: 18,
    pp: 15,
    priority: 0,
    type: "normal",
  },
  bonemerang: {
    id: "bonemerang",
    accuracy: 90,
    damageClass: "physical",
    effect: "Hits twice in one turn.",
    effectChance: null,
    learnedBy: [104, 105],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: 2,
      maxTurns: null,
      minHits: 2,
      minTurns: null,
      statChance: 0,
    },
    name: "Bonemerang",
    power: 50,
    pp: 10,
    priority: 0,
    type: "ground",
  },
  rest: {
    id: "rest",
    accuracy: null,
    damageClass: "status",
    effect: "User sleeps for two turns, completely healing itself.",
    effectChance: null,
    learnedBy: [
      1, 2, 3, 4, 5, 6, 7, 8, 9, 12, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25,
      26, 27, 28, 29, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 40, 41, 42, 43,
      44, 45, 46, 47, 48, 49, 50, 51, 52, 53, 54, 55, 56, 57, 58, 59, 60, 61,
      62, 63, 64, 65, 66, 67, 68, 69, 70, 71, 72, 73, 74, 75, 76, 77, 78, 79,
      80, 81, 82, 83, 84, 85, 86, 87, 88, 89, 90, 91, 92, 93, 94, 95, 96, 97,
      98, 99, 100, 101, 102, 103, 104, 105, 106, 107, 108, 109, 110, 111, 112,
      113, 114, 115, 116, 117, 118, 119, 120, 121, 122, 123, 124, 125, 126, 127,
      128, 130, 131, 133, 134, 135, 136, 137, 138, 139, 140, 141, 142, 143, 144,
      145, 146, 147, 148, 149, 150, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "unique",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Rest",
    power: null,
    pp: 10,
    priority: 0,
    type: "psychic",
  },
  "rock-slide": {
    id: "rock-slide",
    accuracy: 90,
    damageClass: "physical",
    effect: "Has a $effect_chance% chance to make the target flinch.",
    effectChance: 30,
    learnedBy: [
      4, 5, 6, 7, 8, 9, 23, 24, 27, 28, 31, 34, 50, 51, 56, 57, 62, 66, 67, 68,
      74, 75, 76, 88, 89, 95, 98, 99, 104, 105, 106, 107, 108, 111, 112, 113,
      115, 127, 128, 138, 139, 140, 141, 142, 143, 149, 150, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 30,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Rock Slide",
    power: 75,
    pp: 10,
    priority: 0,
    type: "rock",
  },
  "hyper-fang": {
    id: "hyper-fang",
    accuracy: 90,
    damageClass: "physical",
    effect: "Has a $effect_chance% chance to make the target flinch.",
    effectChance: 10,
    learnedBy: [19, 20],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 10,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Hyper Fang",
    power: 80,
    pp: 15,
    priority: 0,
    type: "normal",
  },
  sharpen: {
    id: "sharpen",
    accuracy: null,
    damageClass: "status",
    effect: "Raises the user's Attack by one stage.",
    effectChance: null,
    learnedBy: [137],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "net-good-stats",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Sharpen",
    power: null,
    pp: 30,
    priority: 0,
    type: "normal",
  },
  conversion: {
    id: "conversion",
    accuracy: null,
    damageClass: "status",
    effect: "User's type changes to the type of one of its moves at random.",
    effectChance: null,
    learnedBy: [137],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "unique",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Conversion",
    power: null,
    pp: 30,
    priority: 0,
    type: "normal",
  },
  "tri-attack": {
    id: "tri-attack",
    accuracy: 100,
    damageClass: "special",
    effect:
      "Has a $effect_chance% chance to burn, freeze, or paralyze the target.",
    effectChance: 20,
    learnedBy: [
      21, 22, 35, 36, 39, 40, 51, 63, 64, 65, 79, 80, 82, 84, 85, 90, 91, 96,
      97, 113, 120, 121, 137, 150, 151,
    ],
    meta: {
      ailment: "unknown",
      ailmentChance: 20,
      category: "damage+ailment",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Tri Attack",
    power: 80,
    pp: 10,
    priority: 0,
    type: "normal",
  },
  "super-fang": {
    id: "super-fang",
    accuracy: 90,
    damageClass: "physical",
    effect: "Inflicts damage equal to half the target's HP.",
    effectChance: null,
    learnedBy: [19, 20, 27, 28, 29, 30, 31, 32, 33, 34, 41, 42, 151],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Super Fang",
    power: null,
    pp: 10,
    priority: 0,
    type: "normal",
  },
  slash: {
    id: "slash",
    accuracy: 100,
    damageClass: "physical",
    effect: "Has an increased chance for a critical hit.",
    effectChance: null,
    learnedBy: [
      4, 5, 6, 27, 28, 46, 47, 50, 51, 52, 53, 83, 98, 123, 127, 140, 141,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 1,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Slash",
    power: 70,
    pp: 20,
    priority: 0,
    type: "normal",
  },
  substitute: {
    id: "substitute",
    accuracy: null,
    damageClass: "status",
    effect:
      "Transfers 1/4 of the user's max HP into a doll, protecting the user from further damage or status changes until it breaks.",
    effectChance: null,
    learnedBy: [
      1, 2, 3, 4, 5, 6, 7, 8, 9, 12, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25,
      26, 27, 28, 29, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 40, 41, 42, 43,
      44, 45, 46, 47, 48, 49, 50, 51, 52, 53, 54, 55, 56, 57, 58, 59, 60, 61,
      62, 63, 64, 65, 66, 67, 68, 69, 70, 71, 72, 73, 74, 75, 76, 77, 78, 79,
      80, 81, 82, 83, 84, 85, 86, 87, 88, 89, 90, 91, 92, 93, 94, 95, 96, 97,
      98, 99, 100, 101, 102, 103, 104, 105, 106, 107, 108, 109, 110, 111, 112,
      113, 114, 115, 116, 117, 118, 119, 120, 121, 122, 123, 124, 125, 126, 127,
      128, 130, 131, 133, 134, 135, 136, 137, 138, 139, 140, 141, 142, 143, 144,
      145, 146, 147, 148, 149, 150, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "unique",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Substitute",
    power: null,
    pp: 10,
    priority: 0,
    type: "normal",
  },
  struggle: {
    id: "struggle",
    accuracy: null,
    damageClass: "physical",
    effect: "User takes 1/4 its max HP in recoil.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: -25,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Struggle",
    power: 50,
    pp: 1,
    priority: 0,
    type: "normal",
  },
  sketch: {
    id: "sketch",
    accuracy: null,
    damageClass: "status",
    effect: "Permanently becomes the target's last used move.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "unique",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Sketch",
    power: null,
    pp: 1,
    priority: 0,
    type: "normal",
  },
  "triple-kick": {
    id: "triple-kick",
    accuracy: 90,
    damageClass: "physical",
    effect:
      "Hits three times, increasing power by 100% with each successful hit.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: 3,
      maxTurns: null,
      minHits: 3,
      minTurns: null,
      statChance: 0,
    },
    name: "Triple Kick",
    power: 10,
    pp: 10,
    priority: 0,
    type: "fighting",
  },
  thief: {
    id: "thief",
    accuracy: 100,
    damageClass: "physical",
    effect: "Takes the target's item.",
    effectChance: null,
    learnedBy: [
      12, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30, 31,
      32, 33, 34, 39, 40, 41, 42, 46, 47, 48, 49, 50, 51, 52, 53, 54, 55, 56,
      57, 58, 59, 60, 61, 62, 63, 64, 65, 66, 67, 68, 69, 70, 71, 72, 73, 83,
      84, 85, 86, 87, 88, 89, 92, 93, 94, 96, 97, 98, 99, 100, 101, 102, 103,
      104, 105, 106, 107, 108, 109, 110, 111, 112, 113, 114, 115, 122, 123, 124,
      125, 126, 127, 128, 137, 138, 139, 140, 141, 142, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Thief",
    power: 60,
    pp: 25,
    priority: 0,
    type: "dark",
  },
  "spider-web": {
    id: "spider-web",
    accuracy: null,
    damageClass: "status",
    effect: "Prevents the target from leaving battle.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "unique",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Spider Web",
    power: null,
    pp: 10,
    priority: 0,
    type: "bug",
  },
  "mind-reader": {
    id: "mind-reader",
    accuracy: null,
    damageClass: "status",
    effect: "Ensures that the user's next move will hit the target.",
    effectChance: null,
    learnedBy: [60, 62, 96, 97, 106, 144],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "unique",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Mind Reader",
    power: null,
    pp: 5,
    priority: 0,
    type: "normal",
  },
  nightmare: {
    id: "nightmare",
    accuracy: 100,
    damageClass: "status",
    effect: "Target loses 1/4 its max HP every turn as long as it's asleep.",
    effectChance: null,
    learnedBy: [
      12, 35, 36, 39, 40, 52, 53, 63, 64, 65, 79, 80, 92, 93, 94, 96, 97, 102,
      103, 108, 121, 122, 124, 131, 137, 150, 151,
    ],
    meta: {
      ailment: "nightmare",
      ailmentChance: 0,
      category: "ailment",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Nightmare",
    power: null,
    pp: 15,
    priority: 0,
    type: "ghost",
  },
  "flame-wheel": {
    id: "flame-wheel",
    accuracy: 100,
    damageClass: "physical",
    effect:
      "Has a $effect_chance% chance to burn the target.  Lets frozen Pokémon thaw themselves.",
    effectChance: 10,
    learnedBy: [19, 58, 59, 77, 78, 126],
    meta: {
      ailment: "burn",
      ailmentChance: 10,
      category: "damage+ailment",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Flame Wheel",
    power: 60,
    pp: 25,
    priority: 0,
    type: "fire",
  },
  snore: {
    id: "snore",
    accuracy: 100,
    damageClass: "special",
    effect:
      "Has a $effect_chance% chance to make the target flinch.  Only works if the user is sleeping.",
    effectChance: 30,
    learnedBy: [
      1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 12, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24,
      25, 26, 27, 28, 29, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 40, 41, 42,
      43, 44, 45, 46, 47, 48, 49, 50, 51, 52, 53, 54, 55, 56, 57, 58, 59, 60,
      61, 62, 63, 64, 65, 66, 67, 68, 69, 70, 71, 72, 73, 74, 75, 76, 77, 78,
      79, 80, 81, 82, 83, 84, 85, 86, 87, 88, 89, 90, 91, 92, 93, 94, 95, 96,
      97, 98, 99, 100, 101, 102, 103, 104, 105, 106, 107, 108, 109, 110, 111,
      112, 113, 114, 115, 116, 117, 118, 119, 120, 121, 122, 123, 124, 125, 126,
      127, 128, 130, 131, 133, 134, 135, 136, 137, 138, 139, 140, 141, 142, 143,
      144, 145, 146, 147, 148, 149, 150, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 30,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Snore",
    power: 50,
    pp: 15,
    priority: 0,
    type: "normal",
  },
  curse: {
    id: "curse",
    accuracy: null,
    damageClass: "status",
    effect:
      "Ghosts pay half their max HP to hurt the target every turn.  Others decrease Speed but raise Attack and Defense.",
    effectChance: null,
    learnedBy: [
      1, 2, 3, 4, 5, 6, 7, 8, 9, 12, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25,
      26, 27, 28, 29, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 40, 41, 42, 43,
      44, 45, 46, 47, 48, 49, 50, 51, 52, 53, 54, 55, 56, 57, 58, 59, 60, 61,
      62, 63, 64, 65, 66, 67, 68, 69, 70, 71, 72, 73, 74, 75, 76, 77, 78, 79,
      80, 81, 82, 83, 84, 85, 86, 87, 88, 89, 90, 91, 92, 93, 94, 95, 96, 97,
      98, 99, 100, 101, 102, 103, 104, 105, 106, 107, 108, 109, 110, 111, 112,
      113, 114, 115, 116, 117, 118, 119, 120, 121, 122, 123, 124, 125, 126, 127,
      128, 130, 131, 133, 134, 135, 136, 137, 138, 139, 140, 141, 142, 143, 144,
      145, 146, 147, 148, 149, 150, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "unique",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Curse",
    power: null,
    pp: 10,
    priority: 0,
    type: "ghost",
  },
  flail: {
    id: "flail",
    accuracy: 100,
    damageClass: "physical",
    effect:
      "Inflicts more damage when the user has less HP remaining, with a maximum of 200 power.",
    effectChance: null,
    learnedBy: [
      7, 27, 37, 43, 46, 52, 74, 83, 84, 95, 98, 99, 114, 116, 118, 119, 127,
      129, 130, 133, 140, 143,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Flail",
    power: null,
    pp: 15,
    priority: 0,
    type: "normal",
  },
  "conversion-2": {
    id: "conversion-2",
    accuracy: null,
    damageClass: "status",
    effect:
      "Changes the user's type to a random type either resistant or immune to the last move used against it.",
    effectChance: null,
    learnedBy: [137],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "unique",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Conversion 2",
    power: null,
    pp: 30,
    priority: 0,
    type: "normal",
  },
  aeroblast: {
    id: "aeroblast",
    accuracy: 95,
    damageClass: "special",
    effect: "Has an increased chance for a critical hit.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 1,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Aeroblast",
    power: 100,
    pp: 5,
    priority: 0,
    type: "flying",
  },
  "cotton-spore": {
    id: "cotton-spore",
    accuracy: 100,
    damageClass: "status",
    effect: "Lowers the target's Speed by two stages.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "net-good-stats",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Cotton Spore",
    power: null,
    pp: 40,
    priority: 0,
    type: "grass",
  },
  reversal: {
    id: "reversal",
    accuracy: 100,
    damageClass: "physical",
    effect:
      "Inflicts more damage when the user has less HP remaining, with a maximum of 200 power.",
    effectChance: null,
    learnedBy: [
      19, 25, 26, 50, 51, 56, 57, 58, 59, 62, 66, 67, 68, 106, 111, 112, 115,
      123, 127, 128, 150, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Reversal",
    power: null,
    pp: 15,
    priority: 0,
    type: "fighting",
  },
  spite: {
    id: "spite",
    accuracy: 100,
    damageClass: "status",
    effect: "Lowers the PP of the target's last used move by 4.",
    effectChance: null,
    learnedBy: [
      23, 24, 37, 38, 52, 53, 56, 57, 92, 93, 94, 109, 110, 111, 112, 115, 128,
      130, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "unique",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Spite",
    power: null,
    pp: 10,
    priority: 0,
    type: "ghost",
  },
  "powder-snow": {
    id: "powder-snow",
    accuracy: 100,
    damageClass: "special",
    effect: "Has a $effect_chance% chance to freeze the target.",
    effectChance: 10,
    learnedBy: [124, 144],
    meta: {
      ailment: "freeze",
      ailmentChance: 10,
      category: "damage+ailment",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Powder Snow",
    power: 40,
    pp: 25,
    priority: 0,
    type: "ice",
  },
  protect: {
    id: "protect",
    accuracy: null,
    damageClass: "status",
    effect: "Prevents any moves from hitting the user this turn.",
    effectChance: null,
    learnedBy: [
      1, 2, 3, 4, 5, 6, 7, 8, 9, 12, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25,
      26, 27, 28, 29, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 40, 41, 42, 43,
      44, 45, 46, 47, 48, 49, 50, 51, 52, 53, 54, 55, 56, 57, 58, 59, 60, 61,
      62, 63, 64, 65, 66, 67, 68, 69, 70, 71, 72, 73, 74, 75, 76, 77, 78, 79,
      80, 81, 82, 83, 84, 85, 86, 87, 88, 89, 90, 91, 92, 93, 94, 95, 96, 97,
      98, 99, 100, 101, 102, 103, 104, 105, 106, 107, 108, 109, 110, 111, 112,
      113, 114, 115, 116, 117, 118, 119, 120, 121, 122, 123, 124, 125, 126, 127,
      128, 130, 131, 133, 134, 135, 136, 137, 138, 139, 140, 141, 142, 143, 144,
      145, 146, 147, 148, 149, 150, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "unique",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Protect",
    power: null,
    pp: 10,
    priority: 4,
    type: "normal",
  },
  "mach-punch": {
    id: "mach-punch",
    accuracy: 100,
    damageClass: "physical",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [107],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Mach Punch",
    power: 40,
    pp: 30,
    priority: 1,
    type: "fighting",
  },
  "scary-face": {
    id: "scary-face",
    accuracy: 100,
    damageClass: "status",
    effect: "Lowers the target's Speed by two stages.",
    effectChance: null,
    learnedBy: [
      4, 5, 6, 20, 21, 23, 56, 57, 58, 59, 66, 67, 68, 88, 89, 91, 92, 93, 94,
      95, 97, 101, 111, 112, 123, 126, 128, 130, 136, 142, 149, 150, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "net-good-stats",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Scary Face",
    power: null,
    pp: 10,
    priority: 0,
    type: "normal",
  },
  "feint-attack": {
    id: "feint-attack",
    accuracy: null,
    damageClass: "physical",
    effect: "Never misses.",
    effectChance: null,
    learnedBy: [16, 21, 37, 41, 50, 52, 53, 84, 126, 127, 151],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Feint Attack",
    power: 60,
    pp: 20,
    priority: 0,
    type: "dark",
  },
  "sweet-kiss": {
    id: "sweet-kiss",
    accuracy: 75,
    damageClass: "status",
    effect: "Confuses the target.",
    effectChance: null,
    learnedBy: [25, 26, 35, 36, 39, 40, 113, 124],
    meta: {
      ailment: "confusion",
      ailmentChance: 0,
      category: "ailment",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: 5,
      minHits: null,
      minTurns: 2,
      statChance: 0,
    },
    name: "Sweet Kiss",
    power: null,
    pp: 10,
    priority: 0,
    type: "fairy",
  },
  "belly-drum": {
    id: "belly-drum",
    accuracy: null,
    damageClass: "status",
    effect: "User pays half its max HP to max out its Attack.",
    effectChance: null,
    learnedBy: [4, 60, 61, 62, 79, 104, 108, 143],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "unique",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Belly Drum",
    power: null,
    pp: 10,
    priority: 0,
    type: "normal",
  },
  "sludge-bomb": {
    id: "sludge-bomb",
    accuracy: 100,
    damageClass: "special",
    effect: "Has a $effect_chance% chance to poison the target.",
    effectChance: 30,
    learnedBy: [
      1, 2, 3, 15, 19, 20, 23, 24, 29, 30, 31, 32, 33, 34, 41, 42, 43, 44, 45,
      46, 47, 48, 49, 50, 51, 69, 70, 71, 72, 73, 88, 89, 92, 93, 94, 102, 103,
      109, 110, 114, 151,
    ],
    meta: {
      ailment: "poison",
      ailmentChance: 30,
      category: "damage+ailment",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Sludge Bomb",
    power: 90,
    pp: 10,
    priority: 0,
    type: "poison",
  },
  "mud-slap": {
    id: "mud-slap",
    accuracy: 100,
    damageClass: "special",
    effect:
      "Has a $effect_chance% chance to lower the target's accuracy by one stage.",
    effectChance: 100,
    learnedBy: [
      1, 2, 3, 4, 5, 6, 7, 8, 9, 16, 17, 18, 19, 20, 21, 22, 25, 26, 27, 28, 29,
      30, 31, 32, 33, 34, 35, 36, 39, 40, 50, 51, 52, 53, 54, 55, 56, 57, 58,
      59, 61, 62, 66, 67, 68, 74, 75, 76, 79, 80, 83, 84, 85, 88, 89, 95, 98,
      99, 104, 105, 106, 107, 108, 111, 112, 113, 115, 118, 119, 122, 124, 125,
      126, 133, 134, 135, 136, 140, 141, 143, 144, 145, 146, 149, 150, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage+lower",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 100,
    },
    name: "Mud-Slap",
    power: 20,
    pp: 10,
    priority: 0,
    type: "ground",
  },
  octazooka: {
    id: "octazooka",
    accuracy: 85,
    damageClass: "special",
    effect:
      "Has a $effect_chance% chance to lower the target's accuracy by one stage.",
    effectChance: 50,
    learnedBy: [116],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage+lower",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 50,
    },
    name: "Octazooka",
    power: 65,
    pp: 10,
    priority: 0,
    type: "water",
  },
  spikes: {
    id: "spikes",
    accuracy: null,
    damageClass: "status",
    effect: "Scatters Spikes, hurting opposing Pokémon that switch in.",
    effectChance: null,
    learnedBy: [28, 90, 91, 138, 139, 151],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "field-effect",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Spikes",
    power: null,
    pp: 20,
    priority: 0,
    type: "ground",
  },
  "zap-cannon": {
    id: "zap-cannon",
    accuracy: 50,
    damageClass: "special",
    effect: "Has a $effect_chance% chance to paralyze the target.",
    effectChance: 100,
    learnedBy: [
      25, 26, 35, 36, 39, 40, 52, 53, 63, 64, 65, 79, 80, 81, 82, 88, 89, 92,
      93, 94, 96, 97, 100, 101, 109, 110, 111, 112, 113, 115, 120, 121, 122,
      125, 128, 130, 131, 135, 136, 137, 143, 145, 147, 148, 149, 150, 151,
    ],
    meta: {
      ailment: "paralysis",
      ailmentChance: 100,
      category: "damage+ailment",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Zap Cannon",
    power: 120,
    pp: 5,
    priority: 0,
    type: "electric",
  },
  foresight: {
    id: "foresight",
    accuracy: null,
    damageClass: "status",
    effect:
      "Forces the target to have no Evade, and allows it to be hit by Normal and Fighting moves even if it's a Ghost.",
    effectChance: null,
    learnedBy: [7, 16, 48, 49, 54, 56, 66, 67, 68, 83, 106, 115, 131, 140, 142],
    meta: {
      ailment: "no-type-immunity",
      ailmentChance: 0,
      category: "ailment",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Foresight",
    power: null,
    pp: 40,
    priority: 0,
    type: "normal",
  },
  "destiny-bond": {
    id: "destiny-bond",
    accuracy: null,
    damageClass: "status",
    effect: "If the user faints this turn, the target automatically will, too.",
    effectChance: null,
    learnedBy: [92, 93, 94, 109, 110],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "unique",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Destiny Bond",
    power: null,
    pp: 5,
    priority: 0,
    type: "ghost",
  },
  "perish-song": {
    id: "perish-song",
    accuracy: null,
    damageClass: "status",
    effect: "User and target both faint after three turns.",
    effectChance: null,
    learnedBy: [86, 92, 94, 104, 124, 131],
    meta: {
      ailment: "perish-song",
      ailmentChance: 0,
      category: "ailment",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: 4,
      minHits: null,
      minTurns: 4,
      statChance: 0,
    },
    name: "Perish Song",
    power: null,
    pp: 5,
    priority: 0,
    type: "normal",
  },
  "icy-wind": {
    id: "icy-wind",
    accuracy: 95,
    damageClass: "special",
    effect:
      "Has a $effect_chance% chance to lower the target's Speed by one stage.",
    effectChance: 100,
    learnedBy: [
      7, 8, 9, 19, 20, 31, 34, 35, 36, 39, 40, 52, 53, 54, 55, 60, 61, 62, 72,
      73, 79, 80, 86, 87, 90, 91, 92, 93, 94, 98, 99, 104, 105, 108, 111, 112,
      113, 115, 116, 117, 118, 119, 120, 121, 122, 124, 128, 130, 131, 134, 137,
      138, 139, 140, 141, 143, 144, 147, 148, 149, 150, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage+lower",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 100,
    },
    name: "Icy Wind",
    power: 55,
    pp: 15,
    priority: 0,
    type: "ice",
  },
  detect: {
    id: "detect",
    accuracy: null,
    damageClass: "status",
    effect: "Prevents any moves from hitting the user this turn.",
    effectChance: null,
    learnedBy: [
      16, 17, 18, 21, 22, 25, 26, 27, 28, 29, 30, 31, 32, 33, 34, 35, 36, 39,
      40, 41, 42, 52, 53, 56, 57, 61, 62, 66, 67, 68, 83, 104, 105, 106, 107,
      123, 125, 126, 133, 134, 135, 136, 142, 144, 145, 146, 147, 148, 149, 150,
      151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "unique",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Detect",
    power: null,
    pp: 5,
    priority: 4,
    type: "fighting",
  },
  "bone-rush": {
    id: "bone-rush",
    accuracy: 90,
    damageClass: "physical",
    effect: "Hits 2-5 times in one turn.",
    effectChance: null,
    learnedBy: [104, 105],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: 5,
      maxTurns: null,
      minHits: 2,
      minTurns: null,
      statChance: 0,
    },
    name: "Bone Rush",
    power: 25,
    pp: 10,
    priority: 0,
    type: "ground",
  },
  "lock-on": {
    id: "lock-on",
    accuracy: null,
    damageClass: "status",
    effect: "Ensures that the user's next move will hit the target.",
    effectChance: null,
    learnedBy: [81, 82, 137],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "unique",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Lock-On",
    power: null,
    pp: 5,
    priority: 0,
    type: "normal",
  },
  outrage: {
    id: "outrage",
    accuracy: 100,
    damageClass: "physical",
    effect: "Hits every turn for 2-3 turns, then confuses the user.",
    effectChance: null,
    learnedBy: [
      1, 2, 3, 4, 5, 6, 7, 8, 9, 15, 31, 34, 56, 57, 58, 59, 105, 112, 115, 116,
      117, 127, 128, 130, 131, 143, 147, 148, 149, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Outrage",
    power: 120,
    pp: 10,
    priority: 0,
    type: "dragon",
  },
  sandstorm: {
    id: "sandstorm",
    accuracy: null,
    damageClass: "status",
    effect: "Changes the weather to a sandstorm for five turns.",
    effectChance: null,
    learnedBy: [
      6, 27, 28, 31, 34, 39, 40, 50, 51, 74, 75, 76, 81, 82, 88, 89, 95, 104,
      105, 108, 111, 112, 113, 115, 128, 130, 138, 139, 140, 141, 142, 143, 144,
      145, 146, 149, 150, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "whole-field-effect",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Sandstorm",
    power: null,
    pp: 10,
    priority: 0,
    type: "rock",
  },
  "giga-drain": {
    id: "giga-drain",
    accuracy: 100,
    damageClass: "special",
    effect: "Drains half the damage inflicted to heal the user.",
    effectChance: null,
    learnedBy: [
      1, 2, 3, 12, 15, 23, 24, 41, 42, 43, 44, 45, 46, 47, 48, 49, 69, 70, 71,
      72, 73, 88, 89, 92, 93, 94, 102, 103, 114, 140, 141, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage+heal",
      critRate: 0,
      drain: 50,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Giga Drain",
    power: 75,
    pp: 10,
    priority: 0,
    type: "grass",
  },
  endure: {
    id: "endure",
    accuracy: null,
    damageClass: "status",
    effect: "Prevents the user's HP from lowering below 1 this turn.",
    effectChance: null,
    learnedBy: [
      1, 2, 3, 4, 5, 6, 7, 8, 9, 12, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25,
      26, 27, 28, 29, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 40, 41, 42, 43,
      44, 45, 46, 47, 48, 49, 50, 51, 52, 53, 54, 55, 56, 57, 58, 59, 60, 61,
      62, 63, 64, 65, 66, 67, 68, 69, 70, 71, 72, 73, 74, 75, 76, 77, 78, 79,
      80, 81, 82, 83, 84, 85, 86, 87, 88, 89, 90, 91, 92, 93, 94, 95, 96, 97,
      98, 99, 100, 101, 102, 103, 104, 105, 106, 107, 108, 109, 110, 111, 112,
      113, 114, 115, 116, 117, 118, 119, 120, 121, 122, 123, 124, 125, 126, 127,
      128, 130, 131, 133, 134, 135, 136, 137, 138, 139, 140, 141, 142, 143, 144,
      145, 146, 147, 148, 149, 150, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "unique",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Endure",
    power: null,
    pp: 10,
    priority: 4,
    type: "normal",
  },
  charm: {
    id: "charm",
    accuracy: 100,
    damageClass: "status",
    effect: "Lowers the target's Attack by two stages.",
    effectChance: null,
    learnedBy: [
      1, 2, 3, 25, 26, 29, 30, 31, 35, 36, 39, 40, 43, 44, 45, 50, 51, 52, 53,
      58, 59, 77, 78, 113, 122, 124, 131, 133, 134, 135, 136, 143, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "net-good-stats",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Charm",
    power: null,
    pp: 20,
    priority: 0,
    type: "fairy",
  },
  rollout: {
    id: "rollout",
    accuracy: 90,
    damageClass: "physical",
    effect:
      "Power doubles every turn this move is used in succession after the first, resetting after five turns.",
    effectChance: null,
    learnedBy: [
      7, 8, 9, 25, 26, 27, 28, 35, 36, 39, 40, 74, 75, 76, 81, 82, 95, 100, 101,
      102, 103, 108, 109, 110, 111, 112, 113, 120, 121, 138, 139, 140, 141, 143,
      151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Rollout",
    power: 30,
    pp: 20,
    priority: 0,
    type: "rock",
  },
  "false-swipe": {
    id: "false-swipe",
    accuracy: 100,
    damageClass: "physical",
    effect: "Cannot lower the target's HP below 1.",
    effectChance: null,
    learnedBy: [
      1, 2, 3, 4, 5, 6, 7, 8, 9, 15, 21, 22, 46, 47, 52, 53, 83, 98, 99, 104,
      105, 123, 127, 135, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "False Swipe",
    power: 40,
    pp: 40,
    priority: 0,
    type: "normal",
  },
  swagger: {
    id: "swagger",
    accuracy: 85,
    damageClass: "status",
    effect: "Raises the target's Attack by two stages and confuses the target.",
    effectChance: null,
    learnedBy: [
      1, 2, 3, 4, 5, 6, 7, 8, 9, 12, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25,
      26, 27, 28, 29, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 40, 41, 42, 43,
      44, 45, 46, 47, 48, 49, 50, 51, 52, 53, 54, 55, 56, 57, 58, 59, 60, 61,
      62, 63, 64, 65, 66, 67, 68, 69, 70, 71, 72, 73, 74, 75, 76, 77, 78, 79,
      80, 81, 82, 83, 84, 85, 86, 87, 88, 89, 90, 91, 92, 93, 94, 95, 96, 97,
      98, 99, 100, 101, 102, 103, 104, 105, 106, 107, 108, 109, 110, 111, 112,
      113, 114, 115, 116, 117, 118, 119, 120, 121, 122, 123, 124, 125, 126, 127,
      128, 130, 131, 133, 134, 135, 136, 137, 138, 139, 140, 141, 142, 143, 144,
      145, 146, 147, 148, 149, 150, 151,
    ],
    meta: {
      ailment: "confusion",
      ailmentChance: 0,
      category: "swagger",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: 5,
      minHits: null,
      minTurns: 2,
      statChance: 0,
    },
    name: "Swagger",
    power: null,
    pp: 15,
    priority: 0,
    type: "normal",
  },
  "milk-drink": {
    id: "milk-drink",
    accuracy: null,
    damageClass: "status",
    effect: "Heals the user by half its max HP.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "heal",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 50,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Milk Drink",
    power: null,
    pp: 10,
    priority: 0,
    type: "normal",
  },
  spark: {
    id: "spark",
    accuracy: 100,
    damageClass: "physical",
    effect: "Has a $effect_chance% chance to paralyze the target.",
    effectChance: 30,
    learnedBy: [25, 26, 81, 82, 100, 101],
    meta: {
      ailment: "paralysis",
      ailmentChance: 30,
      category: "damage+ailment",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Spark",
    power: 65,
    pp: 20,
    priority: 0,
    type: "electric",
  },
  "fury-cutter": {
    id: "fury-cutter",
    accuracy: 95,
    damageClass: "physical",
    effect:
      "Power doubles every turn this move is used in succession after the first, maxing out after five turns.",
    effectChance: null,
    learnedBy: [
      1, 2, 3, 4, 5, 6, 15, 27, 28, 31, 34, 46, 47, 55, 76, 80, 83, 98, 99, 104,
      105, 112, 115, 118, 119, 123, 127, 141, 149, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Fury Cutter",
    power: 40,
    pp: 20,
    priority: 0,
    type: "bug",
  },
  "steel-wing": {
    id: "steel-wing",
    accuracy: 90,
    damageClass: "physical",
    effect:
      "Has a $effect_chance% chance to raise the user's Defense by one stage.",
    effectChance: 10,
    learnedBy: [
      6, 16, 17, 18, 21, 22, 41, 42, 83, 84, 85, 123, 142, 144, 145, 146, 149,
      151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage+raise",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 10,
    },
    name: "Steel Wing",
    power: 70,
    pp: 25,
    priority: 0,
    type: "steel",
  },
  "mean-look": {
    id: "mean-look",
    accuracy: null,
    damageClass: "status",
    effect: "Prevents the target from leaving battle.",
    effectChance: null,
    learnedBy: [41, 42, 88, 92, 93, 94, 124],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "unique",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Mean Look",
    power: null,
    pp: 5,
    priority: 0,
    type: "normal",
  },
  attract: {
    id: "attract",
    accuracy: 100,
    damageClass: "status",
    effect:
      "Target falls in love if it has the opposite gender, and has a 50% chance to refuse attacking the user.",
    effectChance: null,
    learnedBy: [
      1, 2, 3, 4, 5, 6, 7, 8, 9, 12, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25,
      26, 27, 28, 29, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 40, 41, 42, 43,
      44, 45, 46, 47, 48, 49, 50, 51, 52, 53, 54, 55, 56, 57, 58, 59, 60, 61,
      62, 63, 64, 65, 66, 67, 68, 69, 70, 71, 72, 73, 74, 75, 76, 77, 78, 79,
      80, 83, 84, 85, 86, 87, 88, 89, 90, 91, 92, 93, 94, 95, 96, 97, 98, 99,
      102, 103, 104, 105, 106, 107, 108, 109, 110, 111, 112, 113, 114, 115, 116,
      117, 118, 119, 120, 121, 122, 123, 124, 125, 126, 127, 128, 130, 131, 133,
      134, 135, 136, 138, 139, 140, 141, 142, 143, 147, 148, 149, 151,
    ],
    meta: {
      ailment: "infatuation",
      ailmentChance: 0,
      category: "ailment",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Attract",
    power: null,
    pp: 15,
    priority: 0,
    type: "normal",
  },
  "sleep-talk": {
    id: "sleep-talk",
    accuracy: null,
    damageClass: "status",
    effect:
      "Randomly uses one of the user's other three moves.  Only works if the user is sleeping.",
    effectChance: null,
    learnedBy: [
      1, 2, 3, 4, 5, 6, 7, 8, 9, 12, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25,
      26, 27, 28, 29, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 40, 41, 42, 43,
      44, 45, 46, 47, 48, 49, 50, 51, 52, 53, 54, 55, 56, 57, 58, 59, 60, 61,
      62, 63, 64, 65, 66, 67, 68, 69, 70, 71, 72, 73, 74, 75, 76, 77, 78, 79,
      80, 81, 82, 83, 84, 85, 86, 87, 88, 89, 90, 91, 92, 93, 94, 95, 96, 97,
      98, 99, 100, 101, 102, 103, 104, 105, 106, 107, 108, 109, 110, 111, 112,
      113, 114, 115, 116, 117, 118, 119, 120, 121, 122, 123, 124, 125, 126, 127,
      128, 130, 131, 133, 134, 135, 136, 137, 138, 139, 140, 141, 142, 143, 144,
      145, 146, 147, 148, 149, 150, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "unique",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Sleep Talk",
    power: null,
    pp: 10,
    priority: 0,
    type: "normal",
  },
  "heal-bell": {
    id: "heal-bell",
    accuracy: null,
    damageClass: "status",
    effect: "Cures the entire party of major status effects.",
    effectChance: null,
    learnedBy: [35, 36, 39, 40, 113, 124, 131, 133, 134, 135, 136, 151],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "unique",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Heal Bell",
    power: null,
    pp: 5,
    priority: 0,
    type: "normal",
  },
  return: {
    id: "return",
    accuracy: 100,
    damageClass: "physical",
    effect: "Power increases with happiness, up to a maximum of 102.",
    effectChance: null,
    learnedBy: [
      1, 2, 3, 4, 5, 6, 7, 8, 9, 12, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25,
      26, 27, 28, 29, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 40, 41, 42, 43,
      44, 45, 46, 47, 48, 49, 50, 51, 52, 53, 54, 55, 56, 57, 58, 59, 60, 61,
      62, 63, 64, 65, 66, 67, 68, 69, 70, 71, 72, 73, 74, 75, 76, 77, 78, 79,
      80, 81, 82, 83, 84, 85, 86, 87, 88, 89, 90, 91, 92, 93, 94, 95, 96, 97,
      98, 99, 100, 101, 102, 103, 104, 105, 106, 107, 108, 109, 110, 111, 112,
      113, 114, 115, 116, 117, 118, 119, 120, 121, 122, 123, 124, 125, 126, 127,
      128, 130, 131, 133, 134, 135, 136, 137, 138, 139, 140, 141, 142, 143, 144,
      145, 146, 147, 148, 149, 150, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Return",
    power: null,
    pp: 20,
    priority: 0,
    type: "normal",
  },
  present: {
    id: "present",
    accuracy: 90,
    damageClass: "physical",
    effect:
      "Randomly inflicts damage with power from 40 to 120 or heals the target for 1/4 its max HP.",
    effectChance: null,
    learnedBy: [113],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Present",
    power: null,
    pp: 15,
    priority: 0,
    type: "normal",
  },
  frustration: {
    id: "frustration",
    accuracy: 100,
    damageClass: "physical",
    effect: "Power increases as happiness decreases, up to a maximum of 102.",
    effectChance: null,
    learnedBy: [
      1, 2, 3, 4, 5, 6, 7, 8, 9, 12, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25,
      26, 27, 28, 29, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 40, 41, 42, 43,
      44, 45, 46, 47, 48, 49, 50, 51, 52, 53, 54, 55, 56, 57, 58, 59, 60, 61,
      62, 63, 64, 65, 66, 67, 68, 69, 70, 71, 72, 73, 74, 75, 76, 77, 78, 79,
      80, 81, 82, 83, 84, 85, 86, 87, 88, 89, 90, 91, 92, 93, 94, 95, 96, 97,
      98, 99, 100, 101, 102, 103, 104, 105, 106, 107, 108, 109, 110, 111, 112,
      113, 114, 115, 116, 117, 118, 119, 120, 121, 122, 123, 124, 125, 126, 127,
      128, 130, 131, 133, 134, 135, 136, 137, 138, 139, 140, 141, 142, 143, 144,
      145, 146, 147, 148, 149, 150, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Frustration",
    power: null,
    pp: 20,
    priority: 0,
    type: "normal",
  },
  safeguard: {
    id: "safeguard",
    accuracy: null,
    damageClass: "status",
    effect:
      "Protects the user's field from major status ailments and confusion for five turns.",
    effectChance: null,
    learnedBy: [
      1, 2, 3, 12, 26, 27, 28, 35, 36, 37, 38, 39, 40, 45, 58, 59, 63, 64, 65,
      72, 73, 79, 80, 86, 87, 96, 97, 113, 115, 122, 123, 131, 146, 147, 148,
      149, 150, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "field-effect",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Safeguard",
    power: null,
    pp: 25,
    priority: 0,
    type: "normal",
  },
  "pain-split": {
    id: "pain-split",
    accuracy: null,
    damageClass: "status",
    effect:
      "Sets the user's and targets's HP to the average of their current HP.",
    effectChance: null,
    learnedBy: [
      37, 38, 39, 40, 88, 89, 92, 93, 94, 109, 110, 114, 120, 121, 137, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "unique",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Pain Split",
    power: null,
    pp: 20,
    priority: 0,
    type: "normal",
  },
  "sacred-fire": {
    id: "sacred-fire",
    accuracy: 95,
    damageClass: "physical",
    effect:
      "Has a $effect_chance% chance to burn the target.  Lets frozen Pokémon thaw themselves.",
    effectChance: 50,
    learnedBy: [],
    meta: {
      ailment: "burn",
      ailmentChance: 50,
      category: "damage+ailment",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Sacred Fire",
    power: 100,
    pp: 5,
    priority: 0,
    type: "fire",
  },
  magnitude: {
    id: "magnitude",
    accuracy: 100,
    damageClass: "physical",
    effect: "Power varies randomly from 10 to 150.",
    effectChance: null,
    learnedBy: [27, 28, 50, 51, 74, 75, 76, 108, 111],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Magnitude",
    power: null,
    pp: 30,
    priority: 0,
    type: "ground",
  },
  "dynamic-punch": {
    id: "dynamic-punch",
    accuracy: 50,
    damageClass: "physical",
    effect: "Has a $effect_chance% chance to confuse the target.",
    effectChance: 100,
    learnedBy: [
      4, 5, 6, 7, 8, 9, 25, 26, 27, 28, 31, 34, 35, 36, 39, 40, 54, 55, 56, 57,
      62, 63, 64, 65, 66, 67, 68, 74, 75, 76, 80, 88, 89, 94, 96, 97, 104, 105,
      106, 107, 108, 112, 113, 115, 122, 124, 125, 126, 143, 149, 150, 151,
    ],
    meta: {
      ailment: "confusion",
      ailmentChance: 100,
      category: "damage+ailment",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: 5,
      minHits: null,
      minTurns: 2,
      statChance: 0,
    },
    name: "Dynamic Punch",
    power: 100,
    pp: 5,
    priority: 0,
    type: "fighting",
  },
  megahorn: {
    id: "megahorn",
    accuracy: 85,
    damageClass: "physical",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [34, 78, 86, 87, 111, 112, 118, 119, 128, 131, 151],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Megahorn",
    power: 120,
    pp: 10,
    priority: 0,
    type: "bug",
  },
  "dragon-breath": {
    id: "dragon-breath",
    accuracy: 100,
    damageClass: "special",
    effect: "Has a $effect_chance% chance to paralyze the target.",
    effectChance: 30,
    learnedBy: [
      4, 5, 6, 58, 59, 95, 116, 117, 130, 131, 142, 147, 148, 149, 151,
    ],
    meta: {
      ailment: "paralysis",
      ailmentChance: 30,
      category: "damage+ailment",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Dragon Breath",
    power: 60,
    pp: 20,
    priority: 0,
    type: "dragon",
  },
  "baton-pass": {
    id: "baton-pass",
    accuracy: null,
    damageClass: "status",
    effect:
      "Allows the trainer to switch out the user and pass effects along to its replacement.",
    effectChance: null,
    learnedBy: [
      12, 35, 36, 39, 40, 48, 49, 78, 83, 97, 122, 123, 133, 134, 135, 136, 145,
      151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "unique",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Baton Pass",
    power: null,
    pp: 40,
    priority: 0,
    type: "normal",
  },
  encore: {
    id: "encore",
    accuracy: 100,
    damageClass: "status",
    effect:
      "Forces the target to repeat its last used move every turn for 2 to 6 turns.",
    effectChance: null,
    learnedBy: [
      25, 26, 35, 36, 37, 38, 39, 40, 54, 55, 56, 57, 60, 61, 62, 63, 64, 65,
      66, 67, 68, 69, 86, 87, 93, 94, 96, 97, 122, 124, 143, 149, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "unique",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Encore",
    power: null,
    pp: 5,
    priority: 0,
    type: "normal",
  },
  pursuit: {
    id: "pursuit",
    accuracy: 100,
    damageClass: "physical",
    effect:
      "Has double power against, and can hit, Pokémon attempting to switch out.",
    effectChance: null,
    learnedBy: [
      15, 16, 19, 20, 21, 22, 23, 29, 41, 46, 50, 56, 57, 84, 85, 107, 111, 123,
      128, 142, 143,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Pursuit",
    power: 40,
    pp: 20,
    priority: 0,
    type: "dark",
  },
  "rapid-spin": {
    id: "rapid-spin",
    accuracy: 100,
    damageClass: "physical",
    effect:
      "Frees the user from binding moves, removes Leech Seed, and blows away Spikes.",
    effectChance: 100,
    learnedBy: [7, 8, 9, 27, 28, 72, 90, 120, 121, 140],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage+raise",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 100,
    },
    name: "Rapid Spin",
    power: 50,
    pp: 40,
    priority: 0,
    type: "normal",
  },
  "sweet-scent": {
    id: "sweet-scent",
    accuracy: 100,
    damageClass: "status",
    effect: "Lowers the target's evasion by one stage.",
    effectChance: null,
    learnedBy: [
      1, 2, 3, 12, 15, 43, 44, 45, 46, 47, 48, 49, 69, 70, 71, 114, 124, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "net-good-stats",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Sweet Scent",
    power: null,
    pp: 20,
    priority: 0,
    type: "normal",
  },
  "iron-tail": {
    id: "iron-tail",
    accuracy: 75,
    damageClass: "physical",
    effect:
      "Has a $effect_chance% chance to lower the target's Defense by one stage.",
    effectChance: 30,
    learnedBy: [
      4, 5, 6, 7, 8, 9, 19, 20, 23, 24, 25, 26, 27, 28, 29, 30, 31, 32, 33, 34,
      35, 36, 37, 38, 52, 53, 54, 55, 56, 57, 58, 59, 63, 64, 65, 77, 78, 79,
      80, 83, 86, 87, 95, 104, 105, 108, 111, 112, 113, 115, 125, 126, 128, 130,
      131, 133, 134, 135, 136, 137, 142, 147, 148, 149, 150, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage+lower",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 30,
    },
    name: "Iron Tail",
    power: 100,
    pp: 15,
    priority: 0,
    type: "steel",
  },
  "metal-claw": {
    id: "metal-claw",
    accuracy: 95,
    damageClass: "physical",
    effect:
      "Has a $effect_chance% chance to raise the user's Attack by one stage.",
    effectChance: 10,
    learnedBy: [4, 5, 6, 27, 46, 52, 53, 98, 99, 151],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage+raise",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 10,
    },
    name: "Metal Claw",
    power: 50,
    pp: 35,
    priority: 0,
    type: "steel",
  },
  "vital-throw": {
    id: "vital-throw",
    accuracy: null,
    damageClass: "physical",
    effect: "Never misses.",
    effectChance: null,
    learnedBy: [66, 67, 68, 127],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Vital Throw",
    power: 70,
    pp: 10,
    priority: -1,
    type: "fighting",
  },
  "morning-sun": {
    id: "morning-sun",
    accuracy: null,
    damageClass: "status",
    effect: "Heals the user by half its max HP.  Affected by weather.",
    effectChance: null,
    learnedBy: [48, 58, 77],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "heal",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 50,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Morning Sun",
    power: null,
    pp: 5,
    priority: 0,
    type: "normal",
  },
  synthesis: {
    id: "synthesis",
    accuracy: null,
    damageClass: "status",
    effect: "Heals the user by half its max HP.  Affected by weather.",
    effectChance: null,
    learnedBy: [1, 2, 3, 43, 44, 45, 46, 47, 69, 70, 71, 102, 103, 114, 151],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "heal",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 50,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Synthesis",
    power: null,
    pp: 5,
    priority: 0,
    type: "grass",
  },
  moonlight: {
    id: "moonlight",
    accuracy: null,
    damageClass: "status",
    effect: "Heals the user by half its max HP.  Affected by weather.",
    effectChance: null,
    learnedBy: [35, 36, 43, 44, 45, 102],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "heal",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 50,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Moonlight",
    power: null,
    pp: 5,
    priority: 0,
    type: "fairy",
  },
  "hidden-power": {
    id: "hidden-power",
    accuracy: 100,
    damageClass: "special",
    effect:
      "Power and type depend upon user's IVs.  Power can range from 30 to 70.",
    effectChance: null,
    learnedBy: [
      1, 2, 3, 4, 5, 6, 7, 8, 9, 12, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25,
      26, 27, 28, 29, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 40, 41, 42, 43,
      44, 45, 46, 47, 48, 49, 50, 51, 52, 53, 54, 55, 56, 57, 58, 59, 60, 61,
      62, 63, 64, 65, 66, 67, 68, 69, 70, 71, 72, 73, 74, 75, 76, 77, 78, 79,
      80, 81, 82, 83, 84, 85, 86, 87, 88, 89, 90, 91, 92, 93, 94, 95, 96, 97,
      98, 99, 100, 101, 102, 103, 104, 105, 106, 107, 108, 109, 110, 111, 112,
      113, 114, 115, 116, 117, 118, 119, 120, 121, 122, 123, 124, 125, 126, 127,
      128, 130, 131, 133, 134, 135, 136, 137, 138, 139, 140, 141, 142, 143, 144,
      145, 146, 147, 148, 149, 150, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Hidden Power",
    power: 60,
    pp: 15,
    priority: 0,
    type: "normal",
  },
  "cross-chop": {
    id: "cross-chop",
    accuracy: 80,
    damageClass: "physical",
    effect: "Has an increased chance for a critical hit.",
    effectChance: null,
    learnedBy: [54, 56, 57, 66, 67, 68],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 1,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Cross Chop",
    power: 100,
    pp: 5,
    priority: 0,
    type: "fighting",
  },
  twister: {
    id: "twister",
    accuracy: 100,
    damageClass: "special",
    effect: "Has a $effect_chance% chance to make the target flinch.",
    effectChance: 20,
    learnedBy: [
      6, 12, 16, 17, 18, 21, 22, 41, 42, 49, 83, 95, 116, 117, 120, 121, 130,
      142, 144, 145, 146, 147, 148, 149, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 20,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Twister",
    power: 40,
    pp: 20,
    priority: 0,
    type: "dragon",
  },
  "rain-dance": {
    id: "rain-dance",
    accuracy: null,
    damageClass: "status",
    effect: "Changes the weather to rain for five turns.",
    effectChance: null,
    learnedBy: [
      7, 8, 9, 12, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 29, 30, 31, 32,
      33, 34, 35, 36, 39, 40, 41, 42, 52, 53, 54, 55, 56, 57, 60, 61, 62, 63,
      64, 65, 66, 67, 68, 72, 73, 79, 80, 81, 82, 86, 87, 88, 89, 90, 91, 92,
      93, 94, 96, 97, 98, 99, 100, 101, 106, 107, 108, 109, 110, 111, 112, 113,
      115, 116, 117, 118, 119, 120, 121, 122, 123, 124, 125, 127, 128, 130, 131,
      133, 134, 135, 136, 137, 138, 139, 140, 141, 142, 143, 144, 145, 146, 147,
      148, 149, 150, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "whole-field-effect",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Rain Dance",
    power: null,
    pp: 5,
    priority: 0,
    type: "water",
  },
  "sunny-day": {
    id: "sunny-day",
    accuracy: null,
    damageClass: "status",
    effect: "Changes the weather to sunny for five turns.",
    effectChance: null,
    learnedBy: [
      1, 2, 3, 4, 5, 6, 12, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 27, 28, 29,
      30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 40, 41, 42, 43, 44, 45, 46, 47,
      48, 49, 50, 51, 52, 53, 56, 57, 58, 59, 63, 64, 65, 66, 67, 68, 69, 70,
      71, 74, 75, 76, 77, 78, 79, 80, 81, 82, 83, 84, 85, 88, 89, 92, 93, 94,
      95, 96, 97, 102, 103, 104, 105, 106, 107, 108, 109, 110, 111, 112, 113,
      114, 115, 122, 123, 126, 127, 128, 130, 133, 134, 135, 136, 137, 142, 143,
      144, 145, 146, 147, 148, 149, 150, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "whole-field-effect",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Sunny Day",
    power: null,
    pp: 5,
    priority: 0,
    type: "fire",
  },
  crunch: {
    id: "crunch",
    accuracy: 100,
    damageClass: "physical",
    effect:
      "Has a $effect_chance% chance to lower the target's Defense by one stage.",
    effectChance: 20,
    learnedBy: [
      4, 5, 6, 9, 19, 20, 23, 24, 29, 30, 31, 41, 42, 58, 59, 111, 112, 115,
      130, 139, 142, 143, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage+lower",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 20,
    },
    name: "Crunch",
    power: 80,
    pp: 15,
    priority: 0,
    type: "dark",
  },
  "mirror-coat": {
    id: "mirror-coat",
    accuracy: 100,
    damageClass: "special",
    effect:
      "Inflicts twice the damage the user received from the last special hit it took.",
    effectChance: null,
    learnedBy: [7, 72, 73, 100, 101, 144],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Mirror Coat",
    power: null,
    pp: 20,
    priority: -5,
    type: "psychic",
  },
  "psych-up": {
    id: "psych-up",
    accuracy: null,
    damageClass: "status",
    effect: "Discards the user's stat changes and copies the target's.",
    effectChance: null,
    learnedBy: [
      12, 35, 36, 37, 38, 39, 40, 52, 53, 54, 55, 56, 57, 63, 64, 65, 79, 80,
      81, 82, 83, 92, 93, 94, 95, 96, 97, 102, 103, 108, 113, 114, 120, 121,
      122, 124, 137, 143, 150, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "unique",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Psych Up",
    power: null,
    pp: 10,
    priority: 0,
    type: "normal",
  },
  "extreme-speed": {
    id: "extreme-speed",
    accuracy: 100,
    damageClass: "physical",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [59, 147, 149],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Extreme Speed",
    power: 80,
    pp: 5,
    priority: 2,
    type: "normal",
  },
  "ancient-power": {
    id: "ancient-power",
    accuracy: 100,
    damageClass: "special",
    effect:
      "Has a $effect_chance% chance to raise all of the user's stats by one stage.",
    effectChance: 10,
    learnedBy: [
      4, 50, 74, 75, 76, 95, 98, 99, 102, 103, 104, 111, 112, 114, 131, 138,
      139, 140, 141, 142, 144, 145, 146, 150, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage+raise",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 10,
    },
    name: "Ancient Power",
    power: 60,
    pp: 5,
    priority: 0,
    type: "rock",
  },
  "shadow-ball": {
    id: "shadow-ball",
    accuracy: 100,
    damageClass: "special",
    effect:
      "Has a $effect_chance% chance to lower the target's Special Defense by one stage.",
    effectChance: 20,
    learnedBy: [
      12, 19, 20, 31, 34, 35, 36, 38, 39, 40, 41, 42, 52, 53, 63, 64, 65, 79,
      80, 88, 89, 92, 93, 94, 96, 97, 108, 109, 110, 113, 115, 122, 124, 128,
      133, 134, 135, 136, 137, 143, 150, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage+lower",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 20,
    },
    name: "Shadow Ball",
    power: 80,
    pp: 15,
    priority: 0,
    type: "ghost",
  },
  "future-sight": {
    id: "future-sight",
    accuracy: 100,
    damageClass: "special",
    effect: "Hits the target two turns later.",
    effectChance: null,
    learnedBy: [54, 55, 64, 65, 79, 80, 96, 97, 103, 122, 124, 131, 150, 151],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "unique",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Future Sight",
    power: 120,
    pp: 10,
    priority: 0,
    type: "psychic",
  },
  "rock-smash": {
    id: "rock-smash",
    accuracy: 100,
    damageClass: "physical",
    effect:
      "Has a $effect_chance% chance to lower the target's Defense by one stage.",
    effectChance: 50,
    learnedBy: [
      1, 2, 3, 4, 5, 6, 7, 8, 9, 15, 19, 20, 25, 26, 27, 28, 29, 30, 31, 32, 33,
      34, 35, 36, 46, 47, 50, 51, 54, 55, 56, 57, 58, 59, 61, 62, 66, 67, 68,
      74, 75, 76, 80, 89, 94, 95, 98, 99, 104, 105, 106, 107, 108, 111, 112,
      113, 114, 115, 123, 125, 126, 127, 128, 130, 131, 134, 135, 136, 138, 139,
      140, 141, 142, 143, 144, 145, 146, 149, 150, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage+lower",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 50,
    },
    name: "Rock Smash",
    power: 40,
    pp: 15,
    priority: 0,
    type: "fighting",
  },
  whirlpool: {
    id: "whirlpool",
    accuracy: 85,
    damageClass: "special",
    effect:
      "Prevents the target from leaving battle and inflicts 1/16 its max HP in damage for 2-5 turns.",
    effectChance: 100,
    learnedBy: [
      7, 8, 9, 31, 34, 54, 55, 60, 61, 62, 72, 73, 79, 80, 86, 87, 90, 91, 98,
      99, 108, 112, 115, 116, 117, 118, 119, 120, 121, 128, 130, 131, 134, 138,
      139, 140, 141, 143, 147, 148, 149, 151,
    ],
    meta: {
      ailment: "trap",
      ailmentChance: 100,
      category: "damage+ailment",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: 6,
      minHits: null,
      minTurns: 5,
      statChance: 0,
    },
    name: "Whirlpool",
    power: 35,
    pp: 15,
    priority: 0,
    type: "water",
  },
  "beat-up": {
    id: "beat-up",
    accuracy: 100,
    damageClass: "physical",
    effect: "Hits once for every conscious Pokémon the trainer has.",
    effectChance: null,
    learnedBy: [4, 5, 6, 23, 29, 30, 31, 32, 33, 34, 50, 51, 56, 115, 151],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: 6,
      maxTurns: null,
      minHits: 6,
      minTurns: null,
      statChance: 0,
    },
    name: "Beat Up",
    power: null,
    pp: 10,
    priority: 0,
    type: "dark",
  },
  "fake-out": {
    id: "fake-out",
    accuracy: 100,
    damageClass: "physical",
    effect:
      "Can only be used as the first move after the user enters battle.  Causes the target to flinch.",
    effectChance: 100,
    learnedBy: [7, 9, 26, 52, 53, 86, 87, 106, 107, 115, 122, 151],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 100,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Fake Out",
    power: 40,
    pp: 10,
    priority: 3,
    type: "normal",
  },
  uproar: {
    id: "uproar",
    accuracy: 100,
    damageClass: "special",
    effect:
      "Forced to use this move for several turns.  Pokémon cannot fall asleep in that time.",
    effectChance: null,
    learnedBy: [
      16, 17, 18, 19, 20, 21, 22, 25, 26, 31, 34, 35, 36, 39, 40, 41, 42, 50,
      51, 52, 53, 56, 57, 83, 84, 85, 92, 93, 94, 102, 103, 104, 105, 106, 107,
      109, 110, 111, 112, 113, 115, 122, 124, 125, 126, 128, 130, 143, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: 3,
      minHits: null,
      minTurns: 3,
      statChance: 0,
    },
    name: "Uproar",
    power: 90,
    pp: 10,
    priority: 0,
    type: "normal",
  },
  stockpile: {
    id: "stockpile",
    accuracy: null,
    damageClass: "status",
    effect: "Stores energy up to three times for use with Spit Up and Swallow.",
    effectChance: null,
    learnedBy: [23, 24, 39, 40, 71, 86, 88, 109, 143],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "unique",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Stockpile",
    power: null,
    pp: 20,
    priority: 0,
    type: "normal",
  },
  "spit-up": {
    id: "spit-up",
    accuracy: 100,
    damageClass: "special",
    effect: "Power is 100 times the amount of energy Stockpiled.",
    effectChance: null,
    learnedBy: [23, 24, 39, 40, 71, 86, 88, 109],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Spit Up",
    power: null,
    pp: 10,
    priority: 0,
    type: "normal",
  },
  swallow: {
    id: "swallow",
    accuracy: null,
    damageClass: "status",
    effect:
      "Recovers 1/4 HP after one Stockpile, 1/2 HP after two Stockpiles, or full HP after three Stockpiles.",
    effectChance: null,
    learnedBy: [23, 24, 39, 40, 71, 86, 88, 109, 143],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "heal",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 25,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Swallow",
    power: null,
    pp: 10,
    priority: 0,
    type: "normal",
  },
  "heat-wave": {
    id: "heat-wave",
    accuracy: 90,
    damageClass: "special",
    effect: "Has a $effect_chance% chance to burn the target.",
    effectChance: 10,
    learnedBy: [
      4, 5, 6, 16, 17, 18, 21, 22, 37, 38, 41, 42, 58, 59, 77, 78, 83, 110, 126,
      136, 142, 145, 146, 149, 151,
    ],
    meta: {
      ailment: "burn",
      ailmentChance: 10,
      category: "damage+ailment",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Heat Wave",
    power: 95,
    pp: 10,
    priority: 0,
    type: "fire",
  },
  hail: {
    id: "hail",
    accuracy: null,
    damageClass: "status",
    effect: "Changes the weather to a hailstorm for five turns.",
    effectChance: null,
    learnedBy: [
      7, 8, 9, 54, 55, 60, 61, 62, 72, 73, 79, 80, 86, 87, 90, 91, 98, 99, 113,
      115, 116, 117, 118, 119, 120, 121, 124, 130, 131, 134, 138, 139, 140, 141,
      144, 145, 147, 148, 149, 150, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "whole-field-effect",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Hail",
    power: null,
    pp: 10,
    priority: 0,
    type: "ice",
  },
  torment: {
    id: "torment",
    accuracy: 100,
    damageClass: "status",
    effect: "Prevents the target from using the same move twice in a row.",
    effectChance: null,
    learnedBy: [
      23, 24, 31, 34, 41, 42, 52, 53, 63, 64, 65, 85, 88, 89, 91, 92, 93, 94,
      95, 96, 97, 100, 101, 109, 110, 122, 124, 130, 142, 150, 151,
    ],
    meta: {
      ailment: "torment",
      ailmentChance: 0,
      category: "ailment",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Torment",
    power: null,
    pp: 15,
    priority: 0,
    type: "dark",
  },
  flatter: {
    id: "flatter",
    accuracy: 100,
    damageClass: "status",
    effect:
      "Raises the target's Special Attack by one stage and confuses the target.",
    effectChance: null,
    learnedBy: [29, 30, 31, 32, 33, 34, 96],
    meta: {
      ailment: "confusion",
      ailmentChance: 0,
      category: "swagger",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: 5,
      minHits: null,
      minTurns: 2,
      statChance: 0,
    },
    name: "Flatter",
    power: null,
    pp: 15,
    priority: 0,
    type: "dark",
  },
  "will-o-wisp": {
    id: "will-o-wisp",
    accuracy: 85,
    damageClass: "status",
    effect: "Burns the target.",
    effectChance: null,
    learnedBy: [
      4, 5, 6, 37, 38, 58, 59, 77, 78, 92, 93, 94, 109, 110, 126, 136, 146, 150,
      151,
    ],
    meta: {
      ailment: "burn",
      ailmentChance: 0,
      category: "ailment",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Will-O-Wisp",
    power: null,
    pp: 15,
    priority: 0,
    type: "fire",
  },
  memento: {
    id: "memento",
    accuracy: 100,
    damageClass: "status",
    effect:
      "Lowers the target's Attack and Special Attack by two stages.  User faints.",
    effectChance: null,
    learnedBy: [37, 50, 88, 89, 109, 110],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "unique",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Memento",
    power: null,
    pp: 10,
    priority: 0,
    type: "dark",
  },
  facade: {
    id: "facade",
    accuracy: 100,
    damageClass: "physical",
    effect: "Power doubles if user is burned, paralyzed, or poisoned.",
    effectChance: null,
    learnedBy: [
      1, 2, 3, 4, 5, 6, 7, 8, 9, 12, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25,
      26, 27, 28, 29, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 40, 41, 42, 43,
      44, 45, 46, 47, 48, 49, 50, 51, 52, 53, 54, 55, 56, 57, 58, 59, 60, 61,
      62, 63, 64, 65, 66, 67, 68, 69, 70, 71, 72, 73, 74, 75, 76, 77, 78, 79,
      80, 81, 82, 83, 84, 85, 86, 87, 88, 89, 90, 91, 92, 93, 94, 95, 96, 97,
      98, 99, 100, 101, 102, 103, 104, 105, 106, 107, 108, 109, 110, 111, 112,
      113, 114, 115, 116, 117, 118, 119, 120, 121, 122, 123, 124, 125, 126, 127,
      128, 130, 131, 133, 134, 135, 136, 137, 138, 139, 140, 141, 142, 143, 144,
      145, 146, 147, 148, 149, 150, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Facade",
    power: 70,
    pp: 20,
    priority: 0,
    type: "normal",
  },
  "focus-punch": {
    id: "focus-punch",
    accuracy: 100,
    damageClass: "physical",
    effect:
      "If the user takes damage before attacking, the attack is canceled.",
    effectChance: null,
    learnedBy: [
      4, 5, 6, 7, 8, 9, 25, 26, 27, 28, 31, 34, 35, 36, 39, 40, 54, 55, 56, 57,
      61, 62, 63, 64, 65, 66, 67, 68, 74, 75, 76, 80, 89, 94, 96, 97, 104, 105,
      106, 107, 108, 112, 113, 115, 122, 124, 125, 126, 127, 143, 149, 150, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Focus Punch",
    power: 150,
    pp: 20,
    priority: -3,
    type: "fighting",
  },
  "smelling-salts": {
    id: "smelling-salts",
    accuracy: 100,
    damageClass: "physical",
    effect:
      "If the target is paralyzed, inflicts double damage and cures the paralysis.",
    effectChance: null,
    learnedBy: [56, 66, 108],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Smelling Salts",
    power: 70,
    pp: 10,
    priority: 0,
    type: "normal",
  },
  "follow-me": {
    id: "follow-me",
    accuracy: null,
    damageClass: "status",
    effect:
      "Redirects the target's single-target effects to the user for this turn.",
    effectChance: null,
    learnedBy: [7, 35, 36, 126],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "unique",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Follow Me",
    power: null,
    pp: 20,
    priority: 2,
    type: "normal",
  },
  "nature-power": {
    id: "nature-power",
    accuracy: null,
    damageClass: "status",
    effect: "Uses a move which depends upon the terrain.",
    effectChance: null,
    learnedBy: [
      1, 2, 3, 43, 44, 45, 46, 47, 69, 70, 71, 74, 75, 76, 95, 102, 103, 114,
      141, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "unique",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Nature Power",
    power: null,
    pp: 20,
    priority: 0,
    type: "normal",
  },
  charge: {
    id: "charge",
    accuracy: null,
    damageClass: "status",
    effect:
      "Raises the user's Special Defense by one stage.  User's Electric moves have doubled power next turn.",
    effectChance: null,
    learnedBy: [100, 101, 125, 145],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "net-good-stats",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Charge",
    power: null,
    pp: 20,
    priority: 0,
    type: "electric",
  },
  taunt: {
    id: "taunt",
    accuracy: 100,
    damageClass: "status",
    effect: "For the next few turns, the target can only use damaging moves.",
    effectChance: null,
    learnedBy: [
      19, 20, 31, 34, 39, 40, 41, 42, 52, 53, 54, 55, 56, 57, 63, 64, 65, 85,
      88, 89, 92, 93, 94, 95, 96, 97, 100, 101, 109, 110, 122, 124, 125, 126,
      130, 142, 150, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "unique",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Taunt",
    power: null,
    pp: 20,
    priority: 0,
    type: "dark",
  },
  "helping-hand": {
    id: "helping-hand",
    accuracy: null,
    damageClass: "status",
    effect: "Ally's next move inflicts half more damage.",
    effectChance: null,
    learnedBy: [
      1, 2, 3, 4, 5, 6, 7, 8, 9, 25, 26, 29, 30, 31, 32, 33, 34, 35, 36, 39, 40,
      50, 52, 53, 54, 55, 56, 57, 58, 59, 60, 61, 62, 66, 67, 68, 79, 80, 81,
      82, 83, 86, 87, 88, 89, 90, 91, 96, 97, 100, 101, 106, 107, 108, 112, 113,
      115, 122, 123, 124, 125, 126, 127, 128, 130, 131, 133, 134, 135, 136, 144,
      145, 146, 147, 148, 149, 150, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "unique",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Helping Hand",
    power: null,
    pp: 20,
    priority: 5,
    type: "normal",
  },
  trick: {
    id: "trick",
    accuracy: 100,
    damageClass: "status",
    effect: "User and target swap items.",
    effectChance: null,
    learnedBy: [
      35, 36, 63, 64, 65, 79, 80, 92, 93, 94, 96, 97, 121, 122, 124, 137, 150,
      151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "unique",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Trick",
    power: null,
    pp: 10,
    priority: 0,
    type: "psychic",
  },
  "role-play": {
    id: "role-play",
    accuracy: null,
    damageClass: "status",
    effect: "Copies the target's ability.",
    effectChance: null,
    learnedBy: [
      35, 36, 37, 38, 39, 40, 54, 55, 56, 57, 63, 64, 65, 66, 67, 68, 94, 96,
      97, 106, 107, 122, 124, 128, 150, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "unique",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Role Play",
    power: null,
    pp: 10,
    priority: 0,
    type: "psychic",
  },
  wish: {
    id: "wish",
    accuracy: null,
    damageClass: "status",
    effect: "User will recover half its max HP at the end of the next turn.",
    effectChance: null,
    learnedBy: [133],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "unique",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Wish",
    power: null,
    pp: 10,
    priority: 0,
    type: "normal",
  },
  assist: {
    id: "assist",
    accuracy: null,
    damageClass: "status",
    effect:
      "Randomly selects and uses one of the trainer's other Pokémon's moves.",
    effectChance: null,
    learnedBy: [52, 96],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "unique",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Assist",
    power: null,
    pp: 20,
    priority: 0,
    type: "normal",
  },
  ingrain: {
    id: "ingrain",
    accuracy: null,
    damageClass: "status",
    effect:
      "Prevents the user from leaving battle.  User regains 1/16 of its max HP every turn.",
    effectChance: null,
    learnedBy: [1, 43, 69, 102, 114],
    meta: {
      ailment: "ingrain",
      ailmentChance: 0,
      category: "ailment",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Ingrain",
    power: null,
    pp: 20,
    priority: 0,
    type: "grass",
  },
  superpower: {
    id: "superpower",
    accuracy: 100,
    damageClass: "physical",
    effect:
      "Lowers the user's Attack and Defense by one stage after inflicting damage.",
    effectChance: 100,
    learnedBy: [
      31, 34, 59, 62, 66, 67, 68, 74, 75, 76, 98, 99, 106, 111, 112, 127, 136,
      141, 143, 149, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage+raise",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 100,
    },
    name: "Superpower",
    power: 120,
    pp: 5,
    priority: 0,
    type: "fighting",
  },
  "magic-coat": {
    id: "magic-coat",
    accuracy: null,
    damageClass: "status",
    effect: "Reflects back the first effect move used on the user this turn.",
    effectChance: null,
    learnedBy: [
      35, 36, 39, 40, 63, 64, 65, 72, 73, 79, 80, 81, 82, 96, 97, 100, 101, 120,
      121, 122, 124, 137, 150, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "unique",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Magic Coat",
    power: null,
    pp: 15,
    priority: 4,
    type: "psychic",
  },
  recycle: {
    id: "recycle",
    accuracy: null,
    damageClass: "status",
    effect: "User recovers the item it last used up.",
    effectChance: null,
    learnedBy: [
      35, 36, 39, 40, 63, 64, 65, 79, 80, 81, 82, 96, 97, 100, 113, 120, 121,
      122, 124, 137, 143, 150, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "unique",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Recycle",
    power: null,
    pp: 10,
    priority: 0,
    type: "normal",
  },
  revenge: {
    id: "revenge",
    accuracy: 100,
    damageClass: "physical",
    effect:
      "Inflicts double damage if the user takes damage before attacking this turn.",
    effectChance: null,
    learnedBy: [19, 56, 66, 67, 68, 83, 106, 107, 127, 128, 151],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Revenge",
    power: 60,
    pp: 10,
    priority: -4,
    type: "fighting",
  },
  "brick-break": {
    id: "brick-break",
    accuracy: 100,
    damageClass: "physical",
    effect: "Destroys Reflect and Light Screen.",
    effectChance: null,
    learnedBy: [
      4, 5, 6, 7, 8, 9, 15, 25, 26, 27, 28, 31, 34, 35, 36, 39, 40, 46, 47, 54,
      55, 56, 57, 61, 62, 66, 67, 68, 74, 75, 76, 80, 89, 94, 96, 97, 98, 99,
      104, 105, 106, 107, 108, 112, 113, 115, 122, 123, 124, 125, 126, 127, 141,
      143, 149, 150, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Brick Break",
    power: 75,
    pp: 15,
    priority: 0,
    type: "fighting",
  },
  yawn: {
    id: "yawn",
    accuracy: null,
    damageClass: "status",
    effect: "Target sleeps at the end of the next turn.",
    effectChance: null,
    learnedBy: [7, 54, 55, 79, 80, 133, 134, 135, 136, 143],
    meta: {
      ailment: "yawn",
      ailmentChance: 0,
      category: "ailment",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: 2,
      minHits: null,
      minTurns: 2,
      statChance: 0,
    },
    name: "Yawn",
    power: null,
    pp: 10,
    priority: 0,
    type: "normal",
  },
  "knock-off": {
    id: "knock-off",
    accuracy: 100,
    damageClass: "physical",
    effect: "Target drops its held item.",
    effectChance: null,
    learnedBy: [
      1, 2, 3, 15, 25, 26, 27, 28, 35, 36, 39, 40, 46, 47, 52, 53, 63, 64, 65,
      66, 67, 68, 69, 70, 71, 72, 73, 83, 84, 85, 92, 93, 94, 98, 99, 104, 105,
      106, 108, 114, 118, 119, 123, 127, 138, 139, 140, 141, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Knock Off",
    power: 65,
    pp: 20,
    priority: 0,
    type: "dark",
  },
  endeavor: {
    id: "endeavor",
    accuracy: 100,
    damageClass: "physical",
    effect: "Lowers the target's HP to equal the user's.",
    effectChance: null,
    learnedBy: [
      15, 19, 20, 35, 36, 39, 40, 56, 57, 60, 61, 62, 84, 85, 104, 105, 111,
      112, 113, 114, 115, 128, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Endeavor",
    power: null,
    pp: 5,
    priority: 0,
    type: "normal",
  },
  eruption: {
    id: "eruption",
    accuracy: 100,
    damageClass: "special",
    effect:
      "Inflicts more damage when the user has more HP remaining, with a maximum of 150 power.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Eruption",
    power: 150,
    pp: 5,
    priority: 0,
    type: "fire",
  },
  "skill-swap": {
    id: "skill-swap",
    accuracy: null,
    damageClass: "status",
    effect: "User and target swap abilities.",
    effectChance: null,
    learnedBy: [
      12, 39, 40, 48, 49, 54, 55, 63, 64, 65, 79, 80, 92, 93, 94, 96, 97, 102,
      103, 113, 121, 122, 124, 150, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "unique",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Skill Swap",
    power: null,
    pp: 10,
    priority: 0,
    type: "psychic",
  },
  imprison: {
    id: "imprison",
    accuracy: null,
    damageClass: "status",
    effect:
      "Prevents the target from using any moves that the user also knows.",
    effectChance: null,
    learnedBy: [
      35, 36, 37, 38, 65, 79, 80, 88, 89, 92, 93, 94, 96, 97, 150, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "unique",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Imprison",
    power: null,
    pp: 10,
    priority: 0,
    type: "psychic",
  },
  refresh: {
    id: "refresh",
    accuracy: null,
    damageClass: "status",
    effect: "Cleanses the user of a burn, paralysis, or poison.",
    effectChance: null,
    learnedBy: [7, 54, 60, 108, 113, 131, 133],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "unique",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Refresh",
    power: null,
    pp: 20,
    priority: 0,
    type: "normal",
  },
  grudge: {
    id: "grudge",
    accuracy: null,
    damageClass: "status",
    effect:
      "If the user faints this turn, the PP of the move that fainted it drops to 0.",
    effectChance: null,
    learnedBy: [37, 38, 92, 109],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "unique",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Grudge",
    power: null,
    pp: 5,
    priority: 0,
    type: "ghost",
  },
  snatch: {
    id: "snatch",
    accuracy: null,
    damageClass: "status",
    effect: "Steals the target's move, if it's self-targeted.",
    effectChance: null,
    learnedBy: [
      23, 24, 35, 36, 39, 40, 41, 42, 52, 53, 63, 64, 65, 92, 93, 94, 96, 97,
      113, 122, 143, 150, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "unique",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Snatch",
    power: null,
    pp: 10,
    priority: 4,
    type: "dark",
  },
  "secret-power": {
    id: "secret-power",
    accuracy: 100,
    damageClass: "physical",
    effect:
      "Has a $effect_chance% chance to inflict a status effect which depends upon the terrain.",
    effectChance: 30,
    learnedBy: [
      1, 2, 3, 4, 5, 6, 7, 8, 9, 12, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25,
      26, 27, 28, 29, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 40, 41, 42, 43,
      44, 45, 46, 47, 48, 49, 50, 51, 52, 53, 54, 55, 56, 57, 58, 59, 60, 61,
      62, 63, 64, 65, 66, 67, 68, 69, 70, 71, 72, 73, 74, 75, 76, 77, 78, 79,
      80, 81, 82, 83, 84, 85, 86, 87, 88, 89, 90, 91, 92, 93, 94, 95, 96, 97,
      98, 99, 100, 101, 102, 103, 104, 105, 106, 107, 108, 109, 110, 111, 112,
      113, 114, 115, 116, 117, 118, 119, 120, 121, 122, 123, 124, 125, 126, 127,
      128, 130, 131, 133, 134, 135, 136, 137, 138, 139, 140, 141, 142, 143, 144,
      145, 146, 147, 148, 149, 150, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 30,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Secret Power",
    power: 70,
    pp: 20,
    priority: 0,
    type: "normal",
  },
  dive: {
    id: "dive",
    accuracy: 100,
    damageClass: "physical",
    effect: "User dives underwater, dodging all attacks, and hits next turn.",
    effectChance: null,
    learnedBy: [
      7, 8, 9, 54, 55, 60, 61, 62, 72, 73, 79, 80, 86, 87, 90, 91, 98, 99, 116,
      117, 118, 119, 120, 121, 130, 131, 134, 138, 139, 141, 149, 150, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Dive",
    power: 80,
    pp: 10,
    priority: 0,
    type: "water",
  },
  "arm-thrust": {
    id: "arm-thrust",
    accuracy: 100,
    damageClass: "physical",
    effect: "Hits 2-5 times in one turn.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: 5,
      maxTurns: null,
      minHits: 2,
      minTurns: null,
      statChance: 0,
    },
    name: "Arm Thrust",
    power: 15,
    pp: 20,
    priority: 0,
    type: "fighting",
  },
  camouflage: {
    id: "camouflage",
    accuracy: null,
    damageClass: "status",
    effect: "User's type changes to match the terrain.",
    effectChance: null,
    learnedBy: [120],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "unique",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Camouflage",
    power: null,
    pp: 20,
    priority: 0,
    type: "normal",
  },
  "tail-glow": {
    id: "tail-glow",
    accuracy: null,
    damageClass: "status",
    effect: "Raises the user's Special Attack by three stages.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "net-good-stats",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Tail Glow",
    power: null,
    pp: 20,
    priority: 0,
    type: "bug",
  },
  "luster-purge": {
    id: "luster-purge",
    accuracy: 100,
    damageClass: "special",
    effect:
      "Has a $effect_chance% chance to lower the target's Special Defense by one stage.",
    effectChance: 50,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage+lower",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 50,
    },
    name: "Luster Purge",
    power: 70,
    pp: 5,
    priority: 0,
    type: "psychic",
  },
  "mist-ball": {
    id: "mist-ball",
    accuracy: 100,
    damageClass: "special",
    effect:
      "Has a $effect_chance% chance to lower the target's Special Attack by one stage.",
    effectChance: 50,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage+lower",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 50,
    },
    name: "Mist Ball",
    power: 70,
    pp: 5,
    priority: 0,
    type: "psychic",
  },
  "feather-dance": {
    id: "feather-dance",
    accuracy: 100,
    damageClass: "status",
    effect: "Lowers the target's Attack by two stages.",
    effectChance: null,
    learnedBy: [16, 17, 18, 21, 83],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "net-good-stats",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Feather Dance",
    power: null,
    pp: 15,
    priority: 0,
    type: "flying",
  },
  "teeter-dance": {
    id: "teeter-dance",
    accuracy: 100,
    damageClass: "status",
    effect: "Confuses the target.",
    effectChance: null,
    learnedBy: [43, 122],
    meta: {
      ailment: "confusion",
      ailmentChance: 0,
      category: "ailment",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: 5,
      minHits: null,
      minTurns: 2,
      statChance: 0,
    },
    name: "Teeter Dance",
    power: null,
    pp: 20,
    priority: 0,
    type: "normal",
  },
  "blaze-kick": {
    id: "blaze-kick",
    accuracy: 90,
    damageClass: "physical",
    effect:
      "Has an increased chance for a critical hit and a $effect_chance% chance to burn the target.",
    effectChance: 10,
    learnedBy: [6, 106, 151],
    meta: {
      ailment: "burn",
      ailmentChance: 10,
      category: "damage+ailment",
      critRate: 1,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Blaze Kick",
    power: 85,
    pp: 10,
    priority: 0,
    type: "fire",
  },
  "mud-sport": {
    id: "mud-sport",
    accuracy: null,
    damageClass: "status",
    effect: "Halves all Electric-type damage.",
    effectChance: null,
    learnedBy: [7, 74, 75, 76, 79, 95, 98, 99, 118],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "whole-field-effect",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Mud Sport",
    power: null,
    pp: 15,
    priority: 0,
    type: "ground",
  },
  "ice-ball": {
    id: "ice-ball",
    accuracy: 90,
    damageClass: "physical",
    effect:
      "Power doubles every turn this move is used in succession after the first, resetting after five turns.",
    effectChance: null,
    learnedBy: [60],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Ice Ball",
    power: 30,
    pp: 20,
    priority: 0,
    type: "ice",
  },
  "needle-arm": {
    id: "needle-arm",
    accuracy: 100,
    damageClass: "physical",
    effect: "Has a $effect_chance% chance to make the target flinch.",
    effectChance: 30,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 30,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Needle Arm",
    power: 60,
    pp: 15,
    priority: 0,
    type: "grass",
  },
  "slack-off": {
    id: "slack-off",
    accuracy: null,
    damageClass: "status",
    effect: "Heals the user by half its max HP.",
    effectChance: null,
    learnedBy: [79, 80],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "heal",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 50,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Slack Off",
    power: null,
    pp: 10,
    priority: 0,
    type: "normal",
  },
  "hyper-voice": {
    id: "hyper-voice",
    accuracy: 100,
    damageClass: "special",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [
      35, 36, 39, 40, 52, 53, 59, 113, 124, 131, 133, 134, 135, 136, 143, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Hyper Voice",
    power: 90,
    pp: 10,
    priority: 0,
    type: "normal",
  },
  "poison-fang": {
    id: "poison-fang",
    accuracy: 100,
    damageClass: "physical",
    effect: "Has a $effect_chance% chance to badly poison the target.",
    effectChance: 50,
    learnedBy: [23, 29, 30, 41, 42, 48, 49],
    meta: {
      ailment: "poison",
      ailmentChance: 50,
      category: "damage+ailment",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: 15,
      minHits: null,
      minTurns: 15,
      statChance: 0,
    },
    name: "Poison Fang",
    power: 50,
    pp: 15,
    priority: 0,
    type: "poison",
  },
  "crush-claw": {
    id: "crush-claw",
    accuracy: 95,
    damageClass: "physical",
    effect:
      "Has a $effect_chance% chance to lower the target's Defense by one stage.",
    effectChance: 50,
    learnedBy: [27, 28, 111, 115],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage+lower",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 50,
    },
    name: "Crush Claw",
    power: 75,
    pp: 10,
    priority: 0,
    type: "normal",
  },
  "blast-burn": {
    id: "blast-burn",
    accuracy: 90,
    damageClass: "special",
    effect: "User foregoes its next turn to recharge.",
    effectChance: null,
    learnedBy: [6, 151],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Blast Burn",
    power: 150,
    pp: 5,
    priority: 0,
    type: "fire",
  },
  "hydro-cannon": {
    id: "hydro-cannon",
    accuracy: 90,
    damageClass: "special",
    effect: "User foregoes its next turn to recharge.",
    effectChance: null,
    learnedBy: [9, 151],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Hydro Cannon",
    power: 150,
    pp: 5,
    priority: 0,
    type: "water",
  },
  "meteor-mash": {
    id: "meteor-mash",
    accuracy: 90,
    damageClass: "physical",
    effect:
      "Has a $effect_chance% chance to raise the user's Attack by one stage.",
    effectChance: 20,
    learnedBy: [35, 36],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage+raise",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 20,
    },
    name: "Meteor Mash",
    power: 90,
    pp: 10,
    priority: 0,
    type: "steel",
  },
  astonish: {
    id: "astonish",
    accuracy: 100,
    damageClass: "physical",
    effect: "Has a $effect_chance% chance to make the target flinch.",
    effectChance: 30,
    learnedBy: [21, 41, 42, 50, 51, 92],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 30,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Astonish",
    power: 30,
    pp: 15,
    priority: 0,
    type: "ghost",
  },
  "weather-ball": {
    id: "weather-ball",
    accuracy: 100,
    damageClass: "special",
    effect:
      "If there be weather, this move has doubled power and the weather's type.",
    effectChance: null,
    learnedBy: [
      1, 2, 3, 4, 5, 6, 7, 8, 9, 37, 38, 69, 79, 80, 91, 131, 133, 134, 135,
      136, 144, 145, 146, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Weather Ball",
    power: 50,
    pp: 10,
    priority: 0,
    type: "normal",
  },
  aromatherapy: {
    id: "aromatherapy",
    accuracy: null,
    damageClass: "status",
    effect: "Cures the entire party of major status effects.",
    effectChance: null,
    learnedBy: [45, 46, 47, 113],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "unique",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Aromatherapy",
    power: null,
    pp: 5,
    priority: 0,
    type: "grass",
  },
  "fake-tears": {
    id: "fake-tears",
    accuracy: 100,
    damageClass: "status",
    effect: "Lowers the target's Special Defense by two stages.",
    effectChance: null,
    learnedBy: [25, 35, 36, 38, 39, 40, 52, 53, 124, 133, 134, 135, 136, 151],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "net-good-stats",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Fake Tears",
    power: null,
    pp: 20,
    priority: 0,
    type: "dark",
  },
  "air-cutter": {
    id: "air-cutter",
    accuracy: 95,
    damageClass: "special",
    effect: "Has an increased chance for a critical hit.",
    effectChance: null,
    learnedBy: [
      4, 6, 12, 15, 16, 17, 18, 21, 22, 41, 42, 49, 83, 84, 85, 123, 142, 144,
      145, 146, 149, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 1,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Air Cutter",
    power: 60,
    pp: 25,
    priority: 0,
    type: "flying",
  },
  overheat: {
    id: "overheat",
    accuracy: 90,
    damageClass: "special",
    effect:
      "Lowers the user's Special Attack by two stages after inflicting damage.",
    effectChance: 100,
    learnedBy: [4, 5, 6, 37, 38, 56, 57, 58, 59, 77, 78, 126, 136, 146, 151],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage+raise",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 100,
    },
    name: "Overheat",
    power: 130,
    pp: 5,
    priority: 0,
    type: "fire",
  },
  "odor-sleuth": {
    id: "odor-sleuth",
    accuracy: null,
    damageClass: "status",
    effect:
      "Forces the target to have no Evade, and allows it to be hit by Normal and Fighting moves even if it's a Ghost.",
    effectChance: null,
    learnedBy: [52, 58, 59],
    meta: {
      ailment: "no-type-immunity",
      ailmentChance: 0,
      category: "ailment",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Odor Sleuth",
    power: null,
    pp: 40,
    priority: 0,
    type: "normal",
  },
  "rock-tomb": {
    id: "rock-tomb",
    accuracy: 95,
    damageClass: "physical",
    effect:
      "Has a $effect_chance% chance to lower the target's Speed by one stage.",
    effectChance: 100,
    learnedBy: [
      4, 5, 6, 7, 8, 9, 23, 24, 27, 28, 31, 34, 50, 51, 56, 57, 62, 66, 67, 68,
      74, 75, 76, 88, 89, 95, 98, 99, 104, 105, 106, 107, 108, 111, 112, 113,
      115, 127, 128, 138, 139, 140, 141, 142, 143, 149, 150, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage+lower",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 100,
    },
    name: "Rock Tomb",
    power: 60,
    pp: 15,
    priority: 0,
    type: "rock",
  },
  "silver-wind": {
    id: "silver-wind",
    accuracy: 100,
    damageClass: "special",
    effect:
      "Has a $effect_chance% chance to raise all of the user's stats by one stage.",
    effectChance: 10,
    learnedBy: [12, 15, 49, 123, 151],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage+raise",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 10,
    },
    name: "Silver Wind",
    power: 60,
    pp: 5,
    priority: 0,
    type: "bug",
  },
  "metal-sound": {
    id: "metal-sound",
    accuracy: 85,
    damageClass: "status",
    effect: "Lowers the target's Special Defense by two stages.",
    effectChance: null,
    learnedBy: [81, 82, 100, 140, 141],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "net-good-stats",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Metal Sound",
    power: null,
    pp: 40,
    priority: 0,
    type: "steel",
  },
  "grass-whistle": {
    id: "grass-whistle",
    accuracy: 55,
    damageClass: "status",
    effect: "Puts the target to sleep.",
    effectChance: null,
    learnedBy: [1],
    meta: {
      ailment: "sleep",
      ailmentChance: 0,
      category: "ailment",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: 4,
      minHits: null,
      minTurns: 2,
      statChance: 0,
    },
    name: "Grass Whistle",
    power: null,
    pp: 15,
    priority: 0,
    type: "grass",
  },
  tickle: {
    id: "tickle",
    accuracy: 100,
    damageClass: "status",
    effect: "Lowers the target's Attack and Defense by one stage.",
    effectChance: null,
    learnedBy: [43, 66, 69, 72, 98, 114, 122, 131, 133, 138, 139],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "net-good-stats",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Tickle",
    power: null,
    pp: 20,
    priority: 0,
    type: "normal",
  },
  "cosmic-power": {
    id: "cosmic-power",
    accuracy: null,
    damageClass: "status",
    effect: "Raises the user's Defense and Special Defense by one stage.",
    effectChance: null,
    learnedBy: [35, 36, 120, 121, 151],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "net-good-stats",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Cosmic Power",
    power: null,
    pp: 20,
    priority: 0,
    type: "psychic",
  },
  "water-spout": {
    id: "water-spout",
    accuracy: 100,
    damageClass: "special",
    effect:
      "Inflicts more damage when the user has more HP remaining, with a maximum of 150 power.",
    effectChance: null,
    learnedBy: [7],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Water Spout",
    power: 150,
    pp: 5,
    priority: 0,
    type: "water",
  },
  "signal-beam": {
    id: "signal-beam",
    accuracy: 100,
    damageClass: "special",
    effect: "Has a $effect_chance% chance to confuse the target.",
    effectChance: 10,
    learnedBy: [
      9, 12, 25, 26, 35, 36, 48, 49, 54, 55, 63, 64, 65, 79, 80, 81, 82, 86, 87,
      91, 96, 97, 100, 101, 116, 117, 118, 119, 120, 121, 122, 124, 125, 131,
      134, 135, 137, 144, 145, 150, 151,
    ],
    meta: {
      ailment: "confusion",
      ailmentChance: 10,
      category: "damage+ailment",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: 5,
      minHits: null,
      minTurns: 2,
      statChance: 0,
    },
    name: "Signal Beam",
    power: 75,
    pp: 15,
    priority: 0,
    type: "bug",
  },
  "shadow-punch": {
    id: "shadow-punch",
    accuracy: null,
    damageClass: "physical",
    effect: "Never misses.",
    effectChance: null,
    learnedBy: [88, 93, 94],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Shadow Punch",
    power: 60,
    pp: 20,
    priority: 0,
    type: "ghost",
  },
  extrasensory: {
    id: "extrasensory",
    accuracy: 100,
    damageClass: "special",
    effect: "Has a $effect_chance% chance to make the target flinch.",
    effectChance: 10,
    learnedBy: [37, 38, 102, 103],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 10,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Extrasensory",
    power: 80,
    pp: 20,
    priority: 0,
    type: "psychic",
  },
  "sky-uppercut": {
    id: "sky-uppercut",
    accuracy: 90,
    damageClass: "physical",
    effect: "Inflicts regular damage and can hit Bounce and Fly users.",
    effectChance: null,
    learnedBy: [107],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Sky Uppercut",
    power: 85,
    pp: 15,
    priority: 0,
    type: "fighting",
  },
  "sand-tomb": {
    id: "sand-tomb",
    accuracy: 85,
    damageClass: "physical",
    effect:
      "Prevents the target from fleeing and inflicts damage for 2-5 turns.",
    effectChance: 100,
    learnedBy: [27, 28, 31, 34, 51, 95, 151],
    meta: {
      ailment: "trap",
      ailmentChance: 100,
      category: "damage+ailment",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: 6,
      minHits: null,
      minTurns: 5,
      statChance: 0,
    },
    name: "Sand Tomb",
    power: 35,
    pp: 15,
    priority: 0,
    type: "ground",
  },
  "sheer-cold": {
    id: "sheer-cold",
    accuracy: 30,
    damageClass: "special",
    effect: "Causes a one-hit KO.",
    effectChance: null,
    learnedBy: [87, 131, 144],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "ohko",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Sheer Cold",
    power: null,
    pp: 5,
    priority: 0,
    type: "ice",
  },
  "muddy-water": {
    id: "muddy-water",
    accuracy: 85,
    damageClass: "special",
    effect:
      "Has a $effect_chance% chance to lower the target's accuracy by one stage.",
    effectChance: 30,
    learnedBy: [
      7, 8, 9, 55, 60, 61, 62, 72, 73, 80, 108, 116, 117, 118, 119, 130, 134,
      138, 139, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage+lower",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 30,
    },
    name: "Muddy Water",
    power: 90,
    pp: 10,
    priority: 0,
    type: "water",
  },
  "bullet-seed": {
    id: "bullet-seed",
    accuracy: 100,
    damageClass: "physical",
    effect: "Hits 2-5 times in one turn.",
    effectChance: null,
    learnedBy: [1, 2, 3, 43, 44, 45, 46, 47, 69, 70, 71, 102, 103, 114, 151],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: 5,
      maxTurns: null,
      minHits: 2,
      minTurns: null,
      statChance: 0,
    },
    name: "Bullet Seed",
    power: 25,
    pp: 30,
    priority: 0,
    type: "grass",
  },
  "aerial-ace": {
    id: "aerial-ace",
    accuracy: null,
    damageClass: "physical",
    effect: "Never misses.",
    effectChance: null,
    learnedBy: [
      4, 5, 6, 12, 15, 16, 17, 18, 21, 22, 27, 28, 29, 30, 31, 41, 42, 46, 47,
      49, 50, 51, 52, 53, 54, 55, 56, 57, 58, 59, 80, 83, 84, 85, 104, 105, 115,
      122, 123, 137, 140, 141, 142, 144, 145, 146, 149, 150, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Aerial Ace",
    power: 60,
    pp: 20,
    priority: 0,
    type: "flying",
  },
  "icicle-spear": {
    id: "icicle-spear",
    accuracy: 100,
    damageClass: "physical",
    effect: "Hits 2-5 times in one turn.",
    effectChance: null,
    learnedBy: [86, 90, 91, 124, 144, 151],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: 5,
      maxTurns: null,
      minHits: 2,
      minTurns: null,
      statChance: 0,
    },
    name: "Icicle Spear",
    power: 25,
    pp: 30,
    priority: 0,
    type: "ice",
  },
  "iron-defense": {
    id: "iron-defense",
    accuracy: null,
    damageClass: "status",
    effect: "Raises the user's Defense by two stages.",
    effectChance: null,
    learnedBy: [
      7, 8, 9, 11, 12, 14, 74, 75, 76, 80, 81, 82, 90, 91, 98, 99, 104, 105,
      112, 122, 127, 138, 139, 140, 141, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "net-good-stats",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Iron Defense",
    power: null,
    pp: 15,
    priority: 0,
    type: "steel",
  },
  block: {
    id: "block",
    accuracy: null,
    damageClass: "status",
    effect: "Prevents the target from leaving battle.",
    effectChance: null,
    learnedBy: [3, 74, 75, 76, 79, 80, 89, 95, 102, 103, 112, 131, 143, 151],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "unique",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Block",
    power: null,
    pp: 5,
    priority: 0,
    type: "normal",
  },
  howl: {
    id: "howl",
    accuracy: null,
    damageClass: "status",
    effect: "Raises the user's Attack by one stage.",
    effectChance: null,
    learnedBy: [37, 58, 59],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "net-good-stats",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Howl",
    power: null,
    pp: 40,
    priority: 0,
    type: "normal",
  },
  "dragon-claw": {
    id: "dragon-claw",
    accuracy: 100,
    damageClass: "physical",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [4, 5, 6, 142, 149, 151],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Dragon Claw",
    power: 80,
    pp: 15,
    priority: 0,
    type: "dragon",
  },
  "frenzy-plant": {
    id: "frenzy-plant",
    accuracy: 90,
    damageClass: "special",
    effect: "User foregoes its next turn to recharge.",
    effectChance: null,
    learnedBy: [3, 151],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Frenzy Plant",
    power: 150,
    pp: 5,
    priority: 0,
    type: "grass",
  },
  "bulk-up": {
    id: "bulk-up",
    accuracy: null,
    damageClass: "status",
    effect: "Raises the user's Attack and Defense by one stage.",
    effectChance: null,
    learnedBy: [56, 57, 62, 66, 67, 68, 106, 107, 127, 150, 151],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "net-good-stats",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Bulk Up",
    power: null,
    pp: 20,
    priority: 0,
    type: "fighting",
  },
  bounce: {
    id: "bounce",
    accuracy: 85,
    damageClass: "physical",
    effect:
      "User bounces high into the air, dodging all attacks, and hits next turn.",
    effectChance: 30,
    learnedBy: [35, 36, 39, 40, 77, 78, 106, 116, 117, 118, 119, 129, 130, 151],
    meta: {
      ailment: "paralysis",
      ailmentChance: 30,
      category: "damage+ailment",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Bounce",
    power: 85,
    pp: 5,
    priority: 0,
    type: "flying",
  },
  "mud-shot": {
    id: "mud-shot",
    accuracy: 95,
    damageClass: "special",
    effect:
      "Has a $effect_chance% chance to lower the target's Speed by one stage.",
    effectChance: 100,
    learnedBy: [
      27, 28, 31, 34, 50, 51, 54, 55, 60, 61, 62, 79, 80, 88, 89, 90, 91, 98,
      99, 111, 112, 118, 119, 138, 139, 140, 141, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage+lower",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 100,
    },
    name: "Mud Shot",
    power: 55,
    pp: 15,
    priority: 0,
    type: "ground",
  },
  "poison-tail": {
    id: "poison-tail",
    accuracy: 100,
    damageClass: "physical",
    effect:
      "Has an increased chance for a critical hit and a $effect_chance% chance to poison the target.",
    effectChance: 10,
    learnedBy: [23, 29, 32, 151],
    meta: {
      ailment: "poison",
      ailmentChance: 10,
      category: "damage+ailment",
      critRate: 1,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Poison Tail",
    power: 50,
    pp: 25,
    priority: 0,
    type: "poison",
  },
  covet: {
    id: "covet",
    accuracy: 100,
    damageClass: "physical",
    effect: "Takes the target's item.",
    effectChance: null,
    learnedBy: [
      19, 20, 25, 26, 27, 28, 35, 36, 37, 38, 39, 40, 52, 53, 56, 57, 58, 59,
      83, 106, 107, 113, 115, 122, 124, 125, 126, 133, 134, 135, 136, 143, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Covet",
    power: 60,
    pp: 25,
    priority: 0,
    type: "normal",
  },
  "volt-tackle": {
    id: "volt-tackle",
    accuracy: 100,
    damageClass: "physical",
    effect:
      "User takes 1/3 the damage inflicted in recoil.  Has a $effect_chance% chance to paralyze the target.",
    effectChance: 10,
    learnedBy: [25],
    meta: {
      ailment: "paralysis",
      ailmentChance: 10,
      category: "damage+ailment",
      critRate: 0,
      drain: -33,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Volt Tackle",
    power: 120,
    pp: 15,
    priority: 0,
    type: "electric",
  },
  "magical-leaf": {
    id: "magical-leaf",
    accuracy: null,
    damageClass: "special",
    effect: "Never misses.",
    effectChance: null,
    learnedBy: [1, 2, 3, 35, 36, 39, 40, 69, 103, 122, 151],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Magical Leaf",
    power: 60,
    pp: 20,
    priority: 0,
    type: "grass",
  },
  "water-sport": {
    id: "water-sport",
    accuracy: null,
    damageClass: "status",
    effect: "Halves all Fire-type damage.",
    effectChance: null,
    learnedBy: [54, 55, 60, 61, 86, 118, 119],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "whole-field-effect",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Water Sport",
    power: null,
    pp: 15,
    priority: 0,
    type: "water",
  },
  "calm-mind": {
    id: "calm-mind",
    accuracy: null,
    damageClass: "status",
    effect:
      "Raises the user's Special Attack and Special Defense by one stage.",
    effectChance: null,
    learnedBy: [
      25, 26, 35, 36, 38, 39, 40, 54, 55, 63, 64, 65, 79, 80, 96, 97, 103, 113,
      122, 124, 133, 134, 135, 136, 150, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "net-good-stats",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Calm Mind",
    power: null,
    pp: 20,
    priority: 0,
    type: "psychic",
  },
  "leaf-blade": {
    id: "leaf-blade",
    accuracy: 100,
    damageClass: "physical",
    effect: "Has an increased chance for a critical hit.",
    effectChance: null,
    learnedBy: [71, 83, 151],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 1,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Leaf Blade",
    power: 90,
    pp: 15,
    priority: 0,
    type: "grass",
  },
  "dragon-dance": {
    id: "dragon-dance",
    accuracy: null,
    damageClass: "status",
    effect: "Raises the user's Attack and Speed by one stage.",
    effectChance: null,
    learnedBy: [4, 5, 6, 95, 116, 117, 130, 131, 142, 147, 148, 149, 151],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "net-good-stats",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Dragon Dance",
    power: null,
    pp: 20,
    priority: 0,
    type: "dragon",
  },
  "rock-blast": {
    id: "rock-blast",
    accuracy: 90,
    damageClass: "physical",
    effect: "Hits 2-5 times in one turn.",
    effectChance: null,
    learnedBy: [
      31, 34, 50, 51, 68, 74, 75, 76, 90, 91, 95, 111, 112, 138, 139, 140, 141,
      142, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: 5,
      maxTurns: null,
      minHits: 2,
      minTurns: null,
      statChance: 0,
    },
    name: "Rock Blast",
    power: 25,
    pp: 10,
    priority: 0,
    type: "rock",
  },
  "shock-wave": {
    id: "shock-wave",
    accuracy: null,
    damageClass: "special",
    effect: "Never misses.",
    effectChance: null,
    learnedBy: [
      19, 20, 25, 26, 29, 30, 31, 32, 33, 34, 35, 36, 39, 40, 52, 53, 63, 64,
      65, 81, 82, 88, 89, 100, 101, 108, 109, 110, 111, 112, 113, 114, 115, 122,
      125, 128, 131, 135, 137, 143, 145, 147, 148, 149, 150, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Shock Wave",
    power: 60,
    pp: 20,
    priority: 0,
    type: "electric",
  },
  "water-pulse": {
    id: "water-pulse",
    accuracy: 100,
    damageClass: "special",
    effect: "Has a $effect_chance% chance to confuse the target.",
    effectChance: 20,
    learnedBy: [
      7, 8, 9, 29, 30, 31, 32, 33, 34, 35, 36, 39, 40, 52, 53, 54, 55, 60, 61,
      62, 72, 73, 79, 80, 86, 87, 90, 91, 98, 99, 108, 113, 115, 116, 117, 118,
      119, 120, 121, 124, 128, 130, 131, 134, 138, 139, 140, 141, 143, 144, 147,
      148, 149, 150, 151,
    ],
    meta: {
      ailment: "confusion",
      ailmentChance: 20,
      category: "damage+ailment",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: 5,
      minHits: null,
      minTurns: 2,
      statChance: 0,
    },
    name: "Water Pulse",
    power: 60,
    pp: 20,
    priority: 0,
    type: "water",
  },
  "doom-desire": {
    id: "doom-desire",
    accuracy: 100,
    damageClass: "special",
    effect: "Hits the target two turns later.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "unique",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Doom Desire",
    power: 140,
    pp: 5,
    priority: 0,
    type: "steel",
  },
  "psycho-boost": {
    id: "psycho-boost",
    accuracy: 90,
    damageClass: "special",
    effect:
      "Lowers the user's Special Attack by two stages after inflicting damage.",
    effectChance: 100,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage+raise",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 100,
    },
    name: "Psycho Boost",
    power: 140,
    pp: 5,
    priority: 0,
    type: "psychic",
  },
  roost: {
    id: "roost",
    accuracy: null,
    damageClass: "status",
    effect: "Heals the user by half its max HP.",
    effectChance: null,
    learnedBy: [
      6, 12, 15, 16, 17, 18, 21, 22, 41, 42, 49, 83, 84, 85, 123, 142, 144, 145,
      146, 149, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "heal",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 50,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Roost",
    power: null,
    pp: 10,
    priority: 0,
    type: "flying",
  },
  gravity: {
    id: "gravity",
    accuracy: null,
    damageClass: "status",
    effect:
      "Disables moves and immunities that involve flying or levitating for five turns.",
    effectChance: null,
    learnedBy: [
      35, 36, 39, 40, 63, 64, 65, 81, 82, 102, 103, 113, 120, 121, 137, 150,
      151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "whole-field-effect",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Gravity",
    power: null,
    pp: 5,
    priority: 0,
    type: "psychic",
  },
  "miracle-eye": {
    id: "miracle-eye",
    accuracy: null,
    damageClass: "status",
    effect:
      "Forces the target to have no evasion, and allows it to be hit by Psychic moves even if it's Dark.",
    effectChance: null,
    learnedBy: [64, 65, 150],
    meta: {
      ailment: "no-type-immunity",
      ailmentChance: 0,
      category: "ailment",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Miracle Eye",
    power: null,
    pp: 40,
    priority: 0,
    type: "psychic",
  },
  "wake-up-slap": {
    id: "wake-up-slap",
    accuracy: 100,
    damageClass: "physical",
    effect: "If the target is asleep, has double power and wakes it up.",
    effectChance: null,
    learnedBy: [35, 39, 60, 61, 66, 67, 68, 96, 97, 114, 122, 124],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Wake-Up Slap",
    power: 70,
    pp: 10,
    priority: 0,
    type: "fighting",
  },
  "hammer-arm": {
    id: "hammer-arm",
    accuracy: 90,
    damageClass: "physical",
    effect: "Lowers user's Speed by one stage.",
    effectChance: 100,
    learnedBy: [74, 98, 99, 108, 112, 115, 143],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage+raise",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 100,
    },
    name: "Hammer Arm",
    power: 100,
    pp: 10,
    priority: 0,
    type: "fighting",
  },
  "gyro-ball": {
    id: "gyro-ball",
    accuracy: 100,
    damageClass: "physical",
    effect:
      "Power raises when the user has lower Speed, up to a maximum of 150.",
    effectChance: null,
    learnedBy: [
      7, 8, 9, 27, 28, 39, 40, 74, 75, 76, 81, 82, 95, 100, 101, 109, 110, 120,
      121, 138, 139, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Gyro Ball",
    power: null,
    pp: 5,
    priority: 0,
    type: "steel",
  },
  "healing-wish": {
    id: "healing-wish",
    accuracy: null,
    damageClass: "status",
    effect:
      "User faints.  Its replacement has its HP fully restored and any major status effect removed.",
    effectChance: null,
    learnedBy: [35, 36, 113],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "unique",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Healing Wish",
    power: null,
    pp: 10,
    priority: 0,
    type: "psychic",
  },
  brine: {
    id: "brine",
    accuracy: 100,
    damageClass: "special",
    effect:
      "Has double power against Pokémon that have less than half their max HP remaining.",
    effectChance: null,
    learnedBy: [
      7, 8, 9, 54, 55, 72, 73, 79, 80, 86, 87, 90, 91, 98, 99, 116, 117, 120,
      121, 130, 131, 134, 138, 139, 140, 141, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Brine",
    power: 65,
    pp: 10,
    priority: 0,
    type: "water",
  },
  "natural-gift": {
    id: "natural-gift",
    accuracy: 100,
    damageClass: "physical",
    effect: "Power and type depend on the held berry.",
    effectChance: null,
    learnedBy: [
      1, 2, 3, 4, 5, 6, 7, 8, 9, 12, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25,
      26, 27, 28, 29, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 40, 41, 42, 43,
      44, 45, 46, 47, 48, 49, 50, 51, 52, 53, 54, 55, 56, 57, 58, 59, 60, 61,
      62, 63, 64, 65, 66, 67, 68, 69, 70, 71, 72, 73, 74, 75, 76, 77, 78, 79,
      80, 81, 82, 83, 84, 85, 86, 87, 88, 89, 90, 91, 92, 93, 94, 95, 96, 97,
      98, 99, 100, 101, 102, 103, 104, 105, 106, 107, 108, 109, 110, 111, 112,
      113, 114, 115, 116, 117, 118, 119, 120, 121, 122, 123, 124, 125, 126, 127,
      128, 130, 131, 133, 134, 135, 136, 137, 138, 139, 140, 141, 142, 143, 144,
      145, 146, 147, 148, 149, 150, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Natural Gift",
    power: null,
    pp: 15,
    priority: 0,
    type: "normal",
  },
  feint: {
    id: "feint",
    accuracy: 100,
    damageClass: "physical",
    effect: "Hits through Protect and Detect.",
    effectChance: null,
    learnedBy: [25, 26, 52, 53, 83, 106, 107, 123, 127, 141],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Feint",
    power: 30,
    pp: 10,
    priority: 2,
    type: "normal",
  },
  pluck: {
    id: "pluck",
    accuracy: 100,
    damageClass: "physical",
    effect: "If target has a berry, inflicts double damage and uses the berry.",
    effectChance: null,
    learnedBy: [
      16, 17, 18, 19, 20, 21, 22, 41, 42, 83, 84, 85, 144, 145, 146, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Pluck",
    power: 60,
    pp: 20,
    priority: 0,
    type: "flying",
  },
  tailwind: {
    id: "tailwind",
    accuracy: null,
    damageClass: "status",
    effect: "For three turns, friendly Pokémon have doubled Speed.",
    effectChance: null,
    learnedBy: [
      6, 12, 15, 16, 17, 18, 21, 22, 41, 42, 49, 83, 123, 142, 144, 145, 146,
      149, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "field-effect",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Tailwind",
    power: null,
    pp: 15,
    priority: 0,
    type: "flying",
  },
  acupressure: {
    id: "acupressure",
    accuracy: null,
    damageClass: "status",
    effect: "Raises one of a friendly Pokémon's stats at random by two stages.",
    effectChance: null,
    learnedBy: [72, 84, 85, 118],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "unique",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Acupressure",
    power: null,
    pp: 30,
    priority: 0,
    type: "normal",
  },
  "metal-burst": {
    id: "metal-burst",
    accuracy: 100,
    damageClass: "physical",
    effect:
      "Strikes back at the last Pokémon to hit the user this turn with 1.5× the damage.",
    effectChance: null,
    learnedBy: [111],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Metal Burst",
    power: null,
    pp: 10,
    priority: 0,
    type: "steel",
  },
  "u-turn": {
    id: "u-turn",
    accuracy: 100,
    damageClass: "physical",
    effect: "User must switch out after attacking.",
    effectChance: null,
    learnedBy: [
      12, 15, 16, 17, 18, 19, 20, 21, 22, 41, 42, 49, 52, 53, 56, 57, 83, 123,
      144, 145, 146, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "U-turn",
    power: 70,
    pp: 20,
    priority: 0,
    type: "bug",
  },
  "close-combat": {
    id: "close-combat",
    accuracy: 100,
    damageClass: "physical",
    effect:
      "Lowers the user's Defense and Special Defense by one stage after inflicting damage.",
    effectChance: 100,
    learnedBy: [
      56, 57, 58, 59, 62, 66, 67, 68, 83, 106, 107, 123, 127, 128, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage+raise",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 100,
    },
    name: "Close Combat",
    power: 120,
    pp: 5,
    priority: 0,
    type: "fighting",
  },
  payback: {
    id: "payback",
    accuracy: 100,
    damageClass: "physical",
    effect: "Power is doubled if the target has already moved this turn.",
    effectChance: null,
    learnedBy: [
      15, 23, 24, 37, 38, 41, 42, 52, 53, 56, 57, 62, 66, 67, 68, 72, 73, 85,
      88, 89, 90, 91, 92, 93, 94, 95, 109, 110, 111, 112, 122, 124, 128, 130,
      142, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Payback",
    power: 50,
    pp: 10,
    priority: 0,
    type: "dark",
  },
  assurance: {
    id: "assurance",
    accuracy: 100,
    damageClass: "physical",
    effect:
      "Power is doubled if the target has already received damage this turn.",
    effectChance: null,
    learnedBy: [
      15, 19, 20, 21, 22, 41, 42, 50, 51, 52, 53, 56, 57, 68, 84, 109, 110, 115,
      123, 128, 142, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Assurance",
    power: 60,
    pp: 10,
    priority: 0,
    type: "dark",
  },
  embargo: {
    id: "embargo",
    accuracy: 100,
    damageClass: "status",
    effect: "Target cannot use held items.",
    effectChance: null,
    learnedBy: [53, 63, 64, 65, 92, 93, 94, 150, 151],
    meta: {
      ailment: "embargo",
      ailmentChance: 0,
      category: "ailment",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: 5,
      minHits: null,
      minTurns: 5,
      statChance: 0,
    },
    name: "Embargo",
    power: null,
    pp: 15,
    priority: 0,
    type: "dark",
  },
  fling: {
    id: "fling",
    accuracy: 100,
    damageClass: "physical",
    effect: "Throws held item at the target; power depends on the item.",
    effectChance: null,
    learnedBy: [
      4, 5, 6, 7, 8, 9, 25, 26, 27, 28, 31, 34, 35, 36, 39, 40, 44, 45, 54, 55,
      56, 57, 61, 62, 63, 64, 65, 66, 67, 68, 74, 75, 76, 80, 86, 87, 88, 89,
      93, 94, 96, 97, 98, 99, 104, 105, 106, 107, 108, 112, 113, 115, 122, 124,
      125, 126, 127, 143, 149, 150, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Fling",
    power: null,
    pp: 10,
    priority: 0,
    type: "dark",
  },
  "psycho-shift": {
    id: "psycho-shift",
    accuracy: 100,
    damageClass: "status",
    effect: "Transfers the user's major status effect to the target.",
    effectChance: null,
    learnedBy: [63],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "unique",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Psycho Shift",
    power: null,
    pp: 10,
    priority: 0,
    type: "psychic",
  },
  "trump-card": {
    id: "trump-card",
    accuracy: null,
    damageClass: "special",
    effect:
      "Power increases when this move has less PP, up to a maximum of 200.",
    effectChance: null,
    learnedBy: [83, 115, 133],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Trump Card",
    power: null,
    pp: 5,
    priority: 0,
    type: "normal",
  },
  "heal-block": {
    id: "heal-block",
    accuracy: 100,
    damageClass: "status",
    effect: "Prevents target from restoring its HP for five turns.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "heal-block",
      ailmentChance: 0,
      category: "ailment",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: 5,
      minHits: null,
      minTurns: 5,
      statChance: 0,
    },
    name: "Heal Block",
    power: null,
    pp: 15,
    priority: 0,
    type: "psychic",
  },
  "wring-out": {
    id: "wring-out",
    accuracy: 100,
    damageClass: "special",
    effect:
      "Power increases against targets with more HP remaining, up to a maximum of 121 power.",
    effectChance: null,
    learnedBy: [69, 70, 72, 73, 108, 114, 124, 138, 140, 141],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Wring Out",
    power: null,
    pp: 5,
    priority: 0,
    type: "normal",
  },
  "power-trick": {
    id: "power-trick",
    accuracy: null,
    damageClass: "status",
    effect: "User swaps Attack and Defense.",
    effectChance: null,
    learnedBy: [63, 66],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "unique",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Power Trick",
    power: null,
    pp: 10,
    priority: 0,
    type: "psychic",
  },
  "gastro-acid": {
    id: "gastro-acid",
    accuracy: 100,
    damageClass: "status",
    effect: "Nullifies target's ability until it leaves battle.",
    effectChance: null,
    learnedBy: [23, 24, 43, 44, 45, 69, 70, 71, 143, 151],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "unique",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Gastro Acid",
    power: null,
    pp: 10,
    priority: 0,
    type: "poison",
  },
  "lucky-chant": {
    id: "lucky-chant",
    accuracy: null,
    damageClass: "status",
    effect: "Prevents the target from scoring critical hits for five turns.",
    effectChance: null,
    learnedBy: [35, 43, 44, 102],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "field-effect",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Lucky Chant",
    power: null,
    pp: 30,
    priority: 0,
    type: "normal",
  },
  "me-first": {
    id: "me-first",
    accuracy: null,
    damageClass: "status",
    effect:
      "Uses the target's move against it before it attacks, with power increased by half.",
    effectChance: null,
    learnedBy: [19, 55, 79, 108, 127, 150, 151],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Me First",
    power: null,
    pp: 20,
    priority: 0,
    type: "normal",
  },
  copycat: {
    id: "copycat",
    accuracy: null,
    damageClass: "status",
    effect: "Uses the target's last used move.",
    effectChance: null,
    learnedBy: [35, 36, 39, 40, 113, 122, 124, 133, 134, 135, 136],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "unique",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Copycat",
    power: null,
    pp: 20,
    priority: 0,
    type: "normal",
  },
  "power-swap": {
    id: "power-swap",
    accuracy: null,
    damageClass: "status",
    effect: "User swaps Attack and Special Attack changes with the target.",
    effectChance: null,
    learnedBy: [37, 38, 63, 64, 65, 102, 103, 114, 122, 126, 137, 150, 151],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "unique",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Power Swap",
    power: null,
    pp: 10,
    priority: 0,
    type: "psychic",
  },
  "guard-swap": {
    id: "guard-swap",
    accuracy: null,
    damageClass: "status",
    effect: "User swaps Defense and Special Defense changes with the target.",
    effectChance: null,
    learnedBy: [63, 64, 65, 96, 122, 137, 150, 151],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "unique",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Guard Swap",
    power: null,
    pp: 10,
    priority: 0,
    type: "psychic",
  },
  punishment: {
    id: "punishment",
    accuracy: 100,
    damageClass: "physical",
    effect:
      "Power increases against targets with more raised stats, up to a maximum of 200.",
    effectChance: null,
    learnedBy: [52, 56, 57],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Punishment",
    power: null,
    pp: 5,
    priority: 0,
    type: "dark",
  },
  "last-resort": {
    id: "last-resort",
    accuracy: 100,
    damageClass: "physical",
    effect:
      "Can only be used after all of the user's other moves have been used.",
    effectChance: null,
    learnedBy: [
      19, 20, 35, 36, 39, 40, 52, 53, 83, 113, 115, 133, 134, 135, 136, 137,
      143, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Last Resort",
    power: 140,
    pp: 5,
    priority: 0,
    type: "normal",
  },
  "worry-seed": {
    id: "worry-seed",
    accuracy: 100,
    damageClass: "status",
    effect: "Changes the target's ability to Insomnia.",
    effectChance: null,
    learnedBy: [
      1, 2, 3, 43, 44, 45, 46, 47, 54, 55, 69, 70, 71, 102, 103, 114, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "unique",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Worry Seed",
    power: null,
    pp: 10,
    priority: 0,
    type: "grass",
  },
  "sucker-punch": {
    id: "sucker-punch",
    accuracy: 100,
    damageClass: "physical",
    effect: "Only works if the target is about to use a damaging move.",
    effectChance: null,
    learnedBy: [
      19, 20, 23, 24, 32, 33, 34, 50, 51, 69, 70, 71, 74, 75, 76, 92, 93, 94,
      100, 101, 106, 115, 122, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Sucker Punch",
    power: 70,
    pp: 5,
    priority: 1,
    type: "dark",
  },
  "toxic-spikes": {
    id: "toxic-spikes",
    accuracy: null,
    damageClass: "status",
    effect:
      "Scatters poisoned spikes, poisoning opposing Pokémon that switch in.",
    effectChance: null,
    learnedBy: [
      15, 29, 30, 31, 32, 33, 34, 48, 49, 72, 73, 89, 90, 91, 93, 94, 109, 110,
      138, 139, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "field-effect",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Toxic Spikes",
    power: null,
    pp: 20,
    priority: 0,
    type: "poison",
  },
  "heart-swap": {
    id: "heart-swap",
    accuracy: null,
    damageClass: "status",
    effect: "User and target swap stat changes.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "unique",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Heart Swap",
    power: null,
    pp: 10,
    priority: 0,
    type: "psychic",
  },
  "aqua-ring": {
    id: "aqua-ring",
    accuracy: null,
    damageClass: "status",
    effect: "Restores 1/16 of the user's max HP each turn.",
    effectChance: null,
    learnedBy: [7, 72, 86, 87, 90, 118, 119, 134],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "unique",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Aqua Ring",
    power: null,
    pp: 20,
    priority: 0,
    type: "water",
  },
  "magnet-rise": {
    id: "magnet-rise",
    accuracy: null,
    damageClass: "status",
    effect: "User is immune to Ground moves and effects for five turns.",
    effectChance: null,
    learnedBy: [25, 26, 81, 82, 100, 101, 125, 135, 137, 151],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "unique",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Magnet Rise",
    power: null,
    pp: 10,
    priority: 0,
    type: "electric",
  },
  "flare-blitz": {
    id: "flare-blitz",
    accuracy: 100,
    damageClass: "physical",
    effect:
      "User takes 1/3 the damage inflicted in recoil.  Has a $effect_chance% chance to burn the target.",
    effectChance: 10,
    learnedBy: [4, 5, 6, 37, 38, 58, 59, 77, 78, 126, 136, 146, 151],
    meta: {
      ailment: "burn",
      ailmentChance: 10,
      category: "damage+ailment",
      critRate: 0,
      drain: -33,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Flare Blitz",
    power: 120,
    pp: 15,
    priority: 0,
    type: "fire",
  },
  "force-palm": {
    id: "force-palm",
    accuracy: 100,
    damageClass: "physical",
    effect: "Has a $effect_chance% chance to paralyze the target.",
    effectChance: 30,
    learnedBy: [],
    meta: {
      ailment: "paralysis",
      ailmentChance: 30,
      category: "damage+ailment",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Force Palm",
    power: 60,
    pp: 10,
    priority: 0,
    type: "fighting",
  },
  "aura-sphere": {
    id: "aura-sphere",
    accuracy: null,
    damageClass: "special",
    effect: "Never misses.",
    effectChance: null,
    learnedBy: [7, 8, 9, 106, 107, 150, 151],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Aura Sphere",
    power: 80,
    pp: 20,
    priority: 0,
    type: "fighting",
  },
  "rock-polish": {
    id: "rock-polish",
    accuracy: null,
    damageClass: "status",
    effect: "Raises the user's Speed by two stages.",
    effectChance: null,
    learnedBy: [74, 75, 76, 95, 111, 112, 138, 139, 140, 141, 142, 151],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "net-good-stats",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Rock Polish",
    power: null,
    pp: 20,
    priority: 0,
    type: "rock",
  },
  "poison-jab": {
    id: "poison-jab",
    accuracy: 100,
    damageClass: "physical",
    effect: "Has a $effect_chance% chance to poison the target.",
    effectChance: 30,
    learnedBy: [
      15, 23, 24, 27, 28, 29, 30, 31, 32, 33, 34, 56, 57, 62, 66, 67, 68, 69,
      70, 71, 72, 73, 78, 83, 88, 89, 91, 92, 93, 94, 106, 111, 112, 118, 119,
      150, 151,
    ],
    meta: {
      ailment: "poison",
      ailmentChance: 30,
      category: "damage+ailment",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Poison Jab",
    power: 80,
    pp: 20,
    priority: 0,
    type: "poison",
  },
  "dark-pulse": {
    id: "dark-pulse",
    accuracy: 100,
    damageClass: "special",
    effect: "Has a $effect_chance% chance to make the target flinch.",
    effectChance: 20,
    learnedBy: [
      9, 23, 24, 37, 38, 39, 40, 52, 53, 89, 92, 93, 94, 109, 110, 130, 150,
      151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 20,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Dark Pulse",
    power: 80,
    pp: 15,
    priority: 0,
    type: "dark",
  },
  "night-slash": {
    id: "night-slash",
    accuracy: 100,
    damageClass: "physical",
    effect: "Has an increased chance for a critical hit.",
    effectChance: null,
    learnedBy: [27, 51, 52, 53, 56, 83, 98, 123, 141],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 1,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Night Slash",
    power: 70,
    pp: 15,
    priority: 0,
    type: "dark",
  },
  "aqua-tail": {
    id: "aqua-tail",
    accuracy: 90,
    damageClass: "physical",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [
      7, 8, 9, 23, 24, 31, 34, 54, 55, 79, 80, 86, 87, 108, 111, 112, 115, 118,
      119, 130, 131, 134, 141, 142, 147, 148, 149, 150, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Aqua Tail",
    power: 90,
    pp: 10,
    priority: 0,
    type: "water",
  },
  "seed-bomb": {
    id: "seed-bomb",
    accuracy: 100,
    damageClass: "physical",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [
      1, 2, 3, 23, 24, 43, 44, 45, 46, 47, 52, 53, 56, 57, 69, 70, 71, 102, 103,
      114, 143, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Seed Bomb",
    power: 80,
    pp: 15,
    priority: 0,
    type: "grass",
  },
  "air-slash": {
    id: "air-slash",
    accuracy: 95,
    damageClass: "special",
    effect: "Has a $effect_chance% chance to make the target flinch.",
    effectChance: 30,
    learnedBy: [6, 12, 16, 17, 18, 41, 42, 49, 83, 123, 144, 146, 149, 151],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 30,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Air Slash",
    power: 75,
    pp: 15,
    priority: 0,
    type: "flying",
  },
  "x-scissor": {
    id: "x-scissor",
    accuracy: 100,
    damageClass: "physical",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [15, 27, 28, 46, 47, 98, 99, 123, 127, 141, 151],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "X-Scissor",
    power: 80,
    pp: 15,
    priority: 0,
    type: "bug",
  },
  "bug-buzz": {
    id: "bug-buzz",
    accuracy: 100,
    damageClass: "special",
    effect:
      "Has a $effect_chance% chance to lower the target's Special Defense by one stage.",
    effectChance: 10,
    learnedBy: [12, 48, 49, 123, 151],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage+lower",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 10,
    },
    name: "Bug Buzz",
    power: 90,
    pp: 10,
    priority: 0,
    type: "bug",
  },
  "dragon-pulse": {
    id: "dragon-pulse",
    accuracy: 100,
    damageClass: "special",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [
      4, 5, 6, 7, 8, 9, 31, 34, 59, 95, 111, 112, 116, 117, 130, 131, 142, 147,
      148, 149, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Dragon Pulse",
    power: 85,
    pp: 10,
    priority: 0,
    type: "dragon",
  },
  "dragon-rush": {
    id: "dragon-rush",
    accuracy: 75,
    damageClass: "physical",
    effect: "Has a $effect_chance% chance to make the target flinch.",
    effectChance: 20,
    learnedBy: [4, 111, 147, 148, 149],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 20,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Dragon Rush",
    power: 100,
    pp: 10,
    priority: 0,
    type: "dragon",
  },
  "power-gem": {
    id: "power-gem",
    accuracy: 100,
    damageClass: "special",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [52, 53, 55, 120, 121, 150, 151],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Power Gem",
    power: 80,
    pp: 20,
    priority: 0,
    type: "rock",
  },
  "drain-punch": {
    id: "drain-punch",
    accuracy: 100,
    damageClass: "physical",
    effect: "Drains half the damage inflicted to heal the user.",
    effectChance: null,
    learnedBy: [
      35, 36, 39, 40, 44, 45, 56, 57, 62, 63, 64, 65, 80, 88, 89, 94, 96, 97,
      107, 113, 115, 122, 124, 150, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage+heal",
      critRate: 0,
      drain: 50,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Drain Punch",
    power: 75,
    pp: 10,
    priority: 0,
    type: "fighting",
  },
  "vacuum-wave": {
    id: "vacuum-wave",
    accuracy: 100,
    damageClass: "special",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [56, 57, 62, 66, 67, 68, 106, 107, 123, 151],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Vacuum Wave",
    power: 40,
    pp: 30,
    priority: 1,
    type: "fighting",
  },
  "focus-blast": {
    id: "focus-blast",
    accuracy: 70,
    damageClass: "special",
    effect:
      "Has a $effect_chance% chance to lower the target's Special Defense by one stage.",
    effectChance: 10,
    learnedBy: [
      4, 6, 9, 26, 28, 31, 34, 36, 40, 55, 56, 57, 62, 65, 66, 67, 68, 76, 80,
      89, 93, 94, 96, 97, 105, 106, 107, 112, 113, 115, 122, 124, 125, 126, 127,
      143, 149, 150, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage+lower",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 10,
    },
    name: "Focus Blast",
    power: 120,
    pp: 5,
    priority: 0,
    type: "fighting",
  },
  "energy-ball": {
    id: "energy-ball",
    accuracy: 100,
    damageClass: "special",
    effect:
      "Has a $effect_chance% chance to lower the target's Special Defense by one stage.",
    effectChance: 10,
    learnedBy: [
      1, 2, 3, 12, 37, 38, 39, 40, 43, 44, 45, 46, 47, 48, 49, 63, 64, 65, 69,
      70, 71, 92, 93, 94, 102, 103, 114, 122, 124, 150, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage+lower",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 10,
    },
    name: "Energy Ball",
    power: 90,
    pp: 10,
    priority: 0,
    type: "grass",
  },
  "brave-bird": {
    id: "brave-bird",
    accuracy: 100,
    damageClass: "physical",
    effect: "User receives 1/3 the damage inflicted in recoil.",
    effectChance: null,
    learnedBy: [16, 41, 42, 83, 84, 144, 145, 146, 151],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: -33,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Brave Bird",
    power: 120,
    pp: 15,
    priority: 0,
    type: "flying",
  },
  "earth-power": {
    id: "earth-power",
    accuracy: 100,
    damageClass: "special",
    effect:
      "Has a $effect_chance% chance to lower the target's Special Defense by one stage.",
    effectChance: 10,
    learnedBy: [
      3, 27, 28, 29, 30, 31, 32, 33, 34, 50, 51, 60, 61, 62, 74, 75, 76, 95,
      104, 105, 111, 112, 138, 139, 140, 141, 142, 150, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage+lower",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 10,
    },
    name: "Earth Power",
    power: 90,
    pp: 10,
    priority: 0,
    type: "ground",
  },
  switcheroo: {
    id: "switcheroo",
    accuracy: 100,
    damageClass: "status",
    effect: "User and target swap items.",
    effectChance: null,
    learnedBy: [23, 53, 97],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "unique",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Switcheroo",
    power: null,
    pp: 10,
    priority: 0,
    type: "dark",
  },
  "giga-impact": {
    id: "giga-impact",
    accuracy: 90,
    damageClass: "physical",
    effect: "User foregoes its next turn to recharge.",
    effectChance: null,
    learnedBy: [
      3, 6, 9, 12, 15, 18, 20, 22, 24, 26, 28, 31, 34, 36, 38, 40, 42, 45, 47,
      49, 51, 53, 55, 57, 59, 62, 65, 68, 71, 73, 76, 78, 80, 82, 85, 87, 89,
      91, 94, 97, 99, 101, 103, 105, 108, 110, 112, 113, 114, 115, 117, 119,
      121, 122, 123, 124, 125, 126, 127, 128, 130, 131, 134, 135, 136, 137, 139,
      141, 142, 143, 144, 145, 146, 147, 148, 149, 150, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Giga Impact",
    power: 150,
    pp: 5,
    priority: 0,
    type: "normal",
  },
  "nasty-plot": {
    id: "nasty-plot",
    accuracy: null,
    damageClass: "status",
    effect: "Raises the user's Special Attack by two stages.",
    effectChance: null,
    learnedBy: [
      25, 26, 38, 39, 40, 41, 42, 52, 53, 54, 55, 65, 80, 92, 93, 94, 96, 97,
      122, 124, 150, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "net-good-stats",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Nasty Plot",
    power: null,
    pp: 20,
    priority: 0,
    type: "dark",
  },
  "bullet-punch": {
    id: "bullet-punch",
    accuracy: 100,
    damageClass: "physical",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [66, 107],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Bullet Punch",
    power: 40,
    pp: 30,
    priority: 1,
    type: "steel",
  },
  avalanche: {
    id: "avalanche",
    accuracy: 100,
    damageClass: "physical",
    effect:
      "Inflicts double damage if the user takes damage before attacking this turn.",
    effectChance: null,
    learnedBy: [
      9, 31, 34, 79, 80, 87, 90, 91, 112, 115, 121, 124, 130, 131, 144, 150,
      151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Avalanche",
    power: 60,
    pp: 10,
    priority: -4,
    type: "ice",
  },
  "ice-shard": {
    id: "ice-shard",
    accuracy: 100,
    damageClass: "physical",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [86, 87, 90, 91, 131, 144],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Ice Shard",
    power: 40,
    pp: 30,
    priority: 1,
    type: "ice",
  },
  "shadow-claw": {
    id: "shadow-claw",
    accuracy: 100,
    damageClass: "physical",
    effect: "Has an increased chance for a critical hit.",
    effectChance: null,
    learnedBy: [
      4, 5, 6, 27, 28, 29, 30, 31, 32, 33, 34, 50, 51, 52, 53, 54, 55, 56, 57,
      93, 94, 112, 115, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 1,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Shadow Claw",
    power: 70,
    pp: 15,
    priority: 0,
    type: "ghost",
  },
  "thunder-fang": {
    id: "thunder-fang",
    accuracy: 95,
    damageClass: "physical",
    effect:
      "Has a $effect_chance% chance to paralyze the target and a $effect_chance% chance to make the target flinch.",
    effectChance: 10,
    learnedBy: [24, 58, 59, 111, 112, 135, 142, 151],
    meta: {
      ailment: "paralysis",
      ailmentChance: 10,
      category: "damage+ailment",
      critRate: 0,
      drain: 0,
      flinchChance: 10,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Thunder Fang",
    power: 65,
    pp: 15,
    priority: 0,
    type: "electric",
  },
  "ice-fang": {
    id: "ice-fang",
    accuracy: 95,
    damageClass: "physical",
    effect:
      "Has a $effect_chance% chance to freeze the target and a $effect_chance% chance to make the target flinch.",
    effectChance: 10,
    learnedBy: [24, 111, 112, 130, 142, 151],
    meta: {
      ailment: "freeze",
      ailmentChance: 10,
      category: "damage+ailment",
      critRate: 0,
      drain: 0,
      flinchChance: 10,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Ice Fang",
    power: 65,
    pp: 15,
    priority: 0,
    type: "ice",
  },
  "fire-fang": {
    id: "fire-fang",
    accuracy: 95,
    damageClass: "physical",
    effect:
      "Has a $effect_chance% chance to burn the target and a $effect_chance% chance to make the target flinch.",
    effectChance: 10,
    learnedBy: [4, 5, 6, 24, 58, 59, 111, 112, 136, 142, 151],
    meta: {
      ailment: "burn",
      ailmentChance: 10,
      category: "damage+ailment",
      critRate: 0,
      drain: 0,
      flinchChance: 10,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Fire Fang",
    power: 65,
    pp: 15,
    priority: 0,
    type: "fire",
  },
  "shadow-sneak": {
    id: "shadow-sneak",
    accuracy: 100,
    damageClass: "physical",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [88],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Shadow Sneak",
    power: 40,
    pp: 30,
    priority: 1,
    type: "ghost",
  },
  "mud-bomb": {
    id: "mud-bomb",
    accuracy: 85,
    damageClass: "special",
    effect:
      "Has a $effect_chance% chance to lower the target's accuracy by one stage.",
    effectChance: 30,
    learnedBy: [23, 24, 50, 51, 54, 60, 61, 88, 89, 113],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage+lower",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 30,
    },
    name: "Mud Bomb",
    power: 65,
    pp: 10,
    priority: 0,
    type: "ground",
  },
  "psycho-cut": {
    id: "psycho-cut",
    accuracy: 100,
    damageClass: "physical",
    effect: "Has an increased chance for a critical hit.",
    effectChance: null,
    learnedBy: [64, 65, 96, 103, 121, 123, 124, 141, 150, 151],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 1,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Psycho Cut",
    power: 70,
    pp: 20,
    priority: 0,
    type: "psychic",
  },
  "zen-headbutt": {
    id: "zen-headbutt",
    accuracy: 90,
    damageClass: "physical",
    effect: "Has a $effect_chance% chance to make the target flinch.",
    effectChance: 20,
    learnedBy: [
      7, 8, 9, 19, 20, 35, 36, 37, 38, 39, 40, 41, 42, 48, 49, 54, 55, 63, 64,
      65, 79, 80, 88, 89, 96, 97, 103, 108, 113, 122, 124, 128, 131, 137, 143,
      150, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 20,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Zen Headbutt",
    power: 80,
    pp: 15,
    priority: 0,
    type: "psychic",
  },
  "mirror-shot": {
    id: "mirror-shot",
    accuracy: 85,
    damageClass: "special",
    effect:
      "Has a $effect_chance% chance to lower the target's accuracy by one stage.",
    effectChance: 30,
    learnedBy: [81, 82],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage+lower",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 30,
    },
    name: "Mirror Shot",
    power: 65,
    pp: 10,
    priority: 0,
    type: "steel",
  },
  "flash-cannon": {
    id: "flash-cannon",
    accuracy: 100,
    damageClass: "special",
    effect:
      "Has a $effect_chance% chance to lower the target's Special Defense by one stage.",
    effectChance: 10,
    learnedBy: [9, 81, 82, 95, 116, 117, 120, 121, 151],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage+lower",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 10,
    },
    name: "Flash Cannon",
    power: 80,
    pp: 10,
    priority: 0,
    type: "steel",
  },
  "rock-climb": {
    id: "rock-climb",
    accuracy: 85,
    damageClass: "physical",
    effect: "Has a $effect_chance% chance to confuse the target.",
    effectChance: 20,
    learnedBy: [
      3, 9, 27, 28, 31, 34, 55, 56, 57, 59, 62, 66, 67, 68, 74, 75, 76, 95, 104,
      105, 106, 107, 108, 111, 112, 113, 115, 125, 126, 127, 128, 139, 141, 143,
      150, 151,
    ],
    meta: {
      ailment: "confusion",
      ailmentChance: 20,
      category: "damage+ailment",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: 5,
      minHits: null,
      minTurns: 2,
      statChance: 0,
    },
    name: "Rock Climb",
    power: 90,
    pp: 20,
    priority: 0,
    type: "normal",
  },
  defog: {
    id: "defog",
    accuracy: null,
    damageClass: "status",
    effect:
      "Lowers the target's evasion by one stage.  Removes field effects from the enemy field.",
    effectChance: null,
    learnedBy: [
      6, 12, 15, 16, 17, 18, 21, 22, 41, 42, 49, 83, 123, 142, 144, 145, 146,
      149, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "unique",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Defog",
    power: null,
    pp: 15,
    priority: 0,
    type: "flying",
  },
  "trick-room": {
    id: "trick-room",
    accuracy: null,
    damageClass: "status",
    effect: "For five turns, slower Pokémon will act before faster Pokémon.",
    effectChance: null,
    learnedBy: [
      63, 64, 65, 79, 80, 92, 93, 94, 96, 97, 102, 103, 121, 122, 124, 137, 150,
      151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "whole-field-effect",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Trick Room",
    power: null,
    pp: 5,
    priority: -7,
    type: "psychic",
  },
  "draco-meteor": {
    id: "draco-meteor",
    accuracy: 90,
    damageClass: "special",
    effect:
      "Lowers the user's Special Attack by two stages after inflicting damage.",
    effectChance: 100,
    learnedBy: [147, 148, 149, 151],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage+raise",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 100,
    },
    name: "Draco Meteor",
    power: 130,
    pp: 5,
    priority: 0,
    type: "dragon",
  },
  discharge: {
    id: "discharge",
    accuracy: 100,
    damageClass: "special",
    effect: "Has a $effect_chance% chance to paralyze the target.",
    effectChance: 30,
    learnedBy: [25, 26, 81, 82, 100, 101, 125, 135, 137, 145],
    meta: {
      ailment: "paralysis",
      ailmentChance: 30,
      category: "damage+ailment",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Discharge",
    power: 80,
    pp: 15,
    priority: 0,
    type: "electric",
  },
  "lava-plume": {
    id: "lava-plume",
    accuracy: 100,
    damageClass: "special",
    effect: "Has a $effect_chance% chance to burn the target.",
    effectChance: 30,
    learnedBy: [126, 136],
    meta: {
      ailment: "burn",
      ailmentChance: 30,
      category: "damage+ailment",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Lava Plume",
    power: 80,
    pp: 15,
    priority: 0,
    type: "fire",
  },
  "leaf-storm": {
    id: "leaf-storm",
    accuracy: 90,
    damageClass: "special",
    effect:
      "Lowers the user's Special Attack by two stages after inflicting damage.",
    effectChance: 100,
    learnedBy: [1, 2, 3, 71, 102, 103, 114, 151],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage+raise",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 100,
    },
    name: "Leaf Storm",
    power: 130,
    pp: 5,
    priority: 0,
    type: "grass",
  },
  "power-whip": {
    id: "power-whip",
    accuracy: 85,
    damageClass: "physical",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [1, 2, 3, 69, 71, 103, 108, 114, 130, 151],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Power Whip",
    power: 120,
    pp: 10,
    priority: 0,
    type: "grass",
  },
  "rock-wrecker": {
    id: "rock-wrecker",
    accuracy: 90,
    damageClass: "physical",
    effect: "User foregoes its next turn to recharge.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Rock Wrecker",
    power: 150,
    pp: 5,
    priority: 0,
    type: "rock",
  },
  "cross-poison": {
    id: "cross-poison",
    accuracy: 100,
    damageClass: "physical",
    effect:
      "Has an increased chance for a critical hit and a $effect_chance% chance to poison the target.",
    effectChance: 10,
    learnedBy: [46, 47, 68, 72, 73, 123, 141, 151],
    meta: {
      ailment: "poison",
      ailmentChance: 10,
      category: "damage+ailment",
      critRate: 1,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Cross Poison",
    power: 70,
    pp: 20,
    priority: 0,
    type: "poison",
  },
  "gunk-shot": {
    id: "gunk-shot",
    accuracy: 80,
    damageClass: "physical",
    effect: "Has a $effect_chance% chance to poison the target.",
    effectChance: 30,
    learnedBy: [23, 24, 52, 53, 56, 57, 88, 89, 92, 93, 94, 143, 151],
    meta: {
      ailment: "poison",
      ailmentChance: 30,
      category: "damage+ailment",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Gunk Shot",
    power: 120,
    pp: 5,
    priority: 0,
    type: "poison",
  },
  "iron-head": {
    id: "iron-head",
    accuracy: 100,
    damageClass: "physical",
    effect: "Has a $effect_chance% chance to make the target flinch.",
    effectChance: 30,
    learnedBy: [
      59, 76, 81, 82, 95, 104, 105, 128, 130, 131, 142, 143, 147, 148, 149, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 30,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Iron Head",
    power: 80,
    pp: 15,
    priority: 0,
    type: "steel",
  },
  "magnet-bomb": {
    id: "magnet-bomb",
    accuracy: null,
    damageClass: "physical",
    effect: "Never misses.",
    effectChance: null,
    learnedBy: [81, 82],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Magnet Bomb",
    power: 60,
    pp: 20,
    priority: 0,
    type: "steel",
  },
  "stone-edge": {
    id: "stone-edge",
    accuracy: 80,
    damageClass: "physical",
    effect: "Has an increased chance for a critical hit.",
    effectChance: null,
    learnedBy: [
      28, 31, 34, 50, 51, 56, 57, 68, 74, 75, 76, 95, 105, 106, 107, 111, 112,
      127, 128, 130, 139, 140, 141, 142, 149, 150, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 1,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Stone Edge",
    power: 100,
    pp: 5,
    priority: 0,
    type: "rock",
  },
  captivate: {
    id: "captivate",
    accuracy: 100,
    damageClass: "status",
    effect:
      "Lowers the target's Special Attack by two stages if it's the opposite gender.",
    effectChance: null,
    learnedBy: [
      1, 2, 3, 4, 5, 6, 7, 8, 9, 12, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25,
      26, 27, 28, 29, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 40, 41, 42, 43,
      44, 45, 46, 47, 48, 49, 50, 51, 52, 53, 54, 55, 56, 57, 58, 59, 60, 61,
      62, 63, 64, 65, 66, 67, 68, 69, 70, 71, 72, 73, 74, 75, 76, 77, 78, 79,
      80, 83, 84, 85, 86, 87, 88, 89, 90, 91, 92, 93, 94, 95, 96, 97, 98, 99,
      102, 103, 104, 105, 106, 107, 108, 109, 110, 111, 112, 113, 114, 115, 116,
      117, 118, 119, 122, 123, 124, 125, 126, 127, 128, 130, 131, 133, 134, 135,
      136, 138, 139, 140, 141, 142, 143, 147, 148, 149, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "net-good-stats",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Captivate",
    power: null,
    pp: 20,
    priority: 0,
    type: "normal",
  },
  "stealth-rock": {
    id: "stealth-rock",
    accuracy: null,
    damageClass: "status",
    effect: "Causes damage when opposing Pokémon switch in.",
    effectChance: null,
    learnedBy: [
      27, 28, 31, 34, 35, 36, 39, 40, 50, 51, 57, 74, 75, 76, 95, 104, 105, 111,
      112, 113, 127, 138, 139, 140, 141, 142, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "field-effect",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Stealth Rock",
    power: null,
    pp: 20,
    priority: 0,
    type: "rock",
  },
  "grass-knot": {
    id: "grass-knot",
    accuracy: 100,
    damageClass: "special",
    effect:
      "Inflicts more damage to heavier targets, with a maximum of 120 power.",
    effectChance: null,
    learnedBy: [
      1, 2, 3, 19, 20, 25, 26, 35, 36, 39, 40, 43, 44, 45, 46, 47, 55, 63, 64,
      65, 69, 70, 71, 79, 80, 96, 97, 102, 103, 113, 114, 121, 122, 124, 150,
      151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Grass Knot",
    power: null,
    pp: 20,
    priority: 0,
    type: "grass",
  },
  chatter: {
    id: "chatter",
    accuracy: 100,
    damageClass: "special",
    effect:
      "Has a higher chance to confuse the target when the recorded sound is louder.",
    effectChance: 100,
    learnedBy: [],
    meta: {
      ailment: "confusion",
      ailmentChance: 100,
      category: "damage+ailment",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: 5,
      minHits: null,
      minTurns: 2,
      statChance: 0,
    },
    name: "Chatter",
    power: 65,
    pp: 20,
    priority: 0,
    type: "flying",
  },
  judgment: {
    id: "judgment",
    accuracy: 100,
    damageClass: "special",
    effect:
      "If the user is holding a appropriate plate or drive, the damage inflicted will match it.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Judgment",
    power: 100,
    pp: 10,
    priority: 0,
    type: "normal",
  },
  "bug-bite": {
    id: "bug-bite",
    accuracy: 100,
    damageClass: "physical",
    effect: "If target has a berry, inflicts double damage and uses the berry.",
    effectChance: null,
    learnedBy: [10, 11, 12, 13, 14, 15, 46, 47, 48, 49, 70, 71, 123, 127, 151],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Bug Bite",
    power: 60,
    pp: 20,
    priority: 0,
    type: "bug",
  },
  "charge-beam": {
    id: "charge-beam",
    accuracy: 90,
    damageClass: "special",
    effect:
      "Has a $effect_chance% chance to raise the user's Special Attack by one stage.",
    effectChance: 70,
    learnedBy: [
      19, 20, 25, 26, 35, 36, 39, 40, 63, 64, 65, 81, 82, 100, 101, 113, 122,
      125, 135, 137, 145, 150, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage+raise",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 70,
    },
    name: "Charge Beam",
    power: 50,
    pp: 10,
    priority: 0,
    type: "electric",
  },
  "wood-hammer": {
    id: "wood-hammer",
    accuracy: 100,
    damageClass: "physical",
    effect: "User receives 1/3 the damage inflicted in recoil.",
    effectChance: null,
    learnedBy: [103],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: -33,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Wood Hammer",
    power: 120,
    pp: 15,
    priority: 0,
    type: "grass",
  },
  "aqua-jet": {
    id: "aqua-jet",
    accuracy: 100,
    damageClass: "physical",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [7, 9, 55, 86, 87, 140, 141, 147, 149],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Aqua Jet",
    power: 40,
    pp: 20,
    priority: 1,
    type: "water",
  },
  "attack-order": {
    id: "attack-order",
    accuracy: 100,
    damageClass: "physical",
    effect: "Has an increased chance for a critical hit.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 1,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Attack Order",
    power: 90,
    pp: 15,
    priority: 0,
    type: "bug",
  },
  "defend-order": {
    id: "defend-order",
    accuracy: null,
    damageClass: "status",
    effect: "Raises the user's Defense and Special Defense by one stage.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "net-good-stats",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Defend Order",
    power: null,
    pp: 10,
    priority: 0,
    type: "bug",
  },
  "heal-order": {
    id: "heal-order",
    accuracy: null,
    damageClass: "status",
    effect: "Heals the user by half its max HP.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "heal",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 50,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Heal Order",
    power: null,
    pp: 10,
    priority: 0,
    type: "bug",
  },
  "head-smash": {
    id: "head-smash",
    accuracy: 80,
    damageClass: "physical",
    effect: "User receives 1/2 the damage inflicted in recoil.",
    effectChance: null,
    learnedBy: [32, 95],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: -50,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Head Smash",
    power: 150,
    pp: 5,
    priority: 0,
    type: "rock",
  },
  "double-hit": {
    id: "double-hit",
    accuracy: 90,
    damageClass: "physical",
    effect: "Hits twice in one turn.",
    effectChance: null,
    learnedBy: [84, 85, 110, 115, 123, 127],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: 2,
      maxTurns: null,
      minHits: 2,
      minTurns: null,
      statChance: 0,
    },
    name: "Double Hit",
    power: 35,
    pp: 10,
    priority: 0,
    type: "normal",
  },
  "roar-of-time": {
    id: "roar-of-time",
    accuracy: 90,
    damageClass: "special",
    effect: "User foregoes its next turn to recharge.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Roar of Time",
    power: 150,
    pp: 5,
    priority: 0,
    type: "dragon",
  },
  "spacial-rend": {
    id: "spacial-rend",
    accuracy: 95,
    damageClass: "special",
    effect: "Has an increased chance for a critical hit.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 1,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Spacial Rend",
    power: 100,
    pp: 5,
    priority: 0,
    type: "dragon",
  },
  "lunar-dance": {
    id: "lunar-dance",
    accuracy: null,
    damageClass: "status",
    effect: "User faints, and its replacement is fully healed.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "unique",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Lunar Dance",
    power: null,
    pp: 10,
    priority: 0,
    type: "psychic",
  },
  "crush-grip": {
    id: "crush-grip",
    accuracy: 100,
    damageClass: "physical",
    effect:
      "Power increases against targets with more HP remaining, up to a maximum of 121 power.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Crush Grip",
    power: null,
    pp: 5,
    priority: 0,
    type: "normal",
  },
  "magma-storm": {
    id: "magma-storm",
    accuracy: 75,
    damageClass: "special",
    effect:
      "Prevents the target from fleeing and inflicts damage for 2-5 turns.",
    effectChance: 100,
    learnedBy: [],
    meta: {
      ailment: "trap",
      ailmentChance: 100,
      category: "damage+ailment",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: 6,
      minHits: null,
      minTurns: 5,
      statChance: 0,
    },
    name: "Magma Storm",
    power: 100,
    pp: 5,
    priority: 0,
    type: "fire",
  },
  "dark-void": {
    id: "dark-void",
    accuracy: 50,
    damageClass: "status",
    effect: "Puts the target to sleep.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "sleep",
      ailmentChance: 0,
      category: "ailment",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: 4,
      minHits: null,
      minTurns: 2,
      statChance: 0,
    },
    name: "Dark Void",
    power: null,
    pp: 10,
    priority: 0,
    type: "dark",
  },
  "seed-flare": {
    id: "seed-flare",
    accuracy: 85,
    damageClass: "special",
    effect:
      "Has a $effect_chance% chance to lower the target's Special Defense by two stages.",
    effectChance: 40,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage+lower",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 40,
    },
    name: "Seed Flare",
    power: 120,
    pp: 5,
    priority: 0,
    type: "grass",
  },
  "ominous-wind": {
    id: "ominous-wind",
    accuracy: 100,
    damageClass: "special",
    effect:
      "Has a $effect_chance% chance to raise all of the user's stats by one stage.",
    effectChance: 10,
    learnedBy: [
      6, 12, 15, 16, 17, 18, 21, 22, 37, 38, 41, 42, 49, 83, 92, 93, 94, 123,
      142, 144, 145, 146, 149, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 10,
      category: "damage+raise",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 10,
    },
    name: "Ominous Wind",
    power: 60,
    pp: 5,
    priority: 0,
    type: "ghost",
  },
  "shadow-force": {
    id: "shadow-force",
    accuracy: 100,
    damageClass: "physical",
    effect:
      "User vanishes, dodging all attacks, and hits next turn.  Hits through Protect and Detect.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Shadow Force",
    power: 120,
    pp: 5,
    priority: 0,
    type: "ghost",
  },
  "hone-claws": {
    id: "hone-claws",
    accuracy: null,
    damageClass: "status",
    effect: "Raises the user's Attack and accuracy by one stage.",
    effectChance: null,
    learnedBy: [
      4, 5, 6, 27, 28, 29, 30, 31, 32, 33, 34, 46, 47, 50, 51, 52, 53, 54, 55,
      56, 57, 98, 99, 140, 141, 142, 149, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "net-good-stats",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Hone Claws",
    power: null,
    pp: 15,
    priority: 0,
    type: "dark",
  },
  "wide-guard": {
    id: "wide-guard",
    accuracy: null,
    damageClass: "status",
    effect:
      "Prevents any multi-target moves from hitting friendly Pokémon this turn.",
    effectChance: null,
    learnedBy: [46, 68, 74, 95, 99, 106, 122, 142],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "field-effect",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Wide Guard",
    power: null,
    pp: 10,
    priority: 3,
    type: "rock",
  },
  "guard-split": {
    id: "guard-split",
    accuracy: null,
    damageClass: "status",
    effect: "Averages Defense and Special Defense with the target.",
    effectChance: null,
    learnedBy: [63, 111],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "unique",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Guard Split",
    power: null,
    pp: 10,
    priority: 0,
    type: "psychic",
  },
  "power-split": {
    id: "power-split",
    accuracy: null,
    damageClass: "status",
    effect: "Averages Attack and Special Attack with the target.",
    effectChance: null,
    learnedBy: [96, 122],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "unique",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Power Split",
    power: null,
    pp: 10,
    priority: 0,
    type: "psychic",
  },
  "wonder-room": {
    id: "wonder-room",
    accuracy: null,
    damageClass: "status",
    effect:
      "All Pokémon's Defense and Special Defense are swapped for 5 turns.",
    effectChance: null,
    learnedBy: [
      35, 36, 54, 55, 63, 64, 65, 79, 80, 92, 93, 94, 121, 122, 124, 137, 150,
      151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "whole-field-effect",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Wonder Room",
    power: null,
    pp: 10,
    priority: 0,
    type: "psychic",
  },
  psyshock: {
    id: "psyshock",
    accuracy: 100,
    damageClass: "special",
    effect:
      "Inflicts damage based on the target's Defense, not Special Defense.",
    effectChance: null,
    learnedBy: [
      35, 36, 38, 39, 40, 54, 55, 63, 64, 65, 79, 80, 96, 97, 103, 121, 122,
      124, 137, 150, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Psyshock",
    power: 80,
    pp: 10,
    priority: 0,
    type: "psychic",
  },
  venoshock: {
    id: "venoshock",
    accuracy: 100,
    damageClass: "special",
    effect: "Inflicts double damage if the target is Poisoned.",
    effectChance: null,
    learnedBy: [
      1, 2, 3, 12, 15, 23, 24, 29, 30, 31, 32, 33, 34, 41, 42, 43, 44, 45, 46,
      47, 48, 49, 69, 70, 71, 72, 73, 88, 89, 92, 93, 94, 109, 110, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Venoshock",
    power: 65,
    pp: 10,
    priority: 0,
    type: "poison",
  },
  autotomize: {
    id: "autotomize",
    accuracy: null,
    damageClass: "status",
    effect:
      "Raises the user's Speed by two stages and halves the user's weight.",
    effectChance: null,
    learnedBy: [74],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "net-good-stats",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Autotomize",
    power: null,
    pp: 15,
    priority: 0,
    type: "steel",
  },
  "rage-powder": {
    id: "rage-powder",
    accuracy: null,
    damageClass: "status",
    effect:
      "Redirects the target's single-target effects to the user for this turn.",
    effectChance: null,
    learnedBy: [12, 46, 47, 48, 114],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "unique",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Rage Powder",
    power: null,
    pp: 20,
    priority: 2,
    type: "bug",
  },
  telekinesis: {
    id: "telekinesis",
    accuracy: null,
    damageClass: "status",
    effect: "Moves have 100% accuracy against the target for three turns.",
    effectChance: null,
    learnedBy: [
      35, 36, 39, 40, 54, 55, 63, 64, 65, 79, 80, 92, 93, 94, 96, 97, 101, 102,
      103, 113, 121, 122, 124, 137, 150, 151,
    ],
    meta: {
      ailment: "unknown",
      ailmentChance: 0,
      category: "ailment",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: 3,
      minHits: null,
      minTurns: 3,
      statChance: 0,
    },
    name: "Telekinesis",
    power: null,
    pp: 15,
    priority: 0,
    type: "psychic",
  },
  "magic-room": {
    id: "magic-room",
    accuracy: null,
    damageClass: "status",
    effect: "Negates held items for five turns.",
    effectChance: null,
    learnedBy: [40, 63, 64, 65, 96, 97, 122, 124, 150, 151],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "whole-field-effect",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Magic Room",
    power: null,
    pp: 10,
    priority: 0,
    type: "psychic",
  },
  "smack-down": {
    id: "smack-down",
    accuracy: 100,
    damageClass: "physical",
    effect: "Removes any immunity to Ground damage.",
    effectChance: 100,
    learnedBy: [
      9, 31, 34, 56, 57, 66, 67, 68, 74, 75, 76, 95, 104, 105, 111, 112, 127,
      138, 139, 140, 141, 142, 143, 151,
    ],
    meta: {
      ailment: "unknown",
      ailmentChance: 100,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Smack Down",
    power: 50,
    pp: 15,
    priority: 0,
    type: "rock",
  },
  "storm-throw": {
    id: "storm-throw",
    accuracy: 100,
    damageClass: "physical",
    effect: "Always scores a critical hit.",
    effectChance: null,
    learnedBy: [127],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 6,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Storm Throw",
    power: 60,
    pp: 10,
    priority: 0,
    type: "fighting",
  },
  "flame-burst": {
    id: "flame-burst",
    accuracy: 100,
    damageClass: "special",
    effect: "Deals splash damage to Pokémon next to the target.",
    effectChance: null,
    learnedBy: [4, 5, 6, 37, 58, 126],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Flame Burst",
    power: 70,
    pp: 15,
    priority: 0,
    type: "fire",
  },
  "sludge-wave": {
    id: "sludge-wave",
    accuracy: 100,
    damageClass: "special",
    effect: "Has a $effect_chance% chance to poison the target.",
    effectChance: 10,
    learnedBy: [
      23, 24, 31, 34, 45, 51, 72, 73, 88, 89, 92, 93, 94, 109, 110, 151,
    ],
    meta: {
      ailment: "poison",
      ailmentChance: 10,
      category: "damage+ailment",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Sludge Wave",
    power: 95,
    pp: 10,
    priority: 0,
    type: "poison",
  },
  "quiver-dance": {
    id: "quiver-dance",
    accuracy: null,
    damageClass: "status",
    effect:
      "Raises the user's Special Attack, Special Defense, and Speed by one stage each.",
    effectChance: null,
    learnedBy: [12, 49],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "net-good-stats",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Quiver Dance",
    power: null,
    pp: 20,
    priority: 0,
    type: "bug",
  },
  "heavy-slam": {
    id: "heavy-slam",
    accuracy: 100,
    damageClass: "physical",
    effect:
      "Power is higher when the user weighs more than the target, up to a maximum of 120.",
    effectChance: null,
    learnedBy: [66, 67, 68, 76, 81, 82, 95, 112, 143, 151],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Heavy Slam",
    power: null,
    pp: 10,
    priority: 0,
    type: "steel",
  },
  synchronoise: {
    id: "synchronoise",
    accuracy: 100,
    damageClass: "special",
    effect: "Hits any Pokémon that shares a type with the user.",
    effectChance: null,
    learnedBy: [54, 96, 97, 133],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Synchronoise",
    power: 120,
    pp: 10,
    priority: 0,
    type: "psychic",
  },
  "electro-ball": {
    id: "electro-ball",
    accuracy: 100,
    damageClass: "special",
    effect:
      "Power is higher when the user has greater Speed than the target, up to a maximum of 150.",
    effectChance: null,
    learnedBy: [25, 26, 81, 82, 100, 101, 125, 135, 145, 150, 151],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Electro Ball",
    power: null,
    pp: 10,
    priority: 0,
    type: "electric",
  },
  soak: {
    id: "soak",
    accuracy: 100,
    damageClass: "status",
    effect: "Changes the target's type to Water.",
    effectChance: null,
    learnedBy: [54, 55, 118, 119],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "unique",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Soak",
    power: null,
    pp: 20,
    priority: 0,
    type: "water",
  },
  "flame-charge": {
    id: "flame-charge",
    accuracy: 100,
    damageClass: "physical",
    effect: "Inflicts regular damage.  Raises the user's Speed by one stage.",
    effectChance: 100,
    learnedBy: [4, 5, 6, 37, 38, 58, 59, 77, 78, 126, 136, 146, 151],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage+raise",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 100,
    },
    name: "Flame Charge",
    power: 50,
    pp: 20,
    priority: 0,
    type: "fire",
  },
  coil: {
    id: "coil",
    accuracy: null,
    damageClass: "status",
    effect:
      "Raises the user's Attack, Defense, and accuracy by one stage each.",
    effectChance: null,
    learnedBy: [23, 24],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "net-good-stats",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Coil",
    power: null,
    pp: 20,
    priority: 0,
    type: "poison",
  },
  "low-sweep": {
    id: "low-sweep",
    accuracy: 100,
    damageClass: "physical",
    effect: "Lowers the target's Speed by one stage.",
    effectChance: 100,
    learnedBy: [
      54, 55, 56, 57, 62, 66, 67, 68, 96, 97, 106, 107, 125, 126, 150, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage+lower",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 100,
    },
    name: "Low Sweep",
    power: 65,
    pp: 20,
    priority: 0,
    type: "fighting",
  },
  "acid-spray": {
    id: "acid-spray",
    accuracy: 100,
    damageClass: "special",
    effect: "Lowers the target's Special Defense by two stages.",
    effectChance: 100,
    learnedBy: [23, 24, 48, 49, 69, 72, 73, 88, 89, 92, 93, 94, 151],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage+lower",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 100,
    },
    name: "Acid Spray",
    power: 40,
    pp: 20,
    priority: 0,
    type: "poison",
  },
  "foul-play": {
    id: "foul-play",
    accuracy: 100,
    damageClass: "physical",
    effect: "Calculates damage with the target's attacking stat.",
    effectChance: null,
    learnedBy: [
      37, 38, 50, 51, 52, 53, 63, 64, 65, 79, 80, 92, 93, 94, 96, 97, 100, 101,
      122, 137, 150, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Foul Play",
    power: 95,
    pp: 15,
    priority: 0,
    type: "dark",
  },
  "simple-beam": {
    id: "simple-beam",
    accuracy: 100,
    damageClass: "status",
    effect: "Changes the target's ability to Simple.",
    effectChance: null,
    learnedBy: [54, 83],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "unique",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Simple Beam",
    power: null,
    pp: 15,
    priority: 0,
    type: "normal",
  },
  entrainment: {
    id: "entrainment",
    accuracy: 100,
    damageClass: "status",
    effect: "Copies the user's ability onto the target.",
    effectChance: null,
    learnedBy: [86],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "unique",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Entrainment",
    power: null,
    pp: 15,
    priority: 0,
    type: "normal",
  },
  "after-you": {
    id: "after-you",
    accuracy: null,
    damageClass: "status",
    effect: "Makes the target act next this turn.",
    effectChance: null,
    learnedBy: [35, 36, 43, 44, 45, 46, 47, 79, 80, 143, 151],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "unique",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "After You",
    power: null,
    pp: 15,
    priority: 0,
    type: "normal",
  },
  round: {
    id: "round",
    accuracy: 100,
    damageClass: "special",
    effect: "Has double power if it's used more than once per turn.",
    effectChance: null,
    learnedBy: [
      1, 2, 3, 4, 5, 6, 7, 8, 9, 12, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25,
      26, 27, 28, 29, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 40, 41, 42, 43,
      44, 45, 46, 47, 48, 49, 50, 51, 52, 53, 54, 55, 56, 57, 58, 59, 60, 61,
      62, 63, 64, 65, 66, 67, 68, 69, 70, 71, 72, 73, 74, 75, 76, 77, 78, 79,
      80, 81, 82, 83, 84, 85, 86, 87, 88, 89, 90, 91, 92, 93, 94, 95, 96, 97,
      98, 99, 100, 101, 102, 103, 104, 105, 106, 107, 108, 109, 110, 111, 112,
      113, 114, 115, 116, 117, 118, 119, 120, 121, 122, 123, 124, 125, 126, 127,
      128, 130, 131, 133, 134, 135, 136, 137, 138, 139, 140, 141, 142, 143, 144,
      145, 146, 147, 148, 149, 150, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Round",
    power: 60,
    pp: 15,
    priority: 0,
    type: "normal",
  },
  "echoed-voice": {
    id: "echoed-voice",
    accuracy: 100,
    damageClass: "special",
    effect:
      "Power increases by 100% for each consecutive use by any friendly Pokémon, to a maximum of 200.",
    effectChance: null,
    learnedBy: [
      1, 2, 3, 4, 5, 6, 21, 22, 25, 26, 29, 30, 31, 32, 33, 34, 35, 36, 39, 40,
      50, 51, 52, 53, 77, 78, 79, 80, 84, 85, 86, 87, 104, 105, 113, 124, 131,
      133, 134, 135, 136, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Echoed Voice",
    power: 40,
    pp: 15,
    priority: 0,
    type: "normal",
  },
  "chip-away": {
    id: "chip-away",
    accuracy: 100,
    damageClass: "physical",
    effect: "Ignores the target's stat modifiers.",
    effectChance: null,
    learnedBy: [27, 29, 31, 32, 34, 98, 104, 108, 111, 112, 115, 143],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Chip Away",
    power: 70,
    pp: 20,
    priority: 0,
    type: "normal",
  },
  "clear-smog": {
    id: "clear-smog",
    accuracy: null,
    damageClass: "special",
    effect: "Removes all of the target's stat modifiers.",
    effectChance: null,
    learnedBy: [54, 69, 71, 92, 109, 110, 116, 117, 126],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Clear Smog",
    power: 50,
    pp: 15,
    priority: 0,
    type: "poison",
  },
  "stored-power": {
    id: "stored-power",
    accuracy: 100,
    damageClass: "special",
    effect:
      "Power is higher the more the user's stats have been raised, to a maximum of 31×.",
    effectChance: null,
    learnedBy: [
      35, 36, 38, 39, 40, 65, 79, 80, 96, 97, 113, 122, 124, 133, 134, 135, 136,
      150, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Stored Power",
    power: 20,
    pp: 10,
    priority: 0,
    type: "psychic",
  },
  "quick-guard": {
    id: "quick-guard",
    accuracy: null,
    damageClass: "status",
    effect:
      "Prevents any priority moves from hitting friendly Pokémon this turn.",
    effectChance: null,
    learnedBy: [41, 42, 66, 107, 122, 123],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "field-effect",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Quick Guard",
    power: null,
    pp: 15,
    priority: 3,
    type: "fighting",
  },
  "ally-switch": {
    id: "ally-switch",
    accuracy: null,
    damageClass: "status",
    effect: "User switches places with the friendly Pokémon opposite it.",
    effectChance: null,
    learnedBy: [
      35, 36, 39, 40, 50, 51, 63, 64, 65, 77, 78, 92, 93, 94, 96, 97, 98, 99,
      113, 121, 122, 124, 137, 150, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "unique",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Ally Switch",
    power: null,
    pp: 15,
    priority: 2,
    type: "psychic",
  },
  scald: {
    id: "scald",
    accuracy: 100,
    damageClass: "special",
    effect: "Has a $effect_chance% chance to burn the target.",
    effectChance: 30,
    learnedBy: [
      7, 8, 9, 54, 55, 60, 61, 62, 72, 73, 79, 80, 98, 99, 116, 117, 118, 119,
      120, 121, 130, 134, 138, 139, 140, 141, 151,
    ],
    meta: {
      ailment: "burn",
      ailmentChance: 30,
      category: "damage+ailment",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Scald",
    power: 80,
    pp: 15,
    priority: 0,
    type: "water",
  },
  "shell-smash": {
    id: "shell-smash",
    accuracy: null,
    damageClass: "status",
    effect:
      "Raises user's Attack, Special Attack, and Speed by two stages.  Lower user's Defense and Special Defense by one stage.",
    effectChance: null,
    learnedBy: [7, 8, 9, 90, 91, 138, 139],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "unique",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Shell Smash",
    power: null,
    pp: 15,
    priority: 0,
    type: "normal",
  },
  "heal-pulse": {
    id: "heal-pulse",
    accuracy: null,
    damageClass: "status",
    effect: "Heals the target for half its max HP.",
    effectChance: null,
    learnedBy: [79, 80, 113],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "heal",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 50,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Heal Pulse",
    power: null,
    pp: 10,
    priority: 0,
    type: "psychic",
  },
  hex: {
    id: "hex",
    accuracy: 100,
    damageClass: "special",
    effect: "Has double power if the target has a major status ailment.",
    effectChance: null,
    learnedBy: [31, 34, 37, 38, 72, 73, 88, 89, 92, 93, 94, 97, 150, 151],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Hex",
    power: 65,
    pp: 10,
    priority: 0,
    type: "ghost",
  },
  "sky-drop": {
    id: "sky-drop",
    accuracy: 100,
    damageClass: "physical",
    effect:
      "Carries the target high into the air, dodging all attacks against either, and drops it next turn.",
    effectChance: null,
    learnedBy: [6, 142, 144, 145, 146, 149, 151],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Sky Drop",
    power: 60,
    pp: 10,
    priority: 0,
    type: "flying",
  },
  "shift-gear": {
    id: "shift-gear",
    accuracy: null,
    damageClass: "status",
    effect:
      "Raises the user's Attack by one stage and its Speed by two stages.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "net-good-stats",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Shift Gear",
    power: null,
    pp: 10,
    priority: 0,
    type: "steel",
  },
  "circle-throw": {
    id: "circle-throw",
    accuracy: 90,
    damageClass: "physical",
    effect: "Ends wild battles.  Forces trainers to switch Pokémon.",
    effectChance: null,
    learnedBy: [62, 115],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Circle Throw",
    power: 60,
    pp: 10,
    priority: -6,
    type: "fighting",
  },
  incinerate: {
    id: "incinerate",
    accuracy: 100,
    damageClass: "special",
    effect: "Destroys the target's held berry.",
    effectChance: null,
    learnedBy: [
      4, 5, 6, 31, 34, 35, 36, 37, 38, 39, 40, 58, 59, 66, 67, 68, 74, 75, 76,
      77, 78, 79, 80, 88, 89, 104, 105, 108, 109, 110, 111, 112, 113, 115, 126,
      128, 130, 136, 142, 143, 146, 147, 148, 149, 150, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Incinerate",
    power: 60,
    pp: 15,
    priority: 0,
    type: "fire",
  },
  quash: {
    id: "quash",
    accuracy: 100,
    damageClass: "status",
    effect: "Makes the target act last this turn.",
    effectChance: null,
    learnedBy: [31, 34, 99, 151],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "unique",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Quash",
    power: null,
    pp: 15,
    priority: 0,
    type: "dark",
  },
  acrobatics: {
    id: "acrobatics",
    accuracy: 100,
    damageClass: "physical",
    effect: "Has double power if the user has no held item.",
    effectChance: null,
    learnedBy: [4, 5, 6, 12, 15, 41, 42, 49, 56, 57, 83, 123, 145, 146, 151],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Acrobatics",
    power: 55,
    pp: 15,
    priority: 0,
    type: "flying",
  },
  "reflect-type": {
    id: "reflect-type",
    accuracy: null,
    damageClass: "status",
    effect: "User becomes the target's type.",
    effectChance: null,
    learnedBy: [73, 92, 94, 120, 138, 151],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "unique",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Reflect Type",
    power: null,
    pp: 15,
    priority: 0,
    type: "normal",
  },
  retaliate: {
    id: "retaliate",
    accuracy: 100,
    damageClass: "physical",
    effect: "Has double power if a friendly Pokémon fainted last turn.",
    effectChance: null,
    learnedBy: [
      19, 20, 35, 36, 39, 40, 52, 53, 56, 57, 58, 59, 66, 67, 68, 83, 104, 105,
      106, 107, 108, 113, 115, 128, 133, 134, 135, 136, 143, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Retaliate",
    power: 70,
    pp: 5,
    priority: 0,
    type: "normal",
  },
  "final-gambit": {
    id: "final-gambit",
    accuracy: 100,
    damageClass: "special",
    effect: "Inflicts damage equal to the user's remaining HP.  User faints.",
    effectChance: null,
    learnedBy: [19, 50, 56, 57, 83],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Final Gambit",
    power: null,
    pp: 5,
    priority: 0,
    type: "fighting",
  },
  bestow: {
    id: "bestow",
    accuracy: null,
    damageClass: "status",
    effect: "Gives the user's held item to the target.",
    effectChance: null,
    learnedBy: [35, 102, 113],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "unique",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Bestow",
    power: null,
    pp: 15,
    priority: 0,
    type: "normal",
  },
  inferno: {
    id: "inferno",
    accuracy: 50,
    damageClass: "special",
    effect: "Has a $effect_chance% chance to burn the target.",
    effectChance: 100,
    learnedBy: [4, 5, 6, 37, 38, 77, 78],
    meta: {
      ailment: "burn",
      ailmentChance: 100,
      category: "damage+ailment",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Inferno",
    power: 100,
    pp: 5,
    priority: 0,
    type: "fire",
  },
  "water-pledge": {
    id: "water-pledge",
    accuracy: 100,
    damageClass: "special",
    effect:
      "With Grass Pledge, halves opposing Pokémon's Speed for four turns.",
    effectChance: null,
    learnedBy: [7, 8, 9, 151],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Water Pledge",
    power: 80,
    pp: 10,
    priority: 0,
    type: "water",
  },
  "fire-pledge": {
    id: "fire-pledge",
    accuracy: 100,
    damageClass: "special",
    effect:
      "With Water Pledge, doubles the effect chance of friendly Pokémon's moves for four turns.",
    effectChance: null,
    learnedBy: [4, 5, 6, 151],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Fire Pledge",
    power: 80,
    pp: 10,
    priority: 0,
    type: "fire",
  },
  "grass-pledge": {
    id: "grass-pledge",
    accuracy: 100,
    damageClass: "special",
    effect:
      "With Fire Pledge, damages opposing Pokémon for 1/8 their max HP every turn for four turns.",
    effectChance: null,
    learnedBy: [1, 2, 3, 151],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Grass Pledge",
    power: 80,
    pp: 10,
    priority: 0,
    type: "grass",
  },
  "volt-switch": {
    id: "volt-switch",
    accuracy: 100,
    damageClass: "special",
    effect: "User must switch out after attacking.",
    effectChance: null,
    learnedBy: [25, 26, 81, 82, 100, 101, 125, 135, 145, 151],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Volt Switch",
    power: 70,
    pp: 20,
    priority: 0,
    type: "electric",
  },
  "struggle-bug": {
    id: "struggle-bug",
    accuracy: 100,
    damageClass: "special",
    effect:
      "Has a $effect_chance% chance to lower the target's Special Attack by one stage.",
    effectChance: 100,
    learnedBy: [12, 15, 46, 47, 48, 49, 123, 127, 151],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage+lower",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 100,
    },
    name: "Struggle Bug",
    power: 50,
    pp: 20,
    priority: 0,
    type: "bug",
  },
  bulldoze: {
    id: "bulldoze",
    accuracy: 100,
    damageClass: "physical",
    effect:
      "Has a $effect_chance% chance to lower the target's Speed by one stage.",
    effectChance: 100,
    learnedBy: [
      3, 6, 9, 23, 24, 27, 28, 31, 34, 50, 51, 56, 57, 59, 60, 61, 62, 66, 67,
      68, 74, 75, 76, 79, 80, 95, 103, 104, 105, 106, 107, 108, 111, 112, 113,
      115, 127, 128, 130, 131, 142, 143, 149, 150, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage+lower",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 100,
    },
    name: "Bulldoze",
    power: 60,
    pp: 20,
    priority: 0,
    type: "ground",
  },
  "frost-breath": {
    id: "frost-breath",
    accuracy: 90,
    damageClass: "special",
    effect: "Always scores a critical hit.",
    effectChance: 100,
    learnedBy: [87, 91, 124, 131, 144, 151],
    meta: {
      ailment: "none",
      ailmentChance: 100,
      category: "damage",
      critRate: 6,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Frost Breath",
    power: 60,
    pp: 10,
    priority: 0,
    type: "ice",
  },
  "dragon-tail": {
    id: "dragon-tail",
    accuracy: 90,
    damageClass: "physical",
    effect: "Ends wild battles.  Forces trainers to switch Pokémon.",
    effectChance: null,
    learnedBy: [4, 5, 6, 9, 24, 31, 34, 95, 108, 112, 130, 147, 148, 149, 151],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Dragon Tail",
    power: 60,
    pp: 10,
    priority: -6,
    type: "dragon",
  },
  "work-up": {
    id: "work-up",
    accuracy: null,
    damageClass: "status",
    effect: "Raises the user's Attack and Special Attack by one stage each.",
    effectChance: null,
    learnedBy: [
      1, 2, 3, 4, 5, 6, 7, 8, 9, 16, 17, 18, 19, 20, 21, 22, 27, 28, 35, 36, 39,
      40, 50, 51, 52, 53, 56, 57, 62, 66, 67, 68, 83, 84, 85, 106, 107, 108,
      113, 115, 128, 133, 134, 135, 136, 143, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "net-good-stats",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Work Up",
    power: null,
    pp: 30,
    priority: 0,
    type: "normal",
  },
  electroweb: {
    id: "electroweb",
    accuracy: 95,
    damageClass: "special",
    effect: "Lowers the target's Speed by one stage.",
    effectChance: 100,
    learnedBy: [10, 11, 12, 13, 14, 15, 25, 26, 81, 82, 125, 137, 151],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage+lower",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 100,
    },
    name: "Electroweb",
    power: 55,
    pp: 15,
    priority: 0,
    type: "electric",
  },
  "wild-charge": {
    id: "wild-charge",
    accuracy: 100,
    damageClass: "physical",
    effect: "User receives 1/4 the damage it inflicts in recoil.",
    effectChance: null,
    learnedBy: [
      19, 20, 25, 26, 39, 40, 58, 59, 77, 78, 81, 82, 100, 101, 113, 125, 128,
      135, 143, 145, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: -25,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Wild Charge",
    power: 90,
    pp: 15,
    priority: 0,
    type: "electric",
  },
  "drill-run": {
    id: "drill-run",
    accuracy: 95,
    damageClass: "physical",
    effect: "Has an increased chance for a critical hit.",
    effectChance: null,
    learnedBy: [
      15, 21, 22, 28, 31, 32, 33, 34, 78, 86, 87, 91, 95, 111, 112, 118, 119,
      131, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 1,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Drill Run",
    power: 80,
    pp: 10,
    priority: 0,
    type: "ground",
  },
  "dual-chop": {
    id: "dual-chop",
    accuracy: 90,
    damageClass: "physical",
    effect: "Hits twice in one turn.",
    effectChance: null,
    learnedBy: [56, 57, 62, 66, 67, 68, 125, 126, 151],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: 2,
      maxTurns: null,
      minHits: 2,
      minTurns: null,
      statChance: 0,
    },
    name: "Dual Chop",
    power: 40,
    pp: 15,
    priority: 0,
    type: "dragon",
  },
  "heart-stamp": {
    id: "heart-stamp",
    accuracy: 100,
    damageClass: "physical",
    effect: "Has a $effect_chance% chance to make the target flinch.",
    effectChance: 30,
    learnedBy: [124],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 30,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Heart Stamp",
    power: 60,
    pp: 25,
    priority: 0,
    type: "psychic",
  },
  "horn-leech": {
    id: "horn-leech",
    accuracy: 100,
    damageClass: "physical",
    effect: "Drains half the damage inflicted to heal the user.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage+heal",
      critRate: 0,
      drain: 50,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Horn Leech",
    power: 75,
    pp: 10,
    priority: 0,
    type: "grass",
  },
  "sacred-sword": {
    id: "sacred-sword",
    accuracy: 100,
    damageClass: "physical",
    effect: "Ignores the target's stat modifiers.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Sacred Sword",
    power: 90,
    pp: 15,
    priority: 0,
    type: "fighting",
  },
  "razor-shell": {
    id: "razor-shell",
    accuracy: 95,
    damageClass: "physical",
    effect:
      "Has a $effect_chance% chance to lower the target's Defense by one stage.",
    effectChance: 50,
    learnedBy: [80, 90, 91, 98, 99, 141, 151],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage+lower",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 50,
    },
    name: "Razor Shell",
    power: 75,
    pp: 10,
    priority: 0,
    type: "water",
  },
  "heat-crash": {
    id: "heat-crash",
    accuracy: 100,
    damageClass: "physical",
    effect:
      "Power is higher when the user weighs more than the target, up to a maximum of 120.",
    effectChance: null,
    learnedBy: [6, 112, 143, 151],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Heat Crash",
    power: null,
    pp: 10,
    priority: 0,
    type: "fire",
  },
  "leaf-tornado": {
    id: "leaf-tornado",
    accuracy: 90,
    damageClass: "special",
    effect:
      "Has a $effect_chance% chance to lower the target's accuracy by one stage.",
    effectChance: 50,
    learnedBy: [71],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage+lower",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 50,
    },
    name: "Leaf Tornado",
    power: 65,
    pp: 10,
    priority: 0,
    type: "grass",
  },
  steamroller: {
    id: "steamroller",
    accuracy: 100,
    damageClass: "physical",
    effect: "Has a $effect_chance% chance to make the target flinch.",
    effectChance: 30,
    learnedBy: [76],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 30,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Steamroller",
    power: 65,
    pp: 20,
    priority: 0,
    type: "bug",
  },
  "cotton-guard": {
    id: "cotton-guard",
    accuracy: null,
    damageClass: "status",
    effect: "Raises the user's Defense by three stages.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "net-good-stats",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Cotton Guard",
    power: null,
    pp: 10,
    priority: 0,
    type: "grass",
  },
  "night-daze": {
    id: "night-daze",
    accuracy: 95,
    damageClass: "special",
    effect:
      "Has a $effect_chance% chance to lower the target's accuracy by one stage.",
    effectChance: 40,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage+lower",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 40,
    },
    name: "Night Daze",
    power: 85,
    pp: 10,
    priority: 0,
    type: "dark",
  },
  psystrike: {
    id: "psystrike",
    accuracy: 100,
    damageClass: "special",
    effect:
      "Inflicts damage based on the target's Defense, not Special Defense.",
    effectChance: null,
    learnedBy: [150],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Psystrike",
    power: 100,
    pp: 10,
    priority: 0,
    type: "psychic",
  },
  "tail-slap": {
    id: "tail-slap",
    accuracy: 85,
    damageClass: "physical",
    effect: "Hits 2-5 times in one turn.",
    effectChance: null,
    learnedBy: [37, 38, 151],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: 5,
      maxTurns: null,
      minHits: 2,
      minTurns: null,
      statChance: 0,
    },
    name: "Tail Slap",
    power: 25,
    pp: 10,
    priority: 0,
    type: "normal",
  },
  hurricane: {
    id: "hurricane",
    accuracy: 70,
    damageClass: "special",
    effect: "Has a $effect_chance% chance to confuse the target.",
    effectChance: 30,
    learnedBy: [6, 12, 16, 17, 18, 130, 142, 144, 145, 146, 149, 150, 151],
    meta: {
      ailment: "confusion",
      ailmentChance: 30,
      category: "damage+ailment",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: 5,
      minHits: null,
      minTurns: 2,
      statChance: 0,
    },
    name: "Hurricane",
    power: 110,
    pp: 10,
    priority: 0,
    type: "flying",
  },
  "head-charge": {
    id: "head-charge",
    accuracy: 100,
    damageClass: "physical",
    effect: "User receives 1/4 the damage it inflicts in recoil.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: -25,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Head Charge",
    power: 120,
    pp: 15,
    priority: 0,
    type: "normal",
  },
  "gear-grind": {
    id: "gear-grind",
    accuracy: 85,
    damageClass: "physical",
    effect: "Hits twice in one turn.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: 2,
      maxTurns: null,
      minHits: 2,
      minTurns: null,
      statChance: 0,
    },
    name: "Gear Grind",
    power: 50,
    pp: 15,
    priority: 0,
    type: "steel",
  },
  "searing-shot": {
    id: "searing-shot",
    accuracy: 100,
    damageClass: "special",
    effect: "Has a $effect_chance% chance to burn the target.",
    effectChance: 30,
    learnedBy: [],
    meta: {
      ailment: "burn",
      ailmentChance: 30,
      category: "damage+ailment",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Searing Shot",
    power: 100,
    pp: 5,
    priority: 0,
    type: "fire",
  },
  "techno-blast": {
    id: "techno-blast",
    accuracy: 100,
    damageClass: "special",
    effect:
      "If the user is holding a appropriate plate or drive, the damage inflicted will match it.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Techno Blast",
    power: 120,
    pp: 5,
    priority: 0,
    type: "normal",
  },
  "relic-song": {
    id: "relic-song",
    accuracy: 100,
    damageClass: "special",
    effect: "Has a $effect_chance% chance to put the target to sleep.",
    effectChance: 10,
    learnedBy: [],
    meta: {
      ailment: "sleep",
      ailmentChance: 10,
      category: "damage+ailment",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: 4,
      minHits: null,
      minTurns: 2,
      statChance: 0,
    },
    name: "Relic Song",
    power: 75,
    pp: 10,
    priority: 0,
    type: "normal",
  },
  "secret-sword": {
    id: "secret-sword",
    accuracy: 100,
    damageClass: "special",
    effect:
      "Inflicts damage based on the target's Defense, not Special Defense.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Secret Sword",
    power: 85,
    pp: 10,
    priority: 0,
    type: "fighting",
  },
  glaciate: {
    id: "glaciate",
    accuracy: 95,
    damageClass: "special",
    effect: "Lowers the target's Speed by one stage.",
    effectChance: 100,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage+lower",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 100,
    },
    name: "Glaciate",
    power: 65,
    pp: 10,
    priority: 0,
    type: "ice",
  },
  "bolt-strike": {
    id: "bolt-strike",
    accuracy: 85,
    damageClass: "physical",
    effect: "Has a $effect_chance% chance to paralyze the target.",
    effectChance: 20,
    learnedBy: [],
    meta: {
      ailment: "paralysis",
      ailmentChance: 20,
      category: "damage+ailment",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Bolt Strike",
    power: 130,
    pp: 5,
    priority: 0,
    type: "electric",
  },
  "blue-flare": {
    id: "blue-flare",
    accuracy: 85,
    damageClass: "special",
    effect: "Has a $effect_chance% chance to burn the target.",
    effectChance: 20,
    learnedBy: [],
    meta: {
      ailment: "burn",
      ailmentChance: 20,
      category: "damage+ailment",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Blue Flare",
    power: 130,
    pp: 5,
    priority: 0,
    type: "fire",
  },
  "fiery-dance": {
    id: "fiery-dance",
    accuracy: 100,
    damageClass: "special",
    effect:
      "Has a $effect_chance% chance to raise the user's Special Attack by one stage.",
    effectChance: 50,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage+raise",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 50,
    },
    name: "Fiery Dance",
    power: 80,
    pp: 10,
    priority: 0,
    type: "fire",
  },
  "freeze-shock": {
    id: "freeze-shock",
    accuracy: 90,
    damageClass: "physical",
    effect:
      "Requires a turn to charge before attacking.  Has a $effect_chance% chance to paralyze the target.",
    effectChance: 30,
    learnedBy: [],
    meta: {
      ailment: "paralysis",
      ailmentChance: 30,
      category: "damage+ailment",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Freeze Shock",
    power: 140,
    pp: 5,
    priority: 0,
    type: "ice",
  },
  "ice-burn": {
    id: "ice-burn",
    accuracy: 90,
    damageClass: "special",
    effect:
      "Requires a turn to charge before attacking.  Has a $effect_chance% chance to burn the target.",
    effectChance: 30,
    learnedBy: [],
    meta: {
      ailment: "burn",
      ailmentChance: 30,
      category: "damage+ailment",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Ice Burn",
    power: 140,
    pp: 5,
    priority: 0,
    type: "ice",
  },
  snarl: {
    id: "snarl",
    accuracy: 95,
    damageClass: "special",
    effect:
      "Has a $effect_chance% chance to lower the target's Special Attack by one stage.",
    effectChance: 100,
    learnedBy: [52, 53, 58, 59, 151],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage+lower",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 100,
    },
    name: "Snarl",
    power: 55,
    pp: 15,
    priority: 0,
    type: "dark",
  },
  "icicle-crash": {
    id: "icicle-crash",
    accuracy: 90,
    damageClass: "physical",
    effect: "Has a $effect_chance% chance to make the target flinch.",
    effectChance: 30,
    learnedBy: [91],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 30,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Icicle Crash",
    power: 85,
    pp: 10,
    priority: 0,
    type: "ice",
  },
  "v-create": {
    id: "v-create",
    accuracy: 95,
    damageClass: "physical",
    effect:
      "Lowers the user's Defense, Special Defense, and Speed by one stage each.",
    effectChance: 100,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage+raise",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 100,
    },
    name: "V-create",
    power: 180,
    pp: 5,
    priority: 0,
    type: "fire",
  },
  "fusion-flare": {
    id: "fusion-flare",
    accuracy: 100,
    damageClass: "special",
    effect: "With Fusion Bolt, inflicts double damage.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Fusion Flare",
    power: 100,
    pp: 5,
    priority: 0,
    type: "fire",
  },
  "fusion-bolt": {
    id: "fusion-bolt",
    accuracy: 100,
    damageClass: "physical",
    effect: "With Fusion Flare, inflicts double damage.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Fusion Bolt",
    power: 100,
    pp: 5,
    priority: 0,
    type: "electric",
  },
  "flying-press": {
    id: "flying-press",
    accuracy: 95,
    damageClass: "physical",
    effect: "Deals both fighting and flying-type damage.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Flying Press",
    power: 100,
    pp: 10,
    priority: 0,
    type: "fighting",
  },
  "mat-block": {
    id: "mat-block",
    accuracy: null,
    damageClass: "status",
    effect:
      "Protects all friendly Pokémon from damaging moves.  Only works on the first turn after the user is sent out.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "field-effect",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Mat Block",
    power: null,
    pp: 10,
    priority: 0,
    type: "fighting",
  },
  belch: {
    id: "belch",
    accuracy: 90,
    damageClass: "special",
    effect: "Can only be used after the user has eaten a berry.",
    effectChance: null,
    learnedBy: [23, 24, 69, 79, 86, 88, 89, 108, 109, 110, 143],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Belch",
    power: 120,
    pp: 10,
    priority: 0,
    type: "poison",
  },
  rototiller: {
    id: "rototiller",
    accuracy: null,
    damageClass: "status",
    effect:
      "Raises the Attack and Special Attack of all grass Pokémon in battle.",
    effectChance: 100,
    learnedBy: [27, 46, 51, 95, 111],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "net-good-stats",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 100,
    },
    name: "Rototiller",
    power: null,
    pp: 10,
    priority: 0,
    type: "ground",
  },
  "sticky-web": {
    id: "sticky-web",
    accuracy: null,
    damageClass: "status",
    effect:
      "Covers the opposing field, lowering opponents' Speed by one stage upon switching in.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "field-effect",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Sticky Web",
    power: null,
    pp: 20,
    priority: 0,
    type: "bug",
  },
  "fell-stinger": {
    id: "fell-stinger",
    accuracy: 100,
    damageClass: "physical",
    effect: "Raises the user's Attack by two stages if it KOs the target.",
    effectChance: null,
    learnedBy: [15, 46],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Fell Stinger",
    power: 50,
    pp: 25,
    priority: 0,
    type: "bug",
  },
  "phantom-force": {
    id: "phantom-force",
    accuracy: 100,
    damageClass: "physical",
    effect:
      "User vanishes, dodging all attacks, and hits next turn.  Hits through Protect and Detect.",
    effectChance: null,
    learnedBy: [93, 94, 151],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Phantom Force",
    power: 90,
    pp: 10,
    priority: 0,
    type: "ghost",
  },
  "trick-or-treat": {
    id: "trick-or-treat",
    accuracy: 100,
    damageClass: "status",
    effect: "Adds ghost to the target's types.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "unique",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Trick-or-Treat",
    power: null,
    pp: 20,
    priority: 0,
    type: "ghost",
  },
  "noble-roar": {
    id: "noble-roar",
    accuracy: 100,
    damageClass: "status",
    effect: "Lowers the target's Attack and Special Attack by one stage.",
    effectChance: 100,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "net-good-stats",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 100,
    },
    name: "Noble Roar",
    power: null,
    pp: 30,
    priority: 0,
    type: "normal",
  },
  "ion-deluge": {
    id: "ion-deluge",
    accuracy: null,
    damageClass: "status",
    effect:
      "Changes all normal moves to electric moves for the rest of the turn.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "whole-field-effect",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Ion Deluge",
    power: null,
    pp: 25,
    priority: 1,
    type: "electric",
  },
  "parabolic-charge": {
    id: "parabolic-charge",
    accuracy: 100,
    damageClass: "special",
    effect: "Heals the user for half the total damage dealt to all targets.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage+heal",
      critRate: 0,
      drain: 50,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Parabolic Charge",
    power: 65,
    pp: 20,
    priority: 0,
    type: "electric",
  },
  "forests-curse": {
    id: "forests-curse",
    accuracy: 100,
    damageClass: "status",
    effect: "Adds grass to the target's types.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "unique",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Forest’s Curse",
    power: null,
    pp: 20,
    priority: 0,
    type: "grass",
  },
  "petal-blizzard": {
    id: "petal-blizzard",
    accuracy: 100,
    damageClass: "physical",
    effect: "Inflicts regular damage.",
    effectChance: null,
    learnedBy: [3, 44, 45],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Petal Blizzard",
    power: 90,
    pp: 15,
    priority: 0,
    type: "grass",
  },
  "freeze-dry": {
    id: "freeze-dry",
    accuracy: 100,
    damageClass: "special",
    effect: "Super-effective against water.",
    effectChance: 10,
    learnedBy: [131, 144],
    meta: {
      ailment: "freeze",
      ailmentChance: 10,
      category: "damage+ailment",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Freeze-Dry",
    power: 70,
    pp: 20,
    priority: 0,
    type: "ice",
  },
  "disarming-voice": {
    id: "disarming-voice",
    accuracy: null,
    damageClass: "special",
    effect: "Never misses.",
    effectChance: null,
    learnedBy: [25, 26, 35, 36, 39, 40, 113, 151],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Disarming Voice",
    power: 40,
    pp: 15,
    priority: 0,
    type: "fairy",
  },
  "parting-shot": {
    id: "parting-shot",
    accuracy: 100,
    damageClass: "status",
    effect:
      "Lowers all targets' Attack and Special Attack by one stage.  Makes the user switch out.",
    effectChance: 100,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "net-good-stats",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 100,
    },
    name: "Parting Shot",
    power: null,
    pp: 20,
    priority: 0,
    type: "dark",
  },
  "topsy-turvy": {
    id: "topsy-turvy",
    accuracy: null,
    damageClass: "status",
    effect: "Inverts the target's stat modifiers.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "unique",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Topsy-Turvy",
    power: null,
    pp: 20,
    priority: 0,
    type: "dark",
  },
  "draining-kiss": {
    id: "draining-kiss",
    accuracy: 100,
    damageClass: "special",
    effect: "Drains 75% of the damage inflicted to heal the user.",
    effectChance: null,
    learnedBy: [12, 25, 26, 35, 36, 39, 40, 96, 97, 124, 151],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage+heal",
      critRate: 0,
      drain: 75,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Draining Kiss",
    power: 50,
    pp: 10,
    priority: 0,
    type: "fairy",
  },
  "crafty-shield": {
    id: "crafty-shield",
    accuracy: null,
    damageClass: "status",
    effect: "Protects all friendly Pokémon from non-damaging moves.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "field-effect",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Crafty Shield",
    power: null,
    pp: 10,
    priority: 3,
    type: "fairy",
  },
  "flower-shield": {
    id: "flower-shield",
    accuracy: null,
    damageClass: "status",
    effect: "Raises the Defense of all grass Pokémon in battle.",
    effectChance: 100,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "unique",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 100,
    },
    name: "Flower Shield",
    power: null,
    pp: 10,
    priority: 0,
    type: "fairy",
  },
  "grassy-terrain": {
    id: "grassy-terrain",
    accuracy: null,
    damageClass: "status",
    effect:
      "For five turns, heals all Pokémon on the ground for 1/16 max HP each turn and strengthens their grass moves to 1.5× their power.",
    effectChance: null,
    learnedBy: [1, 2, 3, 43, 44, 45, 46, 102, 103, 114, 151],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "whole-field-effect",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Grassy Terrain",
    power: null,
    pp: 10,
    priority: 0,
    type: "grass",
  },
  "misty-terrain": {
    id: "misty-terrain",
    accuracy: null,
    damageClass: "status",
    effect:
      "For five turns, protects all Pokémon on the ground from major status ailments and confusion, and halves the power of incoming dragon moves.",
    effectChance: null,
    learnedBy: [35, 36, 39, 40, 122, 151],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "whole-field-effect",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Misty Terrain",
    power: null,
    pp: 10,
    priority: 0,
    type: "fairy",
  },
  electrify: {
    id: "electrify",
    accuracy: null,
    damageClass: "status",
    effect:
      "Changes the target's move's type to electric if it hasn't moved yet this turn.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "unique",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Electrify",
    power: null,
    pp: 20,
    priority: 0,
    type: "electric",
  },
  "play-rough": {
    id: "play-rough",
    accuracy: 90,
    damageClass: "physical",
    effect:
      "Has a $effect_chance% chance to lower the target's Attack by one stage.",
    effectChance: 10,
    learnedBy: [25, 26, 35, 36, 39, 40, 52, 53, 58, 59, 77, 78, 151],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage+lower",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 10,
    },
    name: "Play Rough",
    power: 90,
    pp: 10,
    priority: 0,
    type: "fairy",
  },
  "fairy-wind": {
    id: "fairy-wind",
    accuracy: 100,
    damageClass: "special",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Fairy Wind",
    power: 40,
    pp: 30,
    priority: 0,
    type: "fairy",
  },
  moonblast: {
    id: "moonblast",
    accuracy: 100,
    damageClass: "special",
    effect:
      "Has a $effect_chance% chance to lower the target's Special Attack by one stage.",
    effectChance: 30,
    learnedBy: [35, 36, 43, 44, 45, 89],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage+lower",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 30,
    },
    name: "Moonblast",
    power: 95,
    pp: 15,
    priority: 0,
    type: "fairy",
  },
  boomburst: {
    id: "boomburst",
    accuracy: 100,
    damageClass: "special",
    effect: "Inflicts regular damage.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Boomburst",
    power: 140,
    pp: 10,
    priority: 0,
    type: "normal",
  },
  "fairy-lock": {
    id: "fairy-lock",
    accuracy: null,
    damageClass: "status",
    effect:
      "Prevents all Pokémon from fleeing or switching out during the next turn.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "whole-field-effect",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Fairy Lock",
    power: null,
    pp: 10,
    priority: 0,
    type: "fairy",
  },
  "kings-shield": {
    id: "kings-shield",
    accuracy: null,
    damageClass: "status",
    effect:
      "Blocks damaging attacks and lowers attacking Pokémon's Attack by two stages on contact.  Switches Aegislash to Shield Forme.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "unique",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "King’s Shield",
    power: null,
    pp: 10,
    priority: 4,
    type: "steel",
  },
  "play-nice": {
    id: "play-nice",
    accuracy: null,
    damageClass: "status",
    effect: "Lowers the target's Attack by one stage.",
    effectChance: 100,
    learnedBy: [25, 26, 39],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "net-good-stats",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 100,
    },
    name: "Play Nice",
    power: null,
    pp: 20,
    priority: 0,
    type: "normal",
  },
  confide: {
    id: "confide",
    accuracy: null,
    damageClass: "status",
    effect: "Lowers the target's Special Attack by one stage.",
    effectChance: 100,
    learnedBy: [
      1, 2, 3, 4, 5, 6, 7, 8, 9, 12, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25,
      26, 27, 28, 29, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 40, 41, 42, 43,
      44, 45, 46, 47, 48, 49, 50, 51, 52, 53, 54, 55, 56, 57, 58, 59, 60, 61,
      62, 63, 64, 65, 66, 67, 68, 69, 70, 71, 72, 73, 74, 75, 76, 77, 78, 79,
      80, 81, 82, 83, 84, 85, 86, 87, 88, 89, 90, 91, 92, 93, 94, 95, 96, 97,
      98, 99, 100, 101, 102, 103, 104, 105, 106, 107, 108, 109, 110, 111, 112,
      113, 114, 115, 116, 117, 118, 119, 120, 121, 122, 123, 124, 125, 126, 127,
      128, 130, 131, 133, 134, 135, 136, 137, 138, 139, 140, 141, 142, 143, 144,
      145, 146, 147, 148, 149, 150, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "net-good-stats",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 100,
    },
    name: "Confide",
    power: null,
    pp: 20,
    priority: 0,
    type: "normal",
  },
  "diamond-storm": {
    id: "diamond-storm",
    accuracy: 95,
    damageClass: "physical",
    effect:
      "Has a $effect_chance% chance to raise the user's Defense by two stages for each target hit.",
    effectChance: 50,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage+raise",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 50,
    },
    name: "Diamond Storm",
    power: 100,
    pp: 5,
    priority: 0,
    type: "rock",
  },
  "steam-eruption": {
    id: "steam-eruption",
    accuracy: 95,
    damageClass: "special",
    effect: "Has a $effect_chance% chance to burn the target.",
    effectChance: 30,
    learnedBy: [],
    meta: {
      ailment: "burn",
      ailmentChance: 30,
      category: "damage+ailment",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Steam Eruption",
    power: 110,
    pp: 5,
    priority: 0,
    type: "water",
  },
  "hyperspace-hole": {
    id: "hyperspace-hole",
    accuracy: null,
    damageClass: "special",
    effect: "Ignores and destroys protection effects.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Hyperspace Hole",
    power: 80,
    pp: 5,
    priority: 0,
    type: "psychic",
  },
  "water-shuriken": {
    id: "water-shuriken",
    accuracy: 100,
    damageClass: "special",
    effect: "Hits 2–5 times.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: 5,
      maxTurns: null,
      minHits: 2,
      minTurns: null,
      statChance: 0,
    },
    name: "Water Shuriken",
    power: 15,
    pp: 20,
    priority: 1,
    type: "water",
  },
  "mystical-fire": {
    id: "mystical-fire",
    accuracy: 100,
    damageClass: "special",
    effect:
      "Has a $effect_chance% chance to lower the target's Special Attack by one stage.",
    effectChance: 100,
    learnedBy: [6, 35, 36, 37, 38, 77, 78, 122, 136, 146, 151],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage+lower",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 100,
    },
    name: "Mystical Fire",
    power: 75,
    pp: 10,
    priority: 0,
    type: "fire",
  },
  "spiky-shield": {
    id: "spiky-shield",
    accuracy: null,
    damageClass: "status",
    effect:
      "Blocks damaging attacks and damages attacking Pokémon for 1/8 their max HP.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "unique",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Spiky Shield",
    power: null,
    pp: 10,
    priority: 4,
    type: "grass",
  },
  "aromatic-mist": {
    id: "aromatic-mist",
    accuracy: null,
    damageClass: "status",
    effect: "Raises a selected ally's Special Defense by one stage.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "net-good-stats",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Aromatic Mist",
    power: null,
    pp: 20,
    priority: 0,
    type: "fairy",
  },
  "eerie-impulse": {
    id: "eerie-impulse",
    accuracy: 100,
    damageClass: "status",
    effect: "Lowers the target's Special Attack by two stages.",
    effectChance: null,
    learnedBy: [25, 26, 81, 82, 100, 101, 135, 137, 145, 151],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "net-good-stats",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Eerie Impulse",
    power: null,
    pp: 15,
    priority: 0,
    type: "electric",
  },
  "venom-drench": {
    id: "venom-drench",
    accuracy: 100,
    damageClass: "status",
    effect:
      "Lowers the target's Attack, Special Attack, and Speed by one stage if it is poisoned.",
    effectChance: 100,
    learnedBy: [29, 30, 31, 32, 33, 34, 41, 42, 73, 89, 109, 110, 151],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "net-good-stats",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 100,
    },
    name: "Venom Drench",
    power: null,
    pp: 20,
    priority: 0,
    type: "poison",
  },
  powder: {
    id: "powder",
    accuracy: 100,
    damageClass: "status",
    effect:
      "Explodes if the target uses a fire move this turn, damaging it for 1/4 its max HP and preventing the move.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "unique",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Powder",
    power: null,
    pp: 20,
    priority: 1,
    type: "bug",
  },
  geomancy: {
    id: "geomancy",
    accuracy: null,
    damageClass: "status",
    effect:
      "Takes one turn to charge, then raises the user's Special Attack, Special Defense, and Speed by two stages.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "net-good-stats",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Geomancy",
    power: null,
    pp: 10,
    priority: 0,
    type: "fairy",
  },
  "magnetic-flux": {
    id: "magnetic-flux",
    accuracy: null,
    damageClass: "status",
    effect:
      "Raises the Defense and Special Defense of all friendly Pokémon with plus or minus by one stage.",
    effectChance: null,
    learnedBy: [101, 145],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "net-good-stats",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Magnetic Flux",
    power: null,
    pp: 20,
    priority: 0,
    type: "electric",
  },
  "happy-hour": {
    id: "happy-hour",
    accuracy: null,
    damageClass: "status",
    effect: "Doubles prize money.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "unique",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Happy Hour",
    power: null,
    pp: 30,
    priority: 0,
    type: "normal",
  },
  "electric-terrain": {
    id: "electric-terrain",
    accuracy: null,
    damageClass: "status",
    effect:
      "For five turns, prevents all Pokémon on the ground from sleeping and strengthens their electric moves to 1.5× their power.",
    effectChance: null,
    learnedBy: [25, 26, 81, 82, 100, 101, 113, 135, 145, 151],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "whole-field-effect",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Electric Terrain",
    power: null,
    pp: 10,
    priority: 0,
    type: "electric",
  },
  "dazzling-gleam": {
    id: "dazzling-gleam",
    accuracy: 100,
    damageClass: "special",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [
      35, 36, 39, 40, 43, 44, 45, 63, 64, 65, 72, 73, 92, 93, 94, 96, 97, 113,
      120, 121, 122, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Dazzling Gleam",
    power: 80,
    pp: 10,
    priority: 0,
    type: "fairy",
  },
  celebrate: {
    id: "celebrate",
    accuracy: null,
    damageClass: "status",
    effect: "Does nothing.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "unique",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Celebrate",
    power: null,
    pp: 40,
    priority: 0,
    type: "normal",
  },
  "hold-hands": {
    id: "hold-hands",
    accuracy: null,
    damageClass: "status",
    effect: "Does nothing.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "unique",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Hold Hands",
    power: null,
    pp: 40,
    priority: 0,
    type: "normal",
  },
  "baby-doll-eyes": {
    id: "baby-doll-eyes",
    accuracy: 100,
    damageClass: "status",
    effect: "Lowers the target's Attack by one stage.",
    effectChance: null,
    learnedBy: [37, 133, 134, 135, 136],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "net-good-stats",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Baby-Doll Eyes",
    power: null,
    pp: 30,
    priority: 1,
    type: "fairy",
  },
  nuzzle: {
    id: "nuzzle",
    accuracy: 100,
    damageClass: "physical",
    effect: "Has a $effect_chance% chance to paralyze the target.",
    effectChance: 100,
    learnedBy: [25, 26],
    meta: {
      ailment: "paralysis",
      ailmentChance: 100,
      category: "damage+ailment",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Nuzzle",
    power: 20,
    pp: 20,
    priority: 0,
    type: "electric",
  },
  "hold-back": {
    id: "hold-back",
    accuracy: 100,
    damageClass: "physical",
    effect: "Cannot lower the target's HP below 1.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Hold Back",
    power: 40,
    pp: 40,
    priority: 0,
    type: "normal",
  },
  infestation: {
    id: "infestation",
    accuracy: 100,
    damageClass: "special",
    effect:
      "Prevents the target from fleeing and inflicts damage for 2-5 turns.",
    effectChance: 100,
    learnedBy: [
      12, 15, 23, 24, 43, 44, 45, 48, 49, 69, 70, 71, 72, 73, 88, 89, 92, 93,
      94, 102, 103, 109, 110, 114, 122, 151,
    ],
    meta: {
      ailment: "trap",
      ailmentChance: 100,
      category: "damage+ailment",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: 6,
      minHits: null,
      minTurns: 5,
      statChance: 0,
    },
    name: "Infestation",
    power: 20,
    pp: 20,
    priority: 0,
    type: "bug",
  },
  "power-up-punch": {
    id: "power-up-punch",
    accuracy: 100,
    damageClass: "physical",
    effect: "Raises the user's Attack by one stage after inflicting damage.",
    effectChance: 100,
    learnedBy: [
      4, 5, 6, 7, 8, 9, 31, 34, 35, 36, 39, 40, 54, 55, 56, 57, 61, 62, 66, 67,
      68, 74, 75, 76, 88, 89, 94, 96, 97, 104, 105, 106, 107, 108, 112, 113,
      115, 122, 124, 125, 126, 143, 149, 150, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage+raise",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 100,
    },
    name: "Power-Up Punch",
    power: 40,
    pp: 20,
    priority: 0,
    type: "fighting",
  },
  "oblivion-wing": {
    id: "oblivion-wing",
    accuracy: 100,
    damageClass: "special",
    effect: "Drains 75% of the damage inflicted to heal the user.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage+heal",
      critRate: 0,
      drain: 75,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Oblivion Wing",
    power: 80,
    pp: 10,
    priority: 0,
    type: "flying",
  },
  "thousand-arrows": {
    id: "thousand-arrows",
    accuracy: 100,
    damageClass: "physical",
    effect:
      "Grounds the target, and hits even Flying-type or levitating Pokémon.",
    effectChance: 100,
    learnedBy: [],
    meta: {
      ailment: "unknown",
      ailmentChance: 100,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Thousand Arrows",
    power: 90,
    pp: 10,
    priority: 0,
    type: "ground",
  },
  "thousand-waves": {
    id: "thousand-waves",
    accuracy: 100,
    damageClass: "physical",
    effect: "Prevents the target from leaving battle.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Thousand Waves",
    power: 90,
    pp: 10,
    priority: 0,
    type: "ground",
  },
  "lands-wrath": {
    id: "lands-wrath",
    accuracy: 100,
    damageClass: "physical",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Land’s Wrath",
    power: 90,
    pp: 10,
    priority: 0,
    type: "ground",
  },
  "light-of-ruin": {
    id: "light-of-ruin",
    accuracy: 90,
    damageClass: "special",
    effect: "User receives 1/2 the damage inflicted in recoil.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: -50,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Light of Ruin",
    power: 140,
    pp: 5,
    priority: 0,
    type: "fairy",
  },
  "origin-pulse": {
    id: "origin-pulse",
    accuracy: 85,
    damageClass: "special",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Origin Pulse",
    power: 110,
    pp: 10,
    priority: 0,
    type: "water",
  },
  "precipice-blades": {
    id: "precipice-blades",
    accuracy: 85,
    damageClass: "physical",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Precipice Blades",
    power: 120,
    pp: 10,
    priority: 0,
    type: "ground",
  },
  "dragon-ascent": {
    id: "dragon-ascent",
    accuracy: 100,
    damageClass: "physical",
    effect:
      "Lowers the user's Defense and Special Defense by one stage after inflicting damage.",
    effectChance: 100,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage+raise",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 100,
    },
    name: "Dragon Ascent",
    power: 120,
    pp: 5,
    priority: 0,
    type: "flying",
  },
  "hyperspace-fury": {
    id: "hyperspace-fury",
    accuracy: null,
    damageClass: "physical",
    effect: "Ignores and destroys protection effects.",
    effectChance: 100,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage+raise",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 100,
    },
    name: "Hyperspace Fury",
    power: 100,
    pp: 5,
    priority: 0,
    type: "dark",
  },
  "breakneck-blitz--physical": {
    id: "breakneck-blitz--physical",
    accuracy: null,
    damageClass: "physical",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Breakneck Blitz",
    power: null,
    pp: 1,
    priority: 0,
    type: "normal",
  },
  "breakneck-blitz--special": {
    id: "breakneck-blitz--special",
    accuracy: null,
    damageClass: "special",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Breakneck Blitz",
    power: null,
    pp: 1,
    priority: 0,
    type: "normal",
  },
  "all-out-pummeling--physical": {
    id: "all-out-pummeling--physical",
    accuracy: null,
    damageClass: "physical",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "All-Out Pummeling",
    power: null,
    pp: 1,
    priority: 0,
    type: "fighting",
  },
  "all-out-pummeling--special": {
    id: "all-out-pummeling--special",
    accuracy: null,
    damageClass: "special",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "All-Out Pummeling",
    power: null,
    pp: 1,
    priority: 0,
    type: "fighting",
  },
  "supersonic-skystrike--physical": {
    id: "supersonic-skystrike--physical",
    accuracy: null,
    damageClass: "physical",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Supersonic Skystrike",
    power: null,
    pp: 1,
    priority: 0,
    type: "flying",
  },
  "supersonic-skystrike--special": {
    id: "supersonic-skystrike--special",
    accuracy: null,
    damageClass: "special",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Supersonic Skystrike",
    power: null,
    pp: 1,
    priority: 0,
    type: "flying",
  },
  "acid-downpour--physical": {
    id: "acid-downpour--physical",
    accuracy: null,
    damageClass: "physical",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Acid Downpour",
    power: null,
    pp: 1,
    priority: 0,
    type: "poison",
  },
  "acid-downpour--special": {
    id: "acid-downpour--special",
    accuracy: null,
    damageClass: "special",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Acid Downpour",
    power: null,
    pp: 1,
    priority: 0,
    type: "poison",
  },
  "tectonic-rage--physical": {
    id: "tectonic-rage--physical",
    accuracy: null,
    damageClass: "physical",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Tectonic Rage",
    power: null,
    pp: 1,
    priority: 0,
    type: "ground",
  },
  "tectonic-rage--special": {
    id: "tectonic-rage--special",
    accuracy: null,
    damageClass: "special",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Tectonic Rage",
    power: null,
    pp: 1,
    priority: 0,
    type: "ground",
  },
  "continental-crush--physical": {
    id: "continental-crush--physical",
    accuracy: null,
    damageClass: "physical",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Continental Crush",
    power: null,
    pp: 1,
    priority: 0,
    type: "rock",
  },
  "continental-crush--special": {
    id: "continental-crush--special",
    accuracy: null,
    damageClass: "special",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Continental Crush",
    power: null,
    pp: 1,
    priority: 0,
    type: "rock",
  },
  "savage-spin-out--physical": {
    id: "savage-spin-out--physical",
    accuracy: null,
    damageClass: "physical",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Savage Spin-Out",
    power: null,
    pp: 1,
    priority: 0,
    type: "bug",
  },
  "savage-spin-out--special": {
    id: "savage-spin-out--special",
    accuracy: null,
    damageClass: "special",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Savage Spin-Out",
    power: null,
    pp: 1,
    priority: 0,
    type: "bug",
  },
  "never-ending-nightmare--physical": {
    id: "never-ending-nightmare--physical",
    accuracy: null,
    damageClass: "physical",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Never-Ending Nightmare",
    power: null,
    pp: 1,
    priority: 0,
    type: "ghost",
  },
  "never-ending-nightmare--special": {
    id: "never-ending-nightmare--special",
    accuracy: null,
    damageClass: "special",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Never-Ending Nightmare",
    power: null,
    pp: 1,
    priority: 0,
    type: "ghost",
  },
  "corkscrew-crash--physical": {
    id: "corkscrew-crash--physical",
    accuracy: null,
    damageClass: "physical",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Corkscrew Crash",
    power: null,
    pp: 1,
    priority: 0,
    type: "steel",
  },
  "corkscrew-crash--special": {
    id: "corkscrew-crash--special",
    accuracy: null,
    damageClass: "special",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Corkscrew Crash",
    power: null,
    pp: 1,
    priority: 0,
    type: "steel",
  },
  "inferno-overdrive--physical": {
    id: "inferno-overdrive--physical",
    accuracy: null,
    damageClass: "physical",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Inferno Overdrive",
    power: null,
    pp: 1,
    priority: 0,
    type: "fire",
  },
  "inferno-overdrive--special": {
    id: "inferno-overdrive--special",
    accuracy: null,
    damageClass: "special",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Inferno Overdrive",
    power: null,
    pp: 1,
    priority: 0,
    type: "fire",
  },
  "hydro-vortex--physical": {
    id: "hydro-vortex--physical",
    accuracy: null,
    damageClass: "physical",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Hydro Vortex",
    power: null,
    pp: 1,
    priority: 0,
    type: "water",
  },
  "hydro-vortex--special": {
    id: "hydro-vortex--special",
    accuracy: null,
    damageClass: "special",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Hydro Vortex",
    power: null,
    pp: 1,
    priority: 0,
    type: "water",
  },
  "bloom-doom--physical": {
    id: "bloom-doom--physical",
    accuracy: null,
    damageClass: "physical",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Bloom Doom",
    power: null,
    pp: 1,
    priority: 0,
    type: "grass",
  },
  "bloom-doom--special": {
    id: "bloom-doom--special",
    accuracy: null,
    damageClass: "special",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Bloom Doom",
    power: null,
    pp: 1,
    priority: 0,
    type: "grass",
  },
  "gigavolt-havoc--physical": {
    id: "gigavolt-havoc--physical",
    accuracy: null,
    damageClass: "physical",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Gigavolt Havoc",
    power: null,
    pp: 1,
    priority: 0,
    type: "electric",
  },
  "gigavolt-havoc--special": {
    id: "gigavolt-havoc--special",
    accuracy: null,
    damageClass: "special",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Gigavolt Havoc",
    power: null,
    pp: 1,
    priority: 0,
    type: "electric",
  },
  "shattered-psyche--physical": {
    id: "shattered-psyche--physical",
    accuracy: null,
    damageClass: "physical",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Shattered Psyche",
    power: null,
    pp: 1,
    priority: 0,
    type: "psychic",
  },
  "shattered-psyche--special": {
    id: "shattered-psyche--special",
    accuracy: null,
    damageClass: "special",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Shattered Psyche",
    power: null,
    pp: 1,
    priority: 0,
    type: "psychic",
  },
  "subzero-slammer--physical": {
    id: "subzero-slammer--physical",
    accuracy: null,
    damageClass: "physical",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Subzero Slammer",
    power: null,
    pp: 1,
    priority: 0,
    type: "ice",
  },
  "subzero-slammer--special": {
    id: "subzero-slammer--special",
    accuracy: null,
    damageClass: "special",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Subzero Slammer",
    power: null,
    pp: 1,
    priority: 0,
    type: "ice",
  },
  "devastating-drake--physical": {
    id: "devastating-drake--physical",
    accuracy: null,
    damageClass: "physical",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Devastating Drake",
    power: null,
    pp: 1,
    priority: 0,
    type: "dragon",
  },
  "devastating-drake--special": {
    id: "devastating-drake--special",
    accuracy: null,
    damageClass: "special",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Devastating Drake",
    power: null,
    pp: 1,
    priority: 0,
    type: "dragon",
  },
  "black-hole-eclipse--physical": {
    id: "black-hole-eclipse--physical",
    accuracy: null,
    damageClass: "physical",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Black Hole Eclipse",
    power: null,
    pp: 1,
    priority: 0,
    type: "dark",
  },
  "black-hole-eclipse--special": {
    id: "black-hole-eclipse--special",
    accuracy: null,
    damageClass: "special",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Black Hole Eclipse",
    power: null,
    pp: 1,
    priority: 0,
    type: "dark",
  },
  "twinkle-tackle--physical": {
    id: "twinkle-tackle--physical",
    accuracy: null,
    damageClass: "physical",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Twinkle Tackle",
    power: null,
    pp: 1,
    priority: 0,
    type: "fairy",
  },
  "twinkle-tackle--special": {
    id: "twinkle-tackle--special",
    accuracy: null,
    damageClass: "special",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Twinkle Tackle",
    power: null,
    pp: 1,
    priority: 0,
    type: "fairy",
  },
  catastropika: {
    id: "catastropika",
    accuracy: null,
    damageClass: "physical",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Catastropika",
    power: 210,
    pp: 1,
    priority: 0,
    type: "electric",
  },
  "shore-up": {
    id: "shore-up",
    accuracy: null,
    damageClass: "status",
    effect: "Heals the user for ½ its max HP, or ⅔ during a sandstorm.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "heal",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 50,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Shore Up",
    power: null,
    pp: 10,
    priority: 0,
    type: "ground",
  },
  "first-impression": {
    id: "first-impression",
    accuracy: 100,
    damageClass: "physical",
    effect: "Can only be used as the first move after the user enters battle.",
    effectChance: null,
    learnedBy: [83],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "First Impression",
    power: 90,
    pp: 10,
    priority: 2,
    type: "bug",
  },
  "baneful-bunker": {
    id: "baneful-bunker",
    accuracy: null,
    damageClass: "status",
    effect:
      "Grants the user protection for the rest of the turn and poisons any Pokémon that tries to use a contact move on it.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "unique",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Baneful Bunker",
    power: null,
    pp: 10,
    priority: 4,
    type: "poison",
  },
  "spirit-shackle": {
    id: "spirit-shackle",
    accuracy: 100,
    damageClass: "physical",
    effect: "Traps the target.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Spirit Shackle",
    power: 80,
    pp: 10,
    priority: 0,
    type: "ghost",
  },
  "darkest-lariat": {
    id: "darkest-lariat",
    accuracy: 100,
    damageClass: "physical",
    effect: "Ignores the target's stat modifiers.",
    effectChance: null,
    learnedBy: [62, 68, 143, 151],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Darkest Lariat",
    power: 85,
    pp: 10,
    priority: 0,
    type: "dark",
  },
  "sparkling-aria": {
    id: "sparkling-aria",
    accuracy: 100,
    damageClass: "special",
    effect: "Cures the target of burns.",
    effectChance: null,
    learnedBy: [131],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Sparkling Aria",
    power: 90,
    pp: 10,
    priority: 0,
    type: "water",
  },
  "ice-hammer": {
    id: "ice-hammer",
    accuracy: 90,
    damageClass: "physical",
    effect: "Lowers user's Speed by one stage.",
    effectChance: 100,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage+raise",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 100,
    },
    name: "Ice Hammer",
    power: 100,
    pp: 10,
    priority: 0,
    type: "ice",
  },
  "floral-healing": {
    id: "floral-healing",
    accuracy: null,
    damageClass: "status",
    effect: "Heals the target for ½ its max HP, or ⅔ on Grassy Terrain.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "heal",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 50,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Floral Healing",
    power: null,
    pp: 10,
    priority: 0,
    type: "fairy",
  },
  "high-horsepower": {
    id: "high-horsepower",
    accuracy: 95,
    damageClass: "physical",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [
      31, 34, 51, 62, 68, 77, 78, 95, 99, 111, 112, 127, 128, 143, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "High Horsepower",
    power: 95,
    pp: 10,
    priority: 0,
    type: "ground",
  },
  "strength-sap": {
    id: "strength-sap",
    accuracy: 100,
    damageClass: "status",
    effect:
      "Heals the user by the target's current Attack stat and lowers the target's Attack by one stage.",
    effectChance: 100,
    learnedBy: [43, 69],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "unique",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 100,
    },
    name: "Strength Sap",
    power: null,
    pp: 10,
    priority: 0,
    type: "grass",
  },
  "solar-blade": {
    id: "solar-blade",
    accuracy: 100,
    damageClass: "physical",
    effect: "Requires a turn to charge before attacking.",
    effectChance: null,
    learnedBy: [77, 78, 83, 151],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Solar Blade",
    power: 125,
    pp: 10,
    priority: 0,
    type: "grass",
  },
  leafage: {
    id: "leafage",
    accuracy: 100,
    damageClass: "physical",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Leafage",
    power: 40,
    pp: 40,
    priority: 0,
    type: "grass",
  },
  spotlight: {
    id: "spotlight",
    accuracy: null,
    damageClass: "status",
    effect:
      "Forces the target's opponents to aim at the target for the rest of the turn.",
    effectChance: null,
    learnedBy: [35, 36, 121],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "unique",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Spotlight",
    power: null,
    pp: 15,
    priority: 3,
    type: "normal",
  },
  "toxic-thread": {
    id: "toxic-thread",
    accuracy: 100,
    damageClass: "status",
    effect: "Poisons the target and lowers its Speed by one stage.",
    effectChance: 100,
    learnedBy: [],
    meta: {
      ailment: "poison",
      ailmentChance: 100,
      category: "swagger",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 100,
    },
    name: "Toxic Thread",
    power: null,
    pp: 20,
    priority: 0,
    type: "poison",
  },
  "laser-focus": {
    id: "laser-focus",
    accuracy: null,
    damageClass: "status",
    effect: "Guarantees a critical hit with the user's next move.",
    effectChance: null,
    learnedBy: [
      15, 18, 19, 20, 22, 25, 26, 36, 38, 40, 55, 59, 65, 83, 94, 105, 106, 107,
      113, 116, 117, 123, 133, 134, 135, 136, 142, 144, 145, 146, 150, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "unique",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Laser Focus",
    power: null,
    pp: 30,
    priority: 0,
    type: "normal",
  },
  "gear-up": {
    id: "gear-up",
    accuracy: null,
    damageClass: "status",
    effect:
      "Raises the Attack and Special Attack of all friendly Pokémon with plus or minus by one stage.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "net-good-stats",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Gear Up",
    power: null,
    pp: 20,
    priority: 0,
    type: "steel",
  },
  "throat-chop": {
    id: "throat-chop",
    accuracy: 100,
    damageClass: "physical",
    effect: "Prevents the target from using sound-based moves for two turns.",
    effectChance: 100,
    learnedBy: [
      15, 20, 22, 24, 27, 28, 31, 34, 47, 52, 53, 57, 62, 68, 78, 83, 105, 106,
      107, 118, 119, 127, 128, 151,
    ],
    meta: {
      ailment: "silence",
      ailmentChance: 100,
      category: "damage+ailment",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: 2,
      minHits: null,
      minTurns: 2,
      statChance: 0,
    },
    name: "Throat Chop",
    power: 80,
    pp: 15,
    priority: 0,
    type: "dark",
  },
  "pollen-puff": {
    id: "pollen-puff",
    accuracy: 100,
    damageClass: "special",
    effect: "Damages opponents, but heals allies for 50% of their max HP.",
    effectChance: null,
    learnedBy: [12, 45, 151],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Pollen Puff",
    power: 90,
    pp: 15,
    priority: 0,
    type: "bug",
  },
  "anchor-shot": {
    id: "anchor-shot",
    accuracy: 100,
    damageClass: "physical",
    effect: "Traps the target.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Anchor Shot",
    power: 80,
    pp: 20,
    priority: 0,
    type: "steel",
  },
  "psychic-terrain": {
    id: "psychic-terrain",
    accuracy: null,
    damageClass: "status",
    effect:
      "Protects Pokémon on the ground from priority moves and increases the power of their  Psychic moves by 50%.",
    effectChance: null,
    learnedBy: [63, 64, 65, 79, 80, 96, 97, 122, 124, 150, 151],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "whole-field-effect",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Psychic Terrain",
    power: null,
    pp: 10,
    priority: 0,
    type: "psychic",
  },
  lunge: {
    id: "lunge",
    accuracy: 100,
    damageClass: "physical",
    effect: "Lowers the target's Attack by one stage after inflicting damage.",
    effectChance: 100,
    learnedBy: [84, 85],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage+lower",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 100,
    },
    name: "Lunge",
    power: 80,
    pp: 15,
    priority: 0,
    type: "bug",
  },
  "fire-lash": {
    id: "fire-lash",
    accuracy: 100,
    damageClass: "physical",
    effect: "Lowers the target's Defense by one stage after inflicting damage.",
    effectChance: 100,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage+lower",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 100,
    },
    name: "Fire Lash",
    power: 80,
    pp: 15,
    priority: 0,
    type: "fire",
  },
  "power-trip": {
    id: "power-trip",
    accuracy: 100,
    damageClass: "physical",
    effect:
      "Power is higher the more the user's stats have been raised, to a maximum of 31×.",
    effectChance: null,
    learnedBy: [56],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Power Trip",
    power: 20,
    pp: 10,
    priority: 0,
    type: "dark",
  },
  "burn-up": {
    id: "burn-up",
    accuracy: 100,
    damageClass: "special",
    effect: "Removes the user's fire type after inflicting damage.",
    effectChance: null,
    learnedBy: [58, 59, 146],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Burn Up",
    power: 130,
    pp: 5,
    priority: 0,
    type: "fire",
  },
  "speed-swap": {
    id: "speed-swap",
    accuracy: null,
    damageClass: "status",
    effect: "Exchanges the user's Speed with the target's.",
    effectChance: null,
    learnedBy: [26, 63, 64, 65, 137, 150, 151],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "unique",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Speed Swap",
    power: null,
    pp: 10,
    priority: 0,
    type: "psychic",
  },
  "smart-strike": {
    id: "smart-strike",
    accuracy: null,
    damageClass: "physical",
    effect: "Never misses.",
    effectChance: null,
    learnedBy: [32, 33, 34, 78, 86, 87, 91, 111, 112, 118, 119, 128, 131, 151],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Smart Strike",
    power: 70,
    pp: 10,
    priority: 0,
    type: "steel",
  },
  purify: {
    id: "purify",
    accuracy: null,
    damageClass: "status",
    effect:
      "Cures the target of a major status ailment and heals the user for 50% of its max HP.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "unique",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 50,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Purify",
    power: null,
    pp: 20,
    priority: 0,
    type: "poison",
  },
  "revelation-dance": {
    id: "revelation-dance",
    accuracy: 100,
    damageClass: "special",
    effect: "Has the same type as the user.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Revelation Dance",
    power: 90,
    pp: 15,
    priority: 0,
    type: "normal",
  },
  "core-enforcer": {
    id: "core-enforcer",
    accuracy: 100,
    damageClass: "special",
    effect: "Nullifies the target's ability if it moves earlier.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Core Enforcer",
    power: 100,
    pp: 10,
    priority: 0,
    type: "dragon",
  },
  "trop-kick": {
    id: "trop-kick",
    accuracy: 100,
    damageClass: "physical",
    effect: "Lowers the target's Attack by one stage after inflicting damage.",
    effectChance: 100,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage+lower",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 100,
    },
    name: "Trop Kick",
    power: 70,
    pp: 15,
    priority: 0,
    type: "grass",
  },
  instruct: {
    id: "instruct",
    accuracy: null,
    damageClass: "status",
    effect: "Forces the target to repeat its last used move.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "unique",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Instruct",
    power: null,
    pp: 15,
    priority: 0,
    type: "psychic",
  },
  "beak-blast": {
    id: "beak-blast",
    accuracy: 100,
    damageClass: "physical",
    effect:
      "Inflicts a burn on any Pokémon that makes contact before the attack.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Beak Blast",
    power: 100,
    pp: 15,
    priority: -3,
    type: "flying",
  },
  "clanging-scales": {
    id: "clanging-scales",
    accuracy: 100,
    damageClass: "special",
    effect: "Lowers the user's Defense by one stage after inflicting damage.",
    effectChance: 100,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage+raise",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 100,
    },
    name: "Clanging Scales",
    power: 110,
    pp: 5,
    priority: 0,
    type: "dragon",
  },
  "dragon-hammer": {
    id: "dragon-hammer",
    accuracy: 100,
    damageClass: "physical",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Dragon Hammer",
    power: 90,
    pp: 15,
    priority: 0,
    type: "dragon",
  },
  "brutal-swing": {
    id: "brutal-swing",
    accuracy: 100,
    damageClass: "physical",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [
      6, 15, 23, 24, 26, 72, 73, 83, 95, 99, 104, 105, 108, 112, 123, 127, 130,
      142, 147, 148, 149, 150, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Brutal Swing",
    power: 60,
    pp: 20,
    priority: 0,
    type: "dark",
  },
  "aurora-veil": {
    id: "aurora-veil",
    accuracy: null,
    damageClass: "status",
    effect: "Reduces damage five turns, but must be used during hail.",
    effectChance: null,
    learnedBy: [124, 144, 151],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "field-effect",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Aurora Veil",
    power: null,
    pp: 20,
    priority: 0,
    type: "ice",
  },
  "sinister-arrow-raid": {
    id: "sinister-arrow-raid",
    accuracy: null,
    damageClass: "physical",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Sinister Arrow Raid",
    power: 180,
    pp: 1,
    priority: 0,
    type: "ghost",
  },
  "malicious-moonsault": {
    id: "malicious-moonsault",
    accuracy: null,
    damageClass: "physical",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Malicious Moonsault",
    power: 180,
    pp: 1,
    priority: 0,
    type: "dark",
  },
  "oceanic-operetta": {
    id: "oceanic-operetta",
    accuracy: null,
    damageClass: "special",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Oceanic Operetta",
    power: 195,
    pp: 1,
    priority: 0,
    type: "water",
  },
  "guardian-of-alola": {
    id: "guardian-of-alola",
    accuracy: null,
    damageClass: "special",
    effect: "Damages the target for 75% of its remaining HP.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Guardian of Alola",
    power: null,
    pp: 1,
    priority: 0,
    type: "fairy",
  },
  "soul-stealing-7-star-strike": {
    id: "soul-stealing-7-star-strike",
    accuracy: null,
    damageClass: "physical",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Soul-Stealing 7-Star Strike",
    power: 195,
    pp: 1,
    priority: 0,
    type: "ghost",
  },
  "stoked-sparksurfer": {
    id: "stoked-sparksurfer",
    accuracy: null,
    damageClass: "special",
    effect: "Has a $effect_chance% chance to paralyze the target.",
    effectChance: 100,
    learnedBy: [],
    meta: {
      ailment: "paralysis",
      ailmentChance: 100,
      category: "damage+ailment",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Stoked Sparksurfer",
    power: 175,
    pp: 1,
    priority: 0,
    type: "electric",
  },
  "pulverizing-pancake": {
    id: "pulverizing-pancake",
    accuracy: null,
    damageClass: "physical",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Pulverizing Pancake",
    power: 210,
    pp: 1,
    priority: 0,
    type: "normal",
  },
  "extreme-evoboost": {
    id: "extreme-evoboost",
    accuracy: null,
    damageClass: "status",
    effect: "Raises all of the user's stats by two stages.",
    effectChance: 100,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "net-good-stats",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 100,
    },
    name: "Extreme Evoboost",
    power: null,
    pp: 1,
    priority: 0,
    type: "normal",
  },
  "genesis-supernova": {
    id: "genesis-supernova",
    accuracy: null,
    damageClass: "special",
    effect: "Changes the terrain to Psychic Terrain after inflicting damage.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Genesis Supernova",
    power: 185,
    pp: 1,
    priority: 0,
    type: "psychic",
  },
  "shell-trap": {
    id: "shell-trap",
    accuracy: 100,
    damageClass: "special",
    effect:
      "Only inflicts damage if the user was hit by a physical move this turn.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Shell Trap",
    power: 150,
    pp: 5,
    priority: -3,
    type: "fire",
  },
  "fleur-cannon": {
    id: "fleur-cannon",
    accuracy: 90,
    damageClass: "special",
    effect:
      "Lowers the user's Special Attack by two stages after inflicting damage.",
    effectChance: 100,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage+raise",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 100,
    },
    name: "Fleur Cannon",
    power: 130,
    pp: 5,
    priority: 0,
    type: "fairy",
  },
  "psychic-fangs": {
    id: "psychic-fangs",
    accuracy: 100,
    damageClass: "physical",
    effect: "Destroys Reflect and Light Screen.",
    effectChance: null,
    learnedBy: [58, 59, 142, 151],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Psychic Fangs",
    power: 85,
    pp: 10,
    priority: 0,
    type: "psychic",
  },
  "stomping-tantrum": {
    id: "stomping-tantrum",
    accuracy: 100,
    damageClass: "physical",
    effect: "Has double power if the user's last move failed.",
    effectChance: null,
    learnedBy: [
      3, 20, 24, 27, 28, 30, 31, 33, 34, 50, 51, 56, 57, 67, 68, 75, 76, 85, 95,
      99, 103, 104, 105, 106, 108, 111, 112, 113, 128, 143, 149, 150, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Stomping Tantrum",
    power: 75,
    pp: 10,
    priority: 0,
    type: "ground",
  },
  "shadow-bone": {
    id: "shadow-bone",
    accuracy: 100,
    damageClass: "physical",
    effect:
      "Has a $effect_chance% chance to lower the target's Defense by one stage.",
    effectChance: 20,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage+lower",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 20,
    },
    name: "Shadow Bone",
    power: 85,
    pp: 10,
    priority: 0,
    type: "ghost",
  },
  accelerock: {
    id: "accelerock",
    accuracy: 100,
    damageClass: "physical",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Accelerock",
    power: 40,
    pp: 20,
    priority: 1,
    type: "rock",
  },
  liquidation: {
    id: "liquidation",
    accuracy: 100,
    damageClass: "physical",
    effect:
      "Has a $effect_chance% chance to lower the target's Defense by one stage.",
    effectChance: 20,
    learnedBy: [
      9, 54, 55, 62, 79, 80, 87, 90, 91, 98, 99, 116, 117, 131, 134, 139, 140,
      141, 151,
    ],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage+lower",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 20,
    },
    name: "Liquidation",
    power: 85,
    pp: 10,
    priority: 0,
    type: "water",
  },
  "prismatic-laser": {
    id: "prismatic-laser",
    accuracy: 100,
    damageClass: "special",
    effect: "User foregoes its next turn to recharge.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Prismatic Laser",
    power: 160,
    pp: 10,
    priority: 0,
    type: "psychic",
  },
  "spectral-thief": {
    id: "spectral-thief",
    accuracy: 100,
    damageClass: "physical",
    effect: "Steals the target's stat increases, then inflicts damage.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Spectral Thief",
    power: 90,
    pp: 10,
    priority: 0,
    type: "ghost",
  },
  "sunsteel-strike": {
    id: "sunsteel-strike",
    accuracy: 100,
    damageClass: "physical",
    effect: "Cannot be disrupted by abilities.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Sunsteel Strike",
    power: 100,
    pp: 5,
    priority: 0,
    type: "steel",
  },
  "moongeist-beam": {
    id: "moongeist-beam",
    accuracy: 100,
    damageClass: "special",
    effect: "Cannot be disrupted by abilities.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Moongeist Beam",
    power: 100,
    pp: 5,
    priority: 0,
    type: "ghost",
  },
  "tearful-look": {
    id: "tearful-look",
    accuracy: null,
    damageClass: "status",
    effect: "Lowers the target's Attack and Special Attack by one stage.",
    effectChance: 100,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "net-good-stats",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 100,
    },
    name: "Tearful Look",
    power: null,
    pp: 20,
    priority: 0,
    type: "normal",
  },
  "zing-zap": {
    id: "zing-zap",
    accuracy: 100,
    damageClass: "physical",
    effect: "Has a $effect_chance% chance to make the target flinch.",
    effectChance: 30,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 30,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Zing Zap",
    power: 80,
    pp: 10,
    priority: 0,
    type: "electric",
  },
  "natures-madness": {
    id: "natures-madness",
    accuracy: 90,
    damageClass: "special",
    effect: "Inflicts damage equal to half the target's HP.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Nature’s Madness",
    power: null,
    pp: 10,
    priority: 0,
    type: "fairy",
  },
  "multi-attack": {
    id: "multi-attack",
    accuracy: 100,
    damageClass: "physical",
    effect:
      "If the user is holding a appropriate plate or drive, the damage inflicted will match it.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Multi-Attack",
    power: 120,
    pp: 10,
    priority: 0,
    type: "normal",
  },
  "10-000-000-volt-thunderbolt": {
    id: "10-000-000-volt-thunderbolt",
    accuracy: null,
    damageClass: "special",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 2,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "10,000,000 Volt Thunderbolt",
    power: 195,
    pp: 1,
    priority: 0,
    type: "electric",
  },
  "mind-blown": {
    id: "mind-blown",
    accuracy: 100,
    damageClass: "special",
    effect:
      "Inflicts damage, and the user takes damage equal to half of its max HP, rounded up.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Mind Blown",
    power: 150,
    pp: 5,
    priority: 0,
    type: "fire",
  },
  "plasma-fists": {
    id: "plasma-fists",
    accuracy: 100,
    damageClass: "physical",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Plasma Fists",
    power: 100,
    pp: 15,
    priority: 0,
    type: "electric",
  },
  "photon-geyser": {
    id: "photon-geyser",
    accuracy: 100,
    damageClass: "special",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Photon Geyser",
    power: 100,
    pp: 5,
    priority: 0,
    type: "psychic",
  },
  "light-that-burns-the-sky": {
    id: "light-that-burns-the-sky",
    accuracy: null,
    damageClass: "special",
    effect:
      "Inflicts damage using either Attack or Special Attack stat, whichever is higher.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Light That Burns the Sky",
    power: 200,
    pp: 1,
    priority: 0,
    type: "psychic",
  },
  "searing-sunraze-smash": {
    id: "searing-sunraze-smash",
    accuracy: null,
    damageClass: "physical",
    effect: "Cannot be disrupted by abilities.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Searing Sunraze Smash",
    power: 200,
    pp: 1,
    priority: 0,
    type: "steel",
  },
  "menacing-moonraze-maelstrom": {
    id: "menacing-moonraze-maelstrom",
    accuracy: null,
    damageClass: "special",
    effect: "Cannot be disrupted by abilities.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Menacing Moonraze Maelstrom",
    power: 200,
    pp: 1,
    priority: 0,
    type: "ghost",
  },
  "lets-snuggle-forever": {
    id: "lets-snuggle-forever",
    accuracy: null,
    damageClass: "physical",
    effect:
      "After inflicting damage, causes all Normal-type moves to become Electric-type for the remainder of the turn.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Let’s Snuggle Forever",
    power: 190,
    pp: 1,
    priority: 0,
    type: "fairy",
  },
  "splintered-stormshards": {
    id: "splintered-stormshards",
    accuracy: null,
    damageClass: "physical",
    effect:
      "Inflicts damage and removes any terrain present on the battlefield.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Splintered Stormshards",
    power: 190,
    pp: 1,
    priority: 0,
    type: "rock",
  },
  "clangorous-soulblaze": {
    id: "clangorous-soulblaze",
    accuracy: null,
    damageClass: "special",
    effect:
      "Inflicts damage to all opposing Pokémon and increases the user's Attack, Defense, Special Attack, Special Defense, and Speed by one stage each.",
    effectChance: 100,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage+raise",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 100,
    },
    name: "Clangorous Soulblaze",
    power: 185,
    pp: 1,
    priority: 0,
    type: "dragon",
  },
  "zippy-zap": {
    id: "zippy-zap",
    accuracy: 100,
    damageClass: "physical",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: 100,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage+raise",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 100,
    },
    name: "Zippy Zap",
    power: 80,
    pp: 10,
    priority: 2,
    type: "electric",
  },
  "splishy-splash": {
    id: "splishy-splash",
    accuracy: 100,
    damageClass: "special",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: 30,
    learnedBy: [],
    meta: {
      ailment: "paralysis",
      ailmentChance: 30,
      category: "damage+ailment",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Splishy Splash",
    power: 90,
    pp: 15,
    priority: 0,
    type: "water",
  },
  "floaty-fall": {
    id: "floaty-fall",
    accuracy: 95,
    damageClass: "physical",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: 30,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 30,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Floaty Fall",
    power: 90,
    pp: 15,
    priority: 0,
    type: "flying",
  },
  "pika-papow": {
    id: "pika-papow",
    accuracy: null,
    damageClass: "special",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Pika Papow",
    power: null,
    pp: 20,
    priority: 0,
    type: "electric",
  },
  "bouncy-bubble": {
    id: "bouncy-bubble",
    accuracy: 100,
    damageClass: "special",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage+heal",
      critRate: 0,
      drain: 100,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Bouncy Bubble",
    power: 60,
    pp: 20,
    priority: 0,
    type: "water",
  },
  "buzzy-buzz": {
    id: "buzzy-buzz",
    accuracy: 100,
    damageClass: "special",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: 100,
    learnedBy: [],
    meta: {
      ailment: "paralysis",
      ailmentChance: 100,
      category: "damage+ailment",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Buzzy Buzz",
    power: 60,
    pp: 20,
    priority: 0,
    type: "electric",
  },
  "sizzly-slide": {
    id: "sizzly-slide",
    accuracy: 100,
    damageClass: "physical",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: 100,
    learnedBy: [],
    meta: {
      ailment: "burn",
      ailmentChance: 100,
      category: "damage+ailment",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Sizzly Slide",
    power: 60,
    pp: 20,
    priority: 0,
    type: "fire",
  },
  "glitzy-glow": {
    id: "glitzy-glow",
    accuracy: 95,
    damageClass: "special",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Glitzy Glow",
    power: 80,
    pp: 15,
    priority: 0,
    type: "psychic",
  },
  "baddy-bad": {
    id: "baddy-bad",
    accuracy: 95,
    damageClass: "special",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Baddy Bad",
    power: 80,
    pp: 15,
    priority: 0,
    type: "dark",
  },
  "sappy-seed": {
    id: "sappy-seed",
    accuracy: 90,
    damageClass: "physical",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: 100,
    learnedBy: [],
    meta: {
      ailment: "leech-seed",
      ailmentChance: 100,
      category: "damage+ailment",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Sappy Seed",
    power: 100,
    pp: 10,
    priority: 0,
    type: "grass",
  },
  "freezy-frost": {
    id: "freezy-frost",
    accuracy: 90,
    damageClass: "special",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Freezy Frost",
    power: 100,
    pp: 10,
    priority: 0,
    type: "ice",
  },
  "sparkly-swirl": {
    id: "sparkly-swirl",
    accuracy: 85,
    damageClass: "special",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Sparkly Swirl",
    power: 120,
    pp: 5,
    priority: 0,
    type: "fairy",
  },
  "veevee-volley": {
    id: "veevee-volley",
    accuracy: null,
    damageClass: "physical",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Veevee Volley",
    power: null,
    pp: 20,
    priority: 0,
    type: "normal",
  },
  "double-iron-bash": {
    id: "double-iron-bash",
    accuracy: 100,
    damageClass: "physical",
    effect: "Hits twice in one turn.",
    effectChance: 30,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 30,
      healing: 0,
      maxHits: 2,
      maxTurns: null,
      minHits: 2,
      minTurns: null,
      statChance: 0,
    },
    name: "Double Iron Bash",
    power: 60,
    pp: 5,
    priority: 0,
    type: "steel",
  },
  "max-guard": {
    id: "max-guard",
    accuracy: null,
    damageClass: "status",
    effect: "Prevents any moves from hitting the user this turn.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "unique",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Max Guard",
    power: null,
    pp: 10,
    priority: 4,
    type: "normal",
  },
  "dynamax-cannon": {
    id: "dynamax-cannon",
    accuracy: 100,
    damageClass: "special",
    effect:
      "Sets Light Screen on the user's side of the field after inflicting damage.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Dynamax Cannon",
    power: 100,
    pp: 5,
    priority: 0,
    type: "dragon",
  },
  "snipe-shot": {
    id: "snipe-shot",
    accuracy: 100,
    damageClass: "special",
    effect:
      "Sets Reflect on the user's side of the field after inflicting damage.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 1,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Snipe Shot",
    power: 80,
    pp: 15,
    priority: 0,
    type: "water",
  },
  "jaw-lock": {
    id: "jaw-lock",
    accuracy: 100,
    damageClass: "physical",
    effect: "Seeds the target after inflicting damage.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Jaw Lock",
    power: 80,
    pp: 10,
    priority: 0,
    type: "dark",
  },
  "stuff-cheeks": {
    id: "stuff-cheeks",
    accuracy: null,
    damageClass: "status",
    effect:
      "Cures the entire party of major status effects after inflicting damage.",
    effectChance: 100,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "net-good-stats",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 100,
    },
    name: "Stuff Cheeks",
    power: null,
    pp: 10,
    priority: 0,
    type: "normal",
  },
  "no-retreat": {
    id: "no-retreat",
    accuracy: null,
    damageClass: "status",
    effect:
      "Hits twice in one turn, with a $effect_chance% chance to make the target flinch.",
    effectChance: 100,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "net-good-stats",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 100,
    },
    name: "No Retreat",
    power: null,
    pp: 5,
    priority: 0,
    type: "fighting",
  },
  "tar-shot": {
    id: "tar-shot",
    accuracy: 100,
    damageClass: "status",
    effect: "",
    effectChance: 100,
    learnedBy: [],
    meta: {
      ailment: "tar-shot",
      ailmentChance: 100,
      category: "swagger",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 100,
    },
    name: "Tar Shot",
    power: null,
    pp: 15,
    priority: 0,
    type: "rock",
  },
  "magic-powder": {
    id: "magic-powder",
    accuracy: 100,
    damageClass: "status",
    effect: "",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "unique",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Magic Powder",
    power: null,
    pp: 20,
    priority: 0,
    type: "psychic",
  },
  "dragon-darts": {
    id: "dragon-darts",
    accuracy: 100,
    damageClass: "physical",
    effect: "",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: 2,
      maxTurns: null,
      minHits: 2,
      minTurns: null,
      statChance: 0,
    },
    name: "Dragon Darts",
    power: 50,
    pp: 10,
    priority: 0,
    type: "dragon",
  },
  teatime: {
    id: "teatime",
    accuracy: null,
    damageClass: "status",
    effect: "",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "unique",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Teatime",
    power: null,
    pp: 10,
    priority: 0,
    type: "normal",
  },
  octolock: {
    id: "octolock",
    accuracy: 100,
    damageClass: "status",
    effect: "",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "unique",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Octolock",
    power: null,
    pp: 15,
    priority: 0,
    type: "fighting",
  },
  "bolt-beak": {
    id: "bolt-beak",
    accuracy: 100,
    damageClass: "physical",
    effect: "",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Bolt Beak",
    power: 85,
    pp: 10,
    priority: 0,
    type: "electric",
  },
  "fishious-rend": {
    id: "fishious-rend",
    accuracy: 100,
    damageClass: "physical",
    effect: "",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Fishious Rend",
    power: 85,
    pp: 10,
    priority: 0,
    type: "water",
  },
  "court-change": {
    id: "court-change",
    accuracy: 100,
    damageClass: "status",
    effect: "",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "unique",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Court Change",
    power: null,
    pp: 10,
    priority: 0,
    type: "normal",
  },
  "max-flare": {
    id: "max-flare",
    accuracy: null,
    damageClass: "physical",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Max Flare",
    power: 100,
    pp: 10,
    priority: 0,
    type: "fire",
  },
  "max-flutterby": {
    id: "max-flutterby",
    accuracy: null,
    damageClass: "physical",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Max Flutterby",
    power: 10,
    pp: 10,
    priority: 0,
    type: "bug",
  },
  "max-lightning": {
    id: "max-lightning",
    accuracy: null,
    damageClass: "physical",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Max Lightning",
    power: 10,
    pp: 10,
    priority: 0,
    type: "electric",
  },
  "max-strike": {
    id: "max-strike",
    accuracy: null,
    damageClass: "physical",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Max Strike",
    power: 10,
    pp: 10,
    priority: 0,
    type: "normal",
  },
  "max-knuckle": {
    id: "max-knuckle",
    accuracy: null,
    damageClass: "physical",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Max Knuckle",
    power: 10,
    pp: 10,
    priority: 0,
    type: "fighting",
  },
  "max-phantasm": {
    id: "max-phantasm",
    accuracy: null,
    damageClass: "physical",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Max Phantasm",
    power: 10,
    pp: 10,
    priority: 0,
    type: "ghost",
  },
  "max-hailstorm": {
    id: "max-hailstorm",
    accuracy: null,
    damageClass: "physical",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Max Hailstorm",
    power: 10,
    pp: 10,
    priority: 0,
    type: "ice",
  },
  "max-ooze": {
    id: "max-ooze",
    accuracy: null,
    damageClass: "physical",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Max Ooze",
    power: 10,
    pp: 10,
    priority: 0,
    type: "poison",
  },
  "max-geyser": {
    id: "max-geyser",
    accuracy: null,
    damageClass: "physical",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Max Geyser",
    power: 10,
    pp: 10,
    priority: 0,
    type: "water",
  },
  "max-airstream": {
    id: "max-airstream",
    accuracy: null,
    damageClass: "physical",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Max Airstream",
    power: 10,
    pp: 10,
    priority: 0,
    type: "flying",
  },
  "max-starfall": {
    id: "max-starfall",
    accuracy: null,
    damageClass: "physical",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Max Starfall",
    power: 10,
    pp: 10,
    priority: 0,
    type: "fairy",
  },
  "max-wyrmwind": {
    id: "max-wyrmwind",
    accuracy: null,
    damageClass: "physical",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Max Wyrmwind",
    power: 10,
    pp: 10,
    priority: 0,
    type: "dragon",
  },
  "max-mindstorm": {
    id: "max-mindstorm",
    accuracy: null,
    damageClass: "physical",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Max Mindstorm",
    power: 10,
    pp: 10,
    priority: 0,
    type: "psychic",
  },
  "max-rockfall": {
    id: "max-rockfall",
    accuracy: null,
    damageClass: "physical",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Max Rockfall",
    power: 10,
    pp: 10,
    priority: 0,
    type: "rock",
  },
  "max-quake": {
    id: "max-quake",
    accuracy: null,
    damageClass: "physical",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Max Quake",
    power: 10,
    pp: 10,
    priority: 0,
    type: "ground",
  },
  "max-darkness": {
    id: "max-darkness",
    accuracy: null,
    damageClass: "physical",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Max Darkness",
    power: 10,
    pp: 10,
    priority: 0,
    type: "dark",
  },
  "max-overgrowth": {
    id: "max-overgrowth",
    accuracy: null,
    damageClass: "physical",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Max Overgrowth",
    power: 10,
    pp: 10,
    priority: 0,
    type: "grass",
  },
  "max-steelspike": {
    id: "max-steelspike",
    accuracy: null,
    damageClass: "physical",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Max Steelspike",
    power: 10,
    pp: 10,
    priority: 0,
    type: "steel",
  },
  "clangorous-soul": {
    id: "clangorous-soul",
    accuracy: 100,
    damageClass: "status",
    effect: "",
    effectChance: 100,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "net-good-stats",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: -33,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 100,
    },
    name: "Clangorous Soul",
    power: null,
    pp: 5,
    priority: 0,
    type: "dragon",
  },
  "body-press": {
    id: "body-press",
    accuracy: 100,
    damageClass: "physical",
    effect: "",
    effectChance: null,
    learnedBy: [9, 31, 34, 39, 40, 80, 95, 97, 108, 112, 131, 143, 149, 151],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Body Press",
    power: 80,
    pp: 10,
    priority: 0,
    type: "fighting",
  },
  decorate: {
    id: "decorate",
    accuracy: null,
    damageClass: "status",
    effect: "",
    effectChance: 100,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "net-good-stats",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 100,
    },
    name: "Decorate",
    power: null,
    pp: 15,
    priority: 0,
    type: "fairy",
  },
  "drum-beating": {
    id: "drum-beating",
    accuracy: 100,
    damageClass: "physical",
    effect:
      "Has a $effect_chance% chance to lower the target's Speed by one stage.",
    effectChance: 100,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage+lower",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 100,
    },
    name: "Drum Beating",
    power: 80,
    pp: 10,
    priority: 0,
    type: "grass",
  },
  "snap-trap": {
    id: "snap-trap",
    accuracy: 100,
    damageClass: "physical",
    effect:
      "Prevents the target from fleeing and inflicts damage for 2-5 turns.",
    effectChance: 100,
    learnedBy: [],
    meta: {
      ailment: "trap",
      ailmentChance: 100,
      category: "damage+ailment",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: 6,
      minHits: null,
      minTurns: 5,
      statChance: 0,
    },
    name: "Snap Trap",
    power: 35,
    pp: 15,
    priority: 0,
    type: "grass",
  },
  "pyro-ball": {
    id: "pyro-ball",
    accuracy: 90,
    damageClass: "physical",
    effect: "Has a $effect_chance% chance to burn the target.",
    effectChance: 10,
    learnedBy: [],
    meta: {
      ailment: "burn",
      ailmentChance: 10,
      category: "damage+ailment",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Pyro Ball",
    power: 120,
    pp: 5,
    priority: 0,
    type: "fire",
  },
  "behemoth-blade": {
    id: "behemoth-blade",
    accuracy: 100,
    damageClass: "physical",
    effect: "",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Behemoth Blade",
    power: 100,
    pp: 5,
    priority: 0,
    type: "steel",
  },
  "behemoth-bash": {
    id: "behemoth-bash",
    accuracy: 100,
    damageClass: "physical",
    effect: "",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Behemoth Bash",
    power: 100,
    pp: 5,
    priority: 0,
    type: "steel",
  },
  "aura-wheel": {
    id: "aura-wheel",
    accuracy: 100,
    damageClass: "physical",
    effect: "",
    effectChance: 100,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage+raise",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 100,
    },
    name: "Aura Wheel",
    power: 110,
    pp: 10,
    priority: 0,
    type: "electric",
  },
  "breaking-swipe": {
    id: "breaking-swipe",
    accuracy: 100,
    damageClass: "physical",
    effect: "Lowers the target's Attack by one stage after inflicting damage.",
    effectChance: 100,
    learnedBy: [6, 95, 112, 147, 148, 149, 151],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage+lower",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 100,
    },
    name: "Breaking Swipe",
    power: 60,
    pp: 15,
    priority: 0,
    type: "dragon",
  },
  "branch-poke": {
    id: "branch-poke",
    accuracy: 100,
    damageClass: "physical",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Branch Poke",
    power: 40,
    pp: 40,
    priority: 0,
    type: "grass",
  },
  overdrive: {
    id: "overdrive",
    accuracy: 100,
    damageClass: "special",
    effect: "",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Overdrive",
    power: 80,
    pp: 10,
    priority: 0,
    type: "electric",
  },
  "apple-acid": {
    id: "apple-acid",
    accuracy: 100,
    damageClass: "special",
    effect: "",
    effectChance: 100,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage+lower",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 100,
    },
    name: "Apple Acid",
    power: 80,
    pp: 10,
    priority: 0,
    type: "grass",
  },
  "grav-apple": {
    id: "grav-apple",
    accuracy: 100,
    damageClass: "physical",
    effect: "Lowers the target's Defense by one stage after inflicting damage.",
    effectChance: 100,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage+lower",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 100,
    },
    name: "Grav Apple",
    power: 80,
    pp: 10,
    priority: 0,
    type: "grass",
  },
  "spirit-break": {
    id: "spirit-break",
    accuracy: 100,
    damageClass: "physical",
    effect:
      "Has a $effect_chance% chance to lower the target's Special Attack by one stage.",
    effectChance: 100,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage+lower",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 100,
    },
    name: "Spirit Break",
    power: 75,
    pp: 15,
    priority: 0,
    type: "fairy",
  },
  "strange-steam": {
    id: "strange-steam",
    accuracy: 95,
    damageClass: "special",
    effect: "Has a $effect_chance% chance to confuse the target.",
    effectChance: 20,
    learnedBy: [],
    meta: {
      ailment: "confusion",
      ailmentChance: 20,
      category: "damage+ailment",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: 5,
      minHits: null,
      minTurns: 2,
      statChance: 0,
    },
    name: "Strange Steam",
    power: 90,
    pp: 10,
    priority: 0,
    type: "fairy",
  },
  "life-dew": {
    id: "life-dew",
    accuracy: null,
    damageClass: "status",
    effect: "",
    effectChance: null,
    learnedBy: [7, 35, 36, 90, 113, 131, 150, 151],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "heal",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 25,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Life Dew",
    power: null,
    pp: 10,
    priority: 0,
    type: "water",
  },
  obstruct: {
    id: "obstruct",
    accuracy: 100,
    damageClass: "status",
    effect: "",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "unique",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Obstruct",
    power: null,
    pp: 10,
    priority: 4,
    type: "dark",
  },
  "false-surrender": {
    id: "false-surrender",
    accuracy: null,
    damageClass: "physical",
    effect: "Never misses.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "False Surrender",
    power: 80,
    pp: 10,
    priority: 0,
    type: "dark",
  },
  "meteor-assault": {
    id: "meteor-assault",
    accuracy: 100,
    damageClass: "physical",
    effect: "User foregoes its next turn to recharge.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Meteor Assault",
    power: 150,
    pp: 5,
    priority: 0,
    type: "fighting",
  },
  eternabeam: {
    id: "eternabeam",
    accuracy: 90,
    damageClass: "special",
    effect: "User foregoes its next turn to recharge.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Eternabeam",
    power: 160,
    pp: 5,
    priority: 0,
    type: "dragon",
  },
  "steel-beam": {
    id: "steel-beam",
    accuracy: 95,
    damageClass: "special",
    effect:
      "Inflicts damage, and the user takes damage equal to half of its max HP, rounded up.",
    effectChance: null,
    learnedBy: [81, 82, 151],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Steel Beam",
    power: 140,
    pp: 5,
    priority: 0,
    type: "steel",
  },
  "expanding-force": {
    id: "expanding-force",
    accuracy: 100,
    damageClass: "special",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [40, 64, 65, 79, 80, 103, 121, 122, 124, 150, 151],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Expanding Force",
    power: 80,
    pp: 10,
    priority: 0,
    type: "psychic",
  },
  "steel-roller": {
    id: "steel-roller",
    accuracy: 100,
    damageClass: "physical",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [27, 28, 39, 40, 91, 108, 143, 151],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Steel Roller",
    power: 130,
    pp: 5,
    priority: 0,
    type: "steel",
  },
  "scale-shot": {
    id: "scale-shot",
    accuracy: 90,
    damageClass: "physical",
    effect:
      "Boosts the user's Speed and lowers their Defense by one stage after inflicting damage two to five times in a row.",
    effectChance: null,
    learnedBy: [6, 116, 117, 118, 119, 130, 147, 148, 149, 151],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: 5,
      maxTurns: null,
      minHits: 2,
      minTurns: null,
      statChance: 0,
    },
    name: "Scale Shot",
    power: 25,
    pp: 20,
    priority: 0,
    type: "dragon",
  },
  "meteor-beam": {
    id: "meteor-beam",
    accuracy: 90,
    damageClass: "special",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: 100,
    learnedBy: [35, 36, 95, 112, 121, 138, 139, 140, 141, 142, 151],
    meta: {
      ailment: "none",
      ailmentChance: 100,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Meteor Beam",
    power: 120,
    pp: 10,
    priority: 0,
    type: "rock",
  },
  "shell-side-arm": {
    id: "shell-side-arm",
    accuracy: 100,
    damageClass: "special",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: 20,
    learnedBy: [],
    meta: {
      ailment: "poison",
      ailmentChance: 20,
      category: "damage+ailment",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Shell Side Arm",
    power: 90,
    pp: 10,
    priority: 0,
    type: "poison",
  },
  "misty-explosion": {
    id: "misty-explosion",
    accuracy: 100,
    damageClass: "special",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [35, 36, 39, 40, 151],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Misty Explosion",
    power: 100,
    pp: 5,
    priority: 0,
    type: "fairy",
  },
  "grassy-glide": {
    id: "grassy-glide",
    accuracy: 100,
    damageClass: "physical",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [1, 2, 3, 43, 44, 45, 102, 103, 114, 151],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Grassy Glide",
    power: 70,
    pp: 20,
    priority: 0,
    type: "grass",
  },
  "rising-voltage": {
    id: "rising-voltage",
    accuracy: 100,
    damageClass: "special",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [25, 26, 81, 82, 125, 135, 145, 151],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Rising Voltage",
    power: 70,
    pp: 20,
    priority: 0,
    type: "electric",
  },
  "terrain-pulse": {
    id: "terrain-pulse",
    accuracy: 100,
    damageClass: "special",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [3, 9, 103, 108, 115, 143, 151],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Terrain Pulse",
    power: 50,
    pp: 10,
    priority: 0,
    type: "normal",
  },
  "skitter-smack": {
    id: "skitter-smack",
    accuracy: 90,
    damageClass: "physical",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: 100,
    learnedBy: [53, 92, 93, 94, 151],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage+lower",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 100,
    },
    name: "Skitter Smack",
    power: 70,
    pp: 10,
    priority: 0,
    type: "bug",
  },
  "burning-jealousy": {
    id: "burning-jealousy",
    accuracy: 100,
    damageClass: "special",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: 100,
    learnedBy: [37, 38, 126, 136, 146, 151],
    meta: {
      ailment: "burn",
      ailmentChance: 100,
      category: "damage+ailment",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Burning Jealousy",
    power: 70,
    pp: 5,
    priority: 0,
    type: "fire",
  },
  "lash-out": {
    id: "lash-out",
    accuracy: 100,
    damageClass: "physical",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [52, 53, 128, 130, 151],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Lash Out",
    power: 75,
    pp: 5,
    priority: 0,
    type: "dark",
  },
  poltergeist: {
    id: "poltergeist",
    accuracy: 90,
    damageClass: "physical",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [92, 93, 94, 151],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Poltergeist",
    power: 110,
    pp: 5,
    priority: 0,
    type: "ghost",
  },
  "corrosive-gas": {
    id: "corrosive-gas",
    accuracy: 100,
    damageClass: "status",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [45, 73, 92, 93, 94, 109, 110, 151],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "unique",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Corrosive Gas",
    power: null,
    pp: 40,
    priority: 0,
    type: "poison",
  },
  coaching: {
    id: "coaching",
    accuracy: null,
    damageClass: "status",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: 100,
    learnedBy: [62, 66, 67, 68, 106, 107, 151],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "net-good-stats",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 100,
    },
    name: "Coaching",
    power: null,
    pp: 10,
    priority: 0,
    type: "fighting",
  },
  "flip-turn": {
    id: "flip-turn",
    accuracy: 100,
    damageClass: "physical",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [7, 8, 9, 55, 116, 117, 118, 119, 120, 121, 134, 141, 151],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Flip Turn",
    power: 60,
    pp: 20,
    priority: 0,
    type: "water",
  },
  "triple-axel": {
    id: "triple-axel",
    accuracy: 90,
    damageClass: "physical",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [124, 144, 151],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: 3,
      maxTurns: null,
      minHits: 3,
      minTurns: null,
      statChance: 0,
    },
    name: "Triple Axel",
    power: 20,
    pp: 10,
    priority: 0,
    type: "ice",
  },
  "dual-wingbeat": {
    id: "dual-wingbeat",
    accuracy: 90,
    damageClass: "physical",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [6, 12, 35, 36, 41, 42, 83, 123, 142, 144, 145, 146, 149, 151],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: 2,
      maxTurns: null,
      minHits: 2,
      minTurns: null,
      statChance: 0,
    },
    name: "Dual Wingbeat",
    power: 40,
    pp: 10,
    priority: 0,
    type: "flying",
  },
  "scorching-sands": {
    id: "scorching-sands",
    accuracy: 100,
    damageClass: "special",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: 30,
    learnedBy: [
      6, 27, 28, 31, 34, 38, 50, 51, 59, 78, 95, 104, 105, 111, 112, 126, 136,
      146, 151,
    ],
    meta: {
      ailment: "burn",
      ailmentChance: 30,
      category: "damage+ailment",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Scorching Sands",
    power: 70,
    pp: 10,
    priority: 0,
    type: "ground",
  },
  "jungle-healing": {
    id: "jungle-healing",
    accuracy: null,
    damageClass: "status",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "unique",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 25,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Jungle Healing",
    power: null,
    pp: 10,
    priority: 0,
    type: "grass",
  },
  "wicked-blow": {
    id: "wicked-blow",
    accuracy: 100,
    damageClass: "physical",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 6,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Wicked Blow",
    power: 80,
    pp: 5,
    priority: 0,
    type: "dark",
  },
  "surging-strikes": {
    id: "surging-strikes",
    accuracy: 100,
    damageClass: "physical",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 6,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: 3,
      maxTurns: null,
      minHits: 3,
      minTurns: null,
      statChance: 0,
    },
    name: "Surging Strikes",
    power: 25,
    pp: 5,
    priority: 0,
    type: "water",
  },
  "thunder-cage": {
    id: "thunder-cage",
    accuracy: 90,
    damageClass: "special",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: 100,
    learnedBy: [],
    meta: {
      ailment: "trap",
      ailmentChance: 100,
      category: "damage+ailment",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: 6,
      minHits: null,
      minTurns: 5,
      statChance: 0,
    },
    name: "Thunder Cage",
    power: 80,
    pp: 15,
    priority: 0,
    type: "electric",
  },
  "dragon-energy": {
    id: "dragon-energy",
    accuracy: 100,
    damageClass: "special",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Dragon Energy",
    power: 150,
    pp: 5,
    priority: 0,
    type: "dragon",
  },
  "freezing-glare": {
    id: "freezing-glare",
    accuracy: 100,
    damageClass: "special",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: 10,
    learnedBy: [],
    meta: {
      ailment: "freeze",
      ailmentChance: 10,
      category: "damage+ailment",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Freezing Glare",
    power: 90,
    pp: 10,
    priority: 0,
    type: "psychic",
  },
  "fiery-wrath": {
    id: "fiery-wrath",
    accuracy: 100,
    damageClass: "special",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: 20,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 20,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Fiery Wrath",
    power: 90,
    pp: 10,
    priority: 0,
    type: "dark",
  },
  "thunderous-kick": {
    id: "thunderous-kick",
    accuracy: 100,
    damageClass: "physical",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: 100,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage+lower",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 100,
    },
    name: "Thunderous Kick",
    power: 90,
    pp: 10,
    priority: 0,
    type: "fighting",
  },
  "glacial-lance": {
    id: "glacial-lance",
    accuracy: 100,
    damageClass: "physical",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Glacial Lance",
    power: 130,
    pp: 5,
    priority: 0,
    type: "ice",
  },
  "astral-barrage": {
    id: "astral-barrage",
    accuracy: 100,
    damageClass: "special",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Astral Barrage",
    power: 120,
    pp: 5,
    priority: 0,
    type: "ghost",
  },
  "eerie-spell": {
    id: "eerie-spell",
    accuracy: 100,
    damageClass: "special",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [],
    meta: {
      ailment: "none",
      ailmentChance: 0,
      category: "damage",
      critRate: 0,
      drain: 0,
      flinchChance: 0,
      healing: 0,
      maxHits: null,
      maxTurns: null,
      minHits: null,
      minTurns: null,
      statChance: 0,
    },
    name: "Eerie Spell",
    power: 80,
    pp: 5,
    priority: 0,
    type: "psychic",
  },
  "dire-claw": {
    id: "dire-claw",
    accuracy: 100,
    damageClass: "physical",
    effect: "",
    effectChance: null,
    learnedBy: [],
    meta: null,
    name: "Dire Claw",
    power: 80,
    pp: 15,
    priority: 0,
    type: "poison",
  },
  "psyshield-bash": {
    id: "psyshield-bash",
    accuracy: 90,
    damageClass: "physical",
    effect: "",
    effectChance: null,
    learnedBy: [],
    meta: null,
    name: "Psyshield Bash",
    power: 70,
    pp: 10,
    priority: 0,
    type: "psychic",
  },
  "power-shift": {
    id: "power-shift",
    accuracy: null,
    damageClass: "status",
    effect: "",
    effectChance: null,
    learnedBy: [],
    meta: null,
    name: "Power Shift",
    power: 0,
    pp: 10,
    priority: 0,
    type: "normal",
  },
  "stone-axe": {
    id: "stone-axe",
    accuracy: 90,
    damageClass: "physical",
    effect: "",
    effectChance: null,
    learnedBy: [],
    meta: null,
    name: "Stone Axe",
    power: 65,
    pp: 15,
    priority: 0,
    type: "rock",
  },
  "springtide-storm": {
    id: "springtide-storm",
    accuracy: 80,
    damageClass: "special",
    effect: "",
    effectChance: null,
    learnedBy: [],
    meta: null,
    name: "Springtide Storm",
    power: 100,
    pp: 5,
    priority: 0,
    type: "fairy",
  },
  "mystical-power": {
    id: "mystical-power",
    accuracy: 90,
    damageClass: "special",
    effect: "",
    effectChance: null,
    learnedBy: [],
    meta: null,
    name: "Mystical Power",
    power: 70,
    pp: 10,
    priority: 0,
    type: "psychic",
  },
  "raging-fury": {
    id: "raging-fury",
    accuracy: 100,
    damageClass: "physical",
    effect: "",
    effectChance: null,
    learnedBy: [58],
    meta: null,
    name: "Raging Fury",
    power: 120,
    pp: 10,
    priority: 0,
    type: "fire",
  },
  "wave-crash": {
    id: "wave-crash",
    accuracy: 100,
    damageClass: "physical",
    effect: "",
    effectChance: null,
    learnedBy: [],
    meta: null,
    name: "Wave Crash",
    power: 120,
    pp: 10,
    priority: 0,
    type: "water",
  },
  chloroblast: {
    id: "chloroblast",
    accuracy: 95,
    damageClass: "special",
    effect: "",
    effectChance: null,
    learnedBy: [],
    meta: null,
    name: "Chloroblast",
    power: 150,
    pp: 5,
    priority: 0,
    type: "grass",
  },
  "mountain-gale": {
    id: "mountain-gale",
    accuracy: 85,
    damageClass: "physical",
    effect: "",
    effectChance: null,
    learnedBy: [],
    meta: null,
    name: "Mountain Gale",
    power: 100,
    pp: 10,
    priority: 0,
    type: "ice",
  },
  "victory-dance": {
    id: "victory-dance",
    accuracy: null,
    damageClass: "status",
    effect: "",
    effectChance: null,
    learnedBy: [],
    meta: null,
    name: "Victory Dance",
    power: 0,
    pp: 10,
    priority: 0,
    type: "fighting",
  },
  "headlong-rush": {
    id: "headlong-rush",
    accuracy: 100,
    damageClass: "physical",
    effect: "",
    effectChance: null,
    learnedBy: [],
    meta: null,
    name: "Headlong Rush",
    power: 120,
    pp: 5,
    priority: 0,
    type: "ground",
  },
  "barb-barrage": {
    id: "barb-barrage",
    accuracy: 100,
    damageClass: "physical",
    effect: "",
    effectChance: null,
    learnedBy: [],
    meta: null,
    name: "Barb Barrage",
    power: 60,
    pp: 10,
    priority: 0,
    type: "poison",
  },
  "esper-wing": {
    id: "esper-wing",
    accuracy: 100,
    damageClass: "special",
    effect: "",
    effectChance: null,
    learnedBy: [],
    meta: null,
    name: "Esper Wing",
    power: 80,
    pp: 10,
    priority: 0,
    type: "psychic",
  },
  "bitter-malice": {
    id: "bitter-malice",
    accuracy: 100,
    damageClass: "special",
    effect: "",
    effectChance: null,
    learnedBy: [],
    meta: null,
    name: "Bitter Malice",
    power: 75,
    pp: 10,
    priority: 0,
    type: "ghost",
  },
  shelter: {
    id: "shelter",
    accuracy: null,
    damageClass: "status",
    effect: "",
    effectChance: null,
    learnedBy: [],
    meta: null,
    name: "Shelter",
    power: 0,
    pp: 10,
    priority: 0,
    type: "steel",
  },
  "triple-arrows": {
    id: "triple-arrows",
    accuracy: 100,
    damageClass: "physical",
    effect: "",
    effectChance: null,
    learnedBy: [],
    meta: null,
    name: "Triple Arrows",
    power: 90,
    pp: 10,
    priority: 0,
    type: "fighting",
  },
  "infernal-parade": {
    id: "infernal-parade",
    accuracy: 100,
    damageClass: "special",
    effect: "",
    effectChance: null,
    learnedBy: [],
    meta: null,
    name: "Infernal Parade",
    power: 60,
    pp: 15,
    priority: 0,
    type: "ghost",
  },
  "ceaseless-edge": {
    id: "ceaseless-edge",
    accuracy: 90,
    damageClass: "physical",
    effect: "",
    effectChance: null,
    learnedBy: [],
    meta: null,
    name: "Ceaseless Edge",
    power: 65,
    pp: 15,
    priority: 0,
    type: "dark",
  },
  "bleakwind-storm": {
    id: "bleakwind-storm",
    accuracy: 80,
    damageClass: "special",
    effect: "",
    effectChance: null,
    learnedBy: [],
    meta: null,
    name: "Bleakwind Storm",
    power: 100,
    pp: 10,
    priority: 0,
    type: "flying",
  },
  "wildbolt-storm": {
    id: "wildbolt-storm",
    accuracy: 80,
    damageClass: "special",
    effect: "",
    effectChance: null,
    learnedBy: [],
    meta: null,
    name: "Wildbolt Storm",
    power: 100,
    pp: 10,
    priority: 0,
    type: "electric",
  },
  "sandsear-storm": {
    id: "sandsear-storm",
    accuracy: 80,
    damageClass: "special",
    effect: "",
    effectChance: null,
    learnedBy: [],
    meta: null,
    name: "Sandsear Storm",
    power: 100,
    pp: 10,
    priority: 0,
    type: "ground",
  },
  "lunar-blessing": {
    id: "lunar-blessing",
    accuracy: null,
    damageClass: "status",
    effect: "",
    effectChance: null,
    learnedBy: [],
    meta: null,
    name: "Lunar Blessing",
    power: 0,
    pp: 5,
    priority: 0,
    type: "psychic",
  },
  "take-heart": {
    id: "take-heart",
    accuracy: null,
    damageClass: "status",
    effect: "",
    effectChance: null,
    learnedBy: [],
    meta: null,
    name: "Take Heart",
    power: 0,
    pp: 10,
    priority: 0,
    type: "psychic",
  },
  "tera-blast": {
    id: "tera-blast",
    accuracy: 100,
    damageClass: "special",
    effect: "",
    effectChance: null,
    learnedBy: [
      4, 5, 6, 25, 26, 39, 40, 48, 49, 50, 51, 52, 53, 54, 55, 56, 57, 58, 59,
      79, 80, 81, 82, 88, 89, 90, 91, 92, 93, 94, 96, 97, 100, 101, 113, 123,
      128, 130, 133, 134, 135, 136, 144, 145, 146, 147, 148, 149, 150, 151,
    ],
    meta: null,
    name: "Tera Blast",
    power: 80,
    pp: 10,
    priority: 0,
    type: "normal",
  },
  "silk-trap": {
    id: "silk-trap",
    accuracy: null,
    damageClass: "status",
    effect: "",
    effectChance: null,
    learnedBy: [],
    meta: null,
    name: "Silk Trap",
    power: 0,
    pp: 10,
    priority: 4,
    type: "bug",
  },
  "axe-kick": {
    id: "axe-kick",
    accuracy: 90,
    damageClass: "physical",
    effect: "",
    effectChance: null,
    learnedBy: [],
    meta: null,
    name: "Axe Kick",
    power: 120,
    pp: 10,
    priority: 0,
    type: "fighting",
  },
  "last-respects": {
    id: "last-respects",
    accuracy: 100,
    damageClass: "physical",
    effect: "",
    effectChance: null,
    learnedBy: [],
    meta: null,
    name: "Last Respects",
    power: 50,
    pp: 10,
    priority: 0,
    type: "ghost",
  },
  "lumina-crash": {
    id: "lumina-crash",
    accuracy: 100,
    damageClass: "special",
    effect: "",
    effectChance: null,
    learnedBy: [],
    meta: null,
    name: "Lumina Crash",
    power: 80,
    pp: 10,
    priority: 0,
    type: "psychic",
  },
  "order-up": {
    id: "order-up",
    accuracy: 100,
    damageClass: "physical",
    effect: "",
    effectChance: null,
    learnedBy: [],
    meta: null,
    name: "Order Up",
    power: 80,
    pp: 10,
    priority: 0,
    type: "dragon",
  },
  "jet-punch": {
    id: "jet-punch",
    accuracy: 100,
    damageClass: "physical",
    effect: "",
    effectChance: null,
    learnedBy: [],
    meta: null,
    name: "Jet Punch",
    power: 60,
    pp: 15,
    priority: 1,
    type: "water",
  },
  "spicy-extract": {
    id: "spicy-extract",
    accuracy: null,
    damageClass: "status",
    effect: "",
    effectChance: null,
    learnedBy: [],
    meta: null,
    name: "Spicy Extract",
    power: 0,
    pp: 15,
    priority: 0,
    type: "grass",
  },
  "spin-out": {
    id: "spin-out",
    accuracy: 100,
    damageClass: "physical",
    effect: "",
    effectChance: null,
    learnedBy: [],
    meta: null,
    name: "Spin Out",
    power: 100,
    pp: 5,
    priority: 0,
    type: "steel",
  },
  "population-bomb": {
    id: "population-bomb",
    accuracy: 90,
    damageClass: "physical",
    effect: "",
    effectChance: null,
    learnedBy: [],
    meta: null,
    name: "Population Bomb",
    power: 20,
    pp: 10,
    priority: 0,
    type: "normal",
  },
  "ice-spinner": {
    id: "ice-spinner",
    accuracy: 100,
    damageClass: "physical",
    effect: "",
    effectChance: null,
    learnedBy: [39, 40, 90, 91, 144, 149, 151],
    meta: null,
    name: "Ice Spinner",
    power: 80,
    pp: 15,
    priority: 0,
    type: "ice",
  },
  "glaive-rush": {
    id: "glaive-rush",
    accuracy: 100,
    damageClass: "physical",
    effect: "",
    effectChance: null,
    learnedBy: [],
    meta: null,
    name: "Glaive Rush",
    power: 120,
    pp: 5,
    priority: 0,
    type: "dragon",
  },
  "revival-blessing": {
    id: "revival-blessing",
    accuracy: null,
    damageClass: "status",
    effect: "",
    effectChance: null,
    learnedBy: [],
    meta: null,
    name: "Revival Blessing",
    power: 0,
    pp: 1,
    priority: 0,
    type: "normal",
  },
  "salt-cure": {
    id: "salt-cure",
    accuracy: 100,
    damageClass: "physical",
    effect: "",
    effectChance: null,
    learnedBy: [],
    meta: null,
    name: "Salt Cure",
    power: 40,
    pp: 15,
    priority: 0,
    type: "rock",
  },
  "triple-dive": {
    id: "triple-dive",
    accuracy: 95,
    damageClass: "physical",
    effect: "",
    effectChance: null,
    learnedBy: [],
    meta: null,
    name: "Triple Dive",
    power: 30,
    pp: 10,
    priority: 0,
    type: "water",
  },
  "mortal-spin": {
    id: "mortal-spin",
    accuracy: 100,
    damageClass: "physical",
    effect: "",
    effectChance: null,
    learnedBy: [],
    meta: null,
    name: "Mortal Spin",
    power: 30,
    pp: 15,
    priority: 0,
    type: "poison",
  },
  doodle: {
    id: "doodle",
    accuracy: 100,
    damageClass: "status",
    effect: "",
    effectChance: null,
    learnedBy: [],
    meta: null,
    name: "Doodle",
    power: 0,
    pp: 10,
    priority: 0,
    type: "normal",
  },
  "fillet-away": {
    id: "fillet-away",
    accuracy: null,
    damageClass: "status",
    effect: "",
    effectChance: null,
    learnedBy: [],
    meta: null,
    name: "Fillet Away",
    power: 0,
    pp: 10,
    priority: 0,
    type: "normal",
  },
  "kowtow-cleave": {
    id: "kowtow-cleave",
    accuracy: null,
    damageClass: "physical",
    effect: "",
    effectChance: null,
    learnedBy: [],
    meta: null,
    name: "Kowtow Cleave",
    power: 85,
    pp: 10,
    priority: 0,
    type: "dark",
  },
  "flower-trick": {
    id: "flower-trick",
    accuracy: null,
    damageClass: "physical",
    effect: "",
    effectChance: null,
    learnedBy: [],
    meta: null,
    name: "Flower Trick",
    power: 70,
    pp: 10,
    priority: 0,
    type: "grass",
  },
  "torch-song": {
    id: "torch-song",
    accuracy: 100,
    damageClass: "special",
    effect: "",
    effectChance: null,
    learnedBy: [],
    meta: null,
    name: "Torch Song",
    power: 80,
    pp: 10,
    priority: 0,
    type: "fire",
  },
  "aqua-step": {
    id: "aqua-step",
    accuracy: 100,
    damageClass: "physical",
    effect: "",
    effectChance: null,
    learnedBy: [],
    meta: null,
    name: "Aqua Step",
    power: 80,
    pp: 10,
    priority: 0,
    type: "water",
  },
  "raging-bull": {
    id: "raging-bull",
    accuracy: 100,
    damageClass: "physical",
    effect: "",
    effectChance: null,
    learnedBy: [128],
    meta: null,
    name: "Raging Bull",
    power: 90,
    pp: 10,
    priority: 0,
    type: "normal",
  },
  "make-it-rain": {
    id: "make-it-rain",
    accuracy: 100,
    damageClass: "special",
    effect: "",
    effectChance: null,
    learnedBy: [],
    meta: null,
    name: "Make It Rain",
    power: 120,
    pp: 5,
    priority: 0,
    type: "steel",
  },
  psyblade: {
    id: "psyblade",
    accuracy: 100,
    damageClass: "physical",
    effect: "",
    effectChance: null,
    learnedBy: [],
    meta: null,
    name: "Psyblade",
    power: 80,
    pp: 15,
    priority: 0,
    type: "psychic",
  },
  "hydro-steam": {
    id: "hydro-steam",
    accuracy: 100,
    damageClass: "special",
    effect: "",
    effectChance: null,
    learnedBy: [],
    meta: null,
    name: "Hydro Steam",
    power: 80,
    pp: 15,
    priority: 0,
    type: "water",
  },
  ruination: {
    id: "ruination",
    accuracy: 90,
    damageClass: "special",
    effect: "",
    effectChance: null,
    learnedBy: [],
    meta: null,
    name: "Ruination",
    power: 1,
    pp: 10,
    priority: 0,
    type: "dark",
  },
  "collision-course": {
    id: "collision-course",
    accuracy: 100,
    damageClass: "physical",
    effect: "",
    effectChance: null,
    learnedBy: [],
    meta: null,
    name: "Collision Course",
    power: 100,
    pp: 5,
    priority: 0,
    type: "fighting",
  },
  "electro-drift": {
    id: "electro-drift",
    accuracy: 100,
    damageClass: "special",
    effect: "",
    effectChance: null,
    learnedBy: [],
    meta: null,
    name: "Electro Drift",
    power: 100,
    pp: 5,
    priority: 0,
    type: "electric",
  },
  "shed-tail": {
    id: "shed-tail",
    accuracy: null,
    damageClass: "status",
    effect: "",
    effectChance: null,
    learnedBy: [],
    meta: null,
    name: "Shed Tail",
    power: 0,
    pp: 10,
    priority: 0,
    type: "normal",
  },
  "chilly-reception": {
    id: "chilly-reception",
    accuracy: null,
    damageClass: "status",
    effect: "",
    effectChance: null,
    learnedBy: [],
    meta: null,
    name: "Chilly Reception",
    power: 0,
    pp: 10,
    priority: 0,
    type: "ice",
  },
  "tidy-up": {
    id: "tidy-up",
    accuracy: null,
    damageClass: "status",
    effect: "",
    effectChance: null,
    learnedBy: [],
    meta: null,
    name: "Tidy Up",
    power: 0,
    pp: 10,
    priority: 0,
    type: "normal",
  },
  snowscape: {
    id: "snowscape",
    accuracy: null,
    damageClass: "status",
    effect: "",
    effectChance: null,
    learnedBy: [79, 80, 90, 91, 113, 144, 149, 151],
    meta: null,
    name: "Snowscape",
    power: 0,
    pp: 10,
    priority: 0,
    type: "ice",
  },
  pounce: {
    id: "pounce",
    accuracy: 100,
    damageClass: "physical",
    effect: "",
    effectChance: null,
    learnedBy: [48, 49, 123, 151],
    meta: null,
    name: "Pounce",
    power: 50,
    pp: 20,
    priority: 0,
    type: "bug",
  },
  trailblaze: {
    id: "trailblaze",
    accuracy: 100,
    damageClass: "physical",
    effect: "",
    effectChance: null,
    learnedBy: [
      25, 26, 39, 40, 52, 53, 54, 55, 96, 97, 113, 123, 128, 133, 134, 135, 136,
      150, 151,
    ],
    meta: null,
    name: "Trailblaze",
    power: 50,
    pp: 20,
    priority: 0,
    type: "grass",
  },
  "chilling-water": {
    id: "chilling-water",
    accuracy: 100,
    damageClass: "special",
    effect: "",
    effectChance: null,
    learnedBy: [
      39, 40, 52, 53, 54, 55, 79, 80, 90, 91, 113, 130, 134, 147, 148, 149, 150,
      151,
    ],
    meta: null,
    name: "Chilling Water",
    power: 50,
    pp: 20,
    priority: 0,
    type: "water",
  },
  "hyper-drill": {
    id: "hyper-drill",
    accuracy: 100,
    damageClass: "physical",
    effect: "",
    effectChance: null,
    learnedBy: [],
    meta: null,
    name: "Hyper Drill",
    power: 100,
    pp: 5,
    priority: 0,
    type: "normal",
  },
  "twin-beam": {
    id: "twin-beam",
    accuracy: 100,
    damageClass: "special",
    effect: "",
    effectChance: null,
    learnedBy: [],
    meta: null,
    name: "Twin Beam",
    power: 40,
    pp: 10,
    priority: 0,
    type: "psychic",
  },
  "rage-fist": {
    id: "rage-fist",
    accuracy: 100,
    damageClass: "physical",
    effect: "",
    effectChance: null,
    learnedBy: [57],
    meta: null,
    name: "Rage Fist",
    power: 50,
    pp: 10,
    priority: 0,
    type: "ghost",
  },
  "armor-cannon": {
    id: "armor-cannon",
    accuracy: 100,
    damageClass: "special",
    effect: "",
    effectChance: null,
    learnedBy: [],
    meta: null,
    name: "Armor Cannon",
    power: 120,
    pp: 5,
    priority: 0,
    type: "fire",
  },
  "bitter-blade": {
    id: "bitter-blade",
    accuracy: 100,
    damageClass: "physical",
    effect: "",
    effectChance: null,
    learnedBy: [],
    meta: null,
    name: "Bitter Blade",
    power: 90,
    pp: 10,
    priority: 0,
    type: "fire",
  },
  "double-shock": {
    id: "double-shock",
    accuracy: 100,
    damageClass: "physical",
    effect: "",
    effectChance: null,
    learnedBy: [],
    meta: null,
    name: "Double Shock",
    power: 120,
    pp: 5,
    priority: 0,
    type: "electric",
  },
  "gigaton-hammer": {
    id: "gigaton-hammer",
    accuracy: 100,
    damageClass: "physical",
    effect: "",
    effectChance: null,
    learnedBy: [],
    meta: null,
    name: "Gigaton Hammer",
    power: 160,
    pp: 5,
    priority: 0,
    type: "steel",
  },
  comeuppance: {
    id: "comeuppance",
    accuracy: 100,
    damageClass: "physical",
    effect: "",
    effectChance: null,
    learnedBy: [],
    meta: null,
    name: "Comeuppance",
    power: 1,
    pp: 10,
    priority: 0,
    type: "dark",
  },
  "aqua-cutter": {
    id: "aqua-cutter",
    accuracy: 100,
    damageClass: "physical",
    effect: "",
    effectChance: null,
    learnedBy: [],
    meta: null,
    name: "Aqua Cutter",
    power: 70,
    pp: 20,
    priority: 0,
    type: "water",
  },
  "blazing-torque": {
    id: "blazing-torque",
    accuracy: 100,
    damageClass: "physical",
    effect: "",
    effectChance: null,
    learnedBy: [],
    meta: null,
    name: "Blazing Torque",
    power: 80,
    pp: 10,
    priority: 0,
    type: "fire",
  },
  "wicked-torque": {
    id: "wicked-torque",
    accuracy: 100,
    damageClass: "physical",
    effect: "",
    effectChance: null,
    learnedBy: [],
    meta: null,
    name: "Wicked Torque",
    power: 80,
    pp: 10,
    priority: 0,
    type: "dark",
  },
  "noxious-torque": {
    id: "noxious-torque",
    accuracy: 100,
    damageClass: "physical",
    effect: "",
    effectChance: null,
    learnedBy: [],
    meta: null,
    name: "Noxious Torque",
    power: 100,
    pp: 10,
    priority: 0,
    type: "poison",
  },
  "combat-torque": {
    id: "combat-torque",
    accuracy: 100,
    damageClass: "physical",
    effect: "",
    effectChance: null,
    learnedBy: [],
    meta: null,
    name: "Combat Torque",
    power: 100,
    pp: 10,
    priority: 0,
    type: "fighting",
  },
  "magical-torque": {
    id: "magical-torque",
    accuracy: 100,
    damageClass: "physical",
    effect: "",
    effectChance: null,
    learnedBy: [],
    meta: null,
    name: "Magical Torque",
    power: 100,
    pp: 10,
    priority: 0,
    type: "fairy",
  },
  "shadow-rush": {
    id: "shadow-rush",
    accuracy: 100,
    damageClass: "physical",
    effect: "Has an increased chance for a critical hit in Hyper Mode.",
    effectChance: null,
    learnedBy: [],
    meta: null,
    name: "Shadow Rush",
    power: 55,
    pp: null,
    priority: 0,
    type: "shadow",
  },
  "shadow-blast": {
    id: "shadow-blast",
    accuracy: 100,
    damageClass: "physical",
    effect: "Has an increased chance for a critical hit.",
    effectChance: null,
    learnedBy: [],
    meta: null,
    name: "Shadow Blast",
    power: 80,
    pp: null,
    priority: 0,
    type: "shadow",
  },
  "shadow-blitz": {
    id: "shadow-blitz",
    accuracy: 100,
    damageClass: "physical",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [],
    meta: null,
    name: "Shadow Blitz",
    power: 40,
    pp: null,
    priority: 0,
    type: "shadow",
  },
  "shadow-bolt": {
    id: "shadow-bolt",
    accuracy: 100,
    damageClass: "special",
    effect: "Has a $effect_chance% chance to paralyze the target.",
    effectChance: 10,
    learnedBy: [],
    meta: null,
    name: "Shadow Bolt",
    power: 75,
    pp: null,
    priority: 0,
    type: "shadow",
  },
  "shadow-break": {
    id: "shadow-break",
    accuracy: 100,
    damageClass: "physical",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [],
    meta: null,
    name: "Shadow Break",
    power: 75,
    pp: null,
    priority: 0,
    type: "shadow",
  },
  "shadow-chill": {
    id: "shadow-chill",
    accuracy: 100,
    damageClass: "special",
    effect: "Has a $effect_chance% chance to freeze the target.",
    effectChance: 10,
    learnedBy: [],
    meta: null,
    name: "Shadow Chill",
    power: 75,
    pp: null,
    priority: 0,
    type: "shadow",
  },
  "shadow-end": {
    id: "shadow-end",
    accuracy: 60,
    damageClass: "physical",
    effect: "User receives 1/2 its HP in recoil.",
    effectChance: null,
    learnedBy: [],
    meta: null,
    name: "Shadow End",
    power: 120,
    pp: null,
    priority: 0,
    type: "shadow",
  },
  "shadow-fire": {
    id: "shadow-fire",
    accuracy: 100,
    damageClass: "special",
    effect: "Has a $effect_chance% chance to burn the target.",
    effectChance: 10,
    learnedBy: [],
    meta: null,
    name: "Shadow Fire",
    power: 75,
    pp: null,
    priority: 0,
    type: "shadow",
  },
  "shadow-rave": {
    id: "shadow-rave",
    accuracy: 100,
    damageClass: "special",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [],
    meta: null,
    name: "Shadow Rave",
    power: 70,
    pp: null,
    priority: 0,
    type: "shadow",
  },
  "shadow-storm": {
    id: "shadow-storm",
    accuracy: 100,
    damageClass: "special",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [],
    meta: null,
    name: "Shadow Storm",
    power: 95,
    pp: null,
    priority: 0,
    type: "shadow",
  },
  "shadow-wave": {
    id: "shadow-wave",
    accuracy: 100,
    damageClass: "special",
    effect: "Inflicts regular damage with no additional effect.",
    effectChance: null,
    learnedBy: [],
    meta: null,
    name: "Shadow Wave",
    power: 50,
    pp: null,
    priority: 0,
    type: "shadow",
  },
  "shadow-down": {
    id: "shadow-down",
    accuracy: 100,
    damageClass: "status",
    effect: "Lowers the target's Defense by two stages.",
    effectChance: null,
    learnedBy: [],
    meta: null,
    name: "Shadow Down",
    power: null,
    pp: null,
    priority: 0,
    type: "shadow",
  },
  "shadow-half": {
    id: "shadow-half",
    accuracy: 100,
    damageClass: "special",
    effect: "Halves HP of all Pokémon on the field.  Must recharge",
    effectChance: null,
    learnedBy: [],
    meta: null,
    name: "Shadow Half",
    power: null,
    pp: null,
    priority: 0,
    type: "shadow",
  },
  "shadow-hold": {
    id: "shadow-hold",
    accuracy: null,
    damageClass: "status",
    effect: "Prevents the target from leaving battle.",
    effectChance: null,
    learnedBy: [],
    meta: null,
    name: "Shadow Hold",
    power: null,
    pp: null,
    priority: 0,
    type: "shadow",
  },
  "shadow-mist": {
    id: "shadow-mist",
    accuracy: 100,
    damageClass: "status",
    effect: "Lowers the target's evasion by two stages.",
    effectChance: null,
    learnedBy: [],
    meta: null,
    name: "Shadow Mist",
    power: null,
    pp: null,
    priority: 0,
    type: "shadow",
  },
  "shadow-panic": {
    id: "shadow-panic",
    accuracy: 90,
    damageClass: "status",
    effect: "Confuses the target.",
    effectChance: null,
    learnedBy: [],
    meta: null,
    name: "Shadow Panic",
    power: null,
    pp: null,
    priority: 0,
    type: "shadow",
  },
  "shadow-shed": {
    id: "shadow-shed",
    accuracy: null,
    damageClass: "status",
    effect: "Removes Light Screen, Reflect, and Safeguard.",
    effectChance: null,
    learnedBy: [],
    meta: null,
    name: "Shadow Shed",
    power: null,
    pp: null,
    priority: 0,
    type: "shadow",
  },
  "shadow-sky": {
    id: "shadow-sky",
    accuracy: null,
    damageClass: "status",
    effect: "Changes the weather to Shadow Sky for five turns.",
    effectChance: null,
    learnedBy: [],
    meta: null,
    name: "Shadow Sky",
    power: null,
    pp: null,
    priority: 0,
    type: "shadow",
  },
};

export default moveMetadata;
